import type {
  SearchResponse,
  SearchFilters,
  SearchPreferences,
  SearchAreaKey,
  SearchMode,
  ApiResponse,
  DashboardData,
  DeployStatus,
  Integration,
  IntegrationCreateInput,
  IntegrationUpdateInput,
  AppSettings,
  UserAdminOverview,
  HealthCheckResponse,
  AiChatRequest,
  AiChatResponse,
  AiStreamChunk,
  AiConversation,
  SupportChatStartRequest,
  SupportChatStartResponse,
  SupportChatSendRequest,
  SupportChatSendResponse,
  SupportChatSummary,
  SupportChatDetail,
  SupportChatCounts,
  SupportChatStatus,
  SupportChatSyncResponse,
  SupportDraftResponse,
  KnowledgeAudience,
  KnowledgeSelection,
  SpecCanvasBoard,
  SpecCanvasPosition,
  SpecFolder,
  SpecFolderTree,
  SpecListParams,
  SpecListResult,
  SpecTagCount,
  GlobalTokenUsage,
  UsageStats,
  UsagePeriod,
  InsightsOverview,
  InsightsAi,
  InsightsEngineering,
  InsightsNarrative,
  InsightsRange,
  AiUsageInfo,
  SyncJob,
  DiscourseTopic,
  DiscourseTopicDetails,
  DiscourseTopicAnalysis,
  DiscourseAnalysisSummary,
  DiscourseListParams,
  DiscourseListResult,
  DiscourseFacets,
  DiscourseComposerLimits,
  DiscourseCategory,
  Employee,
  EmployeeDetail,
  DocScope,
  DocumentItem,
  DocumentListResult,
  DocumentDetail,
  DocSearchHit,
  DocCitation,
  KnowledgeArticle,
  KnowledgeArticleDetail,
  KnowledgeArticleBacklink,
  KnowledgeArticleListResult,
  AppNavNode,
  AppNavApp,
  ReleaseStatusItem,
  ReleaseSession,
  ReleaseSessionDetail,
  Commit,
  CommitAnalysis,
  CommitAnalysisDetail,
  AzureWorkItemSummary,
  AzureWorkItemDetail,
  AdoItemType,
  AiOverride,
  DiscourseReplyDraft,
  DiscourseReplyResult,
  DiscourseTopicCreateInput,
  DiscourseTopicCreateResult,
  DiscoursePostEditResult,
  DiscourseTagSuggestion,
  DiscourseAction,
  WorkItemDraft,
  WorkItemDraftResult,
  WorkItemCommitResult,
  LinkedWorkItem,
  RelatedItem,
  AzureUser,
  NextStepsResult,
  AzureNextStepsResult,
  AzureWorkItemHistoryEntry,
  AzureWorkItemHistorySummary,
  ZohoListParams,
  ZohoListResult,
  ZohoFacets,
  ZohoTicketDetail,
  ZohoInsights,
  ZohoTopicsResult,
  ZohoAgentStats,
  ZohoTicketAnalysis,
  ZohoNextStep,
  ZohoNextStepsResult,
  ZohoSteeringOption,
  ZohoDraftSteer,
  ZohoDraftVerification,
  ZohoDraftMetrics,
  ZohoReplyReview,
  CrmCompanySummary,
  CrmCompanyDetail,
  CrmContact,
  CrmDeal,
  CrmLead,
  CrmNote,
  CrmFacets,
  CrmListResult,
  CommsInboxItem,
  CommsInboxListResult,
  CommsReplyDraft,
  JournalEntry,
  JournalListResult,
  EmbedPreview,
  EmbedPreviewType,
  CiTrendPoint,
  RepoOpenPr,
  DashboardLayout,
  BacklogCardLayout,
  BacklogCardType,
  ConfiguredFeatures,
  PersonaKey,
  McpApiKey,
  McpApiKeyWithSecret,
  McpScope,
  PcpChangesSince,
  AdoBoardsResult,
  AzureBoardMoveRequest,
  RealEquityTenantListParams,
  RealEquityListResult,
  RealEquityTenantSummary,
  RealEquityFacets,
  RealEquityTenantDetail,
  RealEquityResourceGroupDetail,
  RealEquityApplicationDetail,
  RealEquityDataStoreDetail,
  RealEquitySearchResults,
  ThinkSetting,
  AzureTagFacet,
  NotificationKindDef,
  NotificationMode,
  NotificationOptionDef,
  Feedback, FeedbackSummary, FeedbackListResult, FeedbackFacets, FeedbackImage, FeedbackNote,
  FeedbackComment, FeedbackReply, FeedbackElementPatch, FeedbackSeverity,
  FeedbackCommentState, FeedbackSort, FeedbackCanvasPrefs,
  BacklogFeedbackSort, BacklogFeedbackSortPrefs, LinkedFeedbackSummary,
  RecentItemType,
  FeedbackLink,
  FeedbackTextBlock,
  FeedbackConnector,
  ConnectorEnd,
} from '@pcp/shared';

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { clearApiCache } from './authBridge';
import { notifyConnectionRequired, promptConnectionRequired, type ConnectionProvider } from './connectionsBridge';
import type { ThemePalette, ThemeMode, ThemeContrast } from './stores';
import type { RevokeAccessResult } from './revokeAccess';
import type { CustomTheme } from '@pcp/shared';
import { ERR_ADO_BOARD_MOVE_NOT_LANDED } from '@pcp/shared';
import type { TicketsFilterResultExtras, ZohoTicketsQueryRequest } from '@pcp/shared';
import type { ListFilterQuery } from './filters/useListFilters';

export type { ConnectionProvider } from './connectionsBridge';

// ─── Base Fetch Wrapper ─────────────────────────────────────────

const BASE_URL = '/api';

export class ApiError extends Error {
  statusCode: number;
  /** Machine-readable error code, e.g. 'permission_denied' | 'connection_required'. */
  code?: string;
  /** For 412 connection_required: which integration needs a personal connection. */
  provider?: string;
  /** For 403 permission_denied: which permission key was missing. */
  permission?: string;
  /** For 412 connection_required: true when the action may fall back to the
   *  shared/global integration connection (admin-enabled) with per-action consent. */
  globalFallbackAvailable?: boolean;
  /** True when the error response had NO parseable JSON body — e.g. the HTML/empty
   *  502/503/504 a proxy (Caddy) returns while the API container rolls during a
   *  deploy. Marks a transient/blank gateway failure rather than a real app error,
   *  so the UI keeps the calm "try again" copy instead of surfacing a bogus message. */
  noBody?: boolean;
  /** The `data` a FAILED response carried, when it carried any — what did get
   *  through of a write that did not fully succeed (e.g. where a board move left
   *  the card, b-01999). Untyped: the caller that knows the `code` knows the shape. */
  data?: unknown;

  constructor(
    statusCode: number,
    message: string,
    opts?: { code?: string; provider?: string; permission?: string; globalFallbackAvailable?: boolean; noBody?: boolean; data?: unknown },
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = opts?.code;
    this.provider = opts?.provider;
    this.permission = opts?.permission;
    this.globalFallbackAvailable = opts?.globalFallbackAvailable;
    this.noBody = opts?.noBody;
    this.data = opts?.data;
  }
}

/** True when the error is a 412 "connect your account first" rejection. */
export function isConnectionRequired(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'connection_required' && !!e.provider;
}

/** Merge `{ useGlobalConnection: true }` into a JSON-string request body so a
 *  retried write consents to the shared/global connection for this one action.
 *  Returns null when the body isn't a JSON object we can extend (then we can't
 *  carry consent, so there's nothing to retry). */
function injectGlobalConsent(body: BodyInit | null | undefined): string | null {
  if (typeof body !== 'string') return null;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return JSON.stringify({ ...parsed, useGlobalConnection: true });
    }
  } catch { /* not JSON — can't inject consent */ }
  return null;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isFallbackRetry = false,
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {};

  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    // Track whether the error body parsed as JSON: a bodyless 5xx is a transient
    // gateway/rolling failure (calm copy), whereas a 5xx carrying a real `error`
    // string is a genuine backend failure whose message must be shown.
    let bodyHadJson = true;
    const error = await response.json().catch(() => {
      bodyHadJson = false;
      return { error: `Request failed with status ${response.status}` };
    });
    const apiError = new ApiError(response.status, error.error || 'Unknown error', {
      code: typeof error.code === 'string' ? error.code : undefined,
      provider: typeof error.provider === 'string' ? error.provider : undefined,
      permission: typeof error.permission === 'string' ? error.permission : undefined,
      globalFallbackAvailable: error.globalFallbackAvailable === true,
      noBody: !bodyHadJson,
      data: error.data,
    });
    // A blocked write (412) means the acting user hasn't connected their own
    // integration account. When the backend marks the action as eligible for the
    // admin-enabled shared/global connection, ask ONCE and — on consent — retry
    // the SAME request with useGlobalConnection so it runs on the shared
    // connection. Components that own the 412 surface (the AI composer's inline
    // banner) suppress the global prompt → promptConnectionRequired resolves
    // 'cancel' and we just rethrow for them to handle inline.
    if (isConnectionRequired(apiError)) {
      if (!isFallbackRetry && apiError.globalFallbackAvailable) {
        const consentBody = injectGlobalConsent(options.body);
        if (consentBody !== null) {
          const decision = await promptConnectionRequired(apiError.provider as ConnectionProvider, true);
          if (decision === 'use-global') {
            return request<T>(endpoint, { ...options, body: consentBody }, true);
          }
          // 'connect' (the sheet navigates to Profile) or 'cancel' → surface the error.
          throw apiError;
        }
      }
      // No fallback available (or no JSON body to carry consent): nudge the user
      // to connect their own account — the original behavior.
      if (!isFallbackRetry) notifyConnectionRequired(apiError.provider as ConnectionProvider);
    }
    throw apiError;
  }

  const data = (await response.json()) as ApiResponse<T>;
  return data.data;
}

// ─── Health ─────────────────────────────────────────────────────

export async function getHealth(): Promise<HealthCheckResponse> {
  const response = await fetch(`${BASE_URL}/health`);
  return response.json() as Promise<HealthCheckResponse>;
}

// ─── Deploy status (release-in-progress banner) ─────────────────────
// Flat (non-envelope) response like /health and /runtime-config. Throws on a
// failed fetch/non-OK so react-query holds the last value — the banner stays
// up through the brief API outage while services roll, then clears when the
// new API reports inProgress:false.
export async function getDeployStatus(): Promise<DeployStatus> {
  const response = await fetch(`${BASE_URL}/deploy-status`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`deploy-status request failed (${response.status})`);
  return response.json() as Promise<DeployStatus>;
}

// ─── App errors (Diagnostics page) ──────────────────────────────────
// Reads the unified client + server error store. Gated by diagnostics.view; the
// page also live-refreshes via the 'errors' realtime entity (RealtimeBridge).
export interface AppErrorQuery {
  env?: string;
  source?: string;
  level?: string;
  q?: string;
  resolved?: boolean;
  sinceMinutes?: number;
  sort?: 'recent' | 'count';
  page?: number;
  pageSize?: number;
}

export async function getAppErrors(
  query: AppErrorQuery = {},
): Promise<import('@pcp/shared').AppErrorListResponse> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const qs = p.toString();
  return request(`/app-errors${qs ? `?${qs}` : ''}`);
}

export async function getAppErrorDetail(
  id: string,
): Promise<import('@pcp/shared').AppErrorDetail> {
  return request(`/app-errors/${id}`);
}

export async function resolveAppError(id: string, resolved = true): Promise<{ ok: boolean }> {
  return request(`/app-errors/${id}/resolve`, { method: 'POST', body: JSON.stringify({ resolved }) });
}

// ─── Dashboard ──────────────────────────────────────────────────

export async function getDashboard(): Promise<DashboardData> {
  return request<DashboardData>('/dashboard');
}

// ─── AI dashboard-layout builder (SSE) ──────────────────────────
// POST /api/dashboard/ai-layout streams `{type:'progress'|'result'|'error'}` like
// the next-steps recommender. The server reconciles its own output, so the
// returned layout is always safe to preview/apply. The caller MUST pass an
// AbortSignal and abort on unmount so a slow generation can't leak into a torn-down
// component. The server already falls back to a persona layout on AI failure, so a
// successful response always carries a usable layout.
export interface AiLayoutResult {
  layout: DashboardLayout;
  /** 'ai' = the model produced it; 'fallback' = persona seeds (AI unavailable/weak). */
  source: 'ai' | 'fallback';
}

export async function buildAiDashboardLayout(
  params: { personas: PersonaKey[]; density?: 'comfortable' | 'compact' },
  opts?: { onProgress?: (message: string) => void; signal?: AbortSignal },
): Promise<AiLayoutResult> {
  const response = await fetch(`${BASE_URL}/dashboard/ai-layout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(params),
    signal: opts?.signal,
  });
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new ApiError(response.status, (e as { error?: string }).error || 'Layout generation failed');
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let result: AiLayoutResult | null = null;
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(line.slice(6));
        if (event.type === 'progress' && opts?.onProgress) opts.onProgress(event.message);
        else if (event.type === 'result') result = { layout: event.data, source: event.source ?? 'ai' };
        else if (event.type === 'error') throw new Error(event.message);
      } catch (e) {
        if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
      }
    }
  }
  if (!result) throw new Error('No layout received');
  return result;
}

// ─── Knowledge / embedding inspector ────────────────────────────

export async function getEmbedPreview(type: EmbedPreviewType, id: string): Promise<EmbedPreview> {
  return request<EmbedPreview>(`/knowledge/embed-preview/${type}/${encodeURIComponent(id)}`);
}

// ─── Integrations ───────────────────────────────────────────────

export async function getIntegrations(): Promise<Integration[]> {
  return request<Integration[]>('/integrations');
}

export async function getIntegration(id: string): Promise<Integration> {
  return request<Integration>(`/integrations/${id}`);
}

export async function createIntegration(
  input: IntegrationCreateInput,
): Promise<Integration> {
  return request<Integration>('/integrations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateIntegration(
  id: string,
  input: IntegrationUpdateInput,
): Promise<Integration> {
  return request<Integration>(`/integrations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteIntegration(id: string): Promise<void> {
  return request<void>(`/integrations/${id}`, {
    method: 'DELETE',
  });
}

export async function syncIntegration(id: string): Promise<SyncJob> {
  return request<SyncJob>(`/integrations/${id}/sync`, {
    method: 'POST',
  });
}

// ─── Settings ───────────────────────────────────────────────────

export async function getSettings(): Promise<AppSettings> {
  return request<AppSettings>('/settings');
}

export async function updateSettings(
  settings: Partial<AppSettings>,
): Promise<AppSettings> {
  return request<AppSettings>('/settings', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// ─── User administration ────────────────────────────────────────

export async function getUserAdminOverview(): Promise<UserAdminOverview> {
  return request<UserAdminOverview>('/user-admin/overview');
}

/** Revoke access (b-02059): signs the user out everywhere AND revokes their MCP API
 *  keys + AI-client (OAuth) grants; the account stays active. */
export async function revokeUserAccess(userId: string): Promise<RevokeAccessResult> {
  return request<RevokeAccessResult>(`/user-admin/users/${userId}/revoke`, { method: 'POST' });
}

// ─── MCP server API keys ────────────────────────────────────────

export async function getMcpKeys(): Promise<McpApiKey[]> {
  return request<McpApiKey[]>('/mcp/keys');
}

export async function createMcpKey(input: { name: string; scopes: McpScope[] }): Promise<McpApiKeyWithSecret> {
  return request<McpApiKeyWithSecret>('/mcp/keys', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function revokeMcpKey(id: string): Promise<void> {
  return request<void>(`/mcp/keys/${id}`, { method: 'DELETE' });
}

// ─── Push Notifications ─────────────────────────────────────────

export async function getVapidPublicKey(): Promise<{ key: string; enabled: boolean }> {
  return request<{ key: string; enabled: boolean }>('/push/vapid-public-key');
}

export async function subscribePush(
  subscription: PushSubscriptionJSON,
): Promise<{ subscribed: boolean }> {
  return request<{ subscribed: boolean }>('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}

export async function unsubscribePush(endpoint: string): Promise<{ unsubscribed: boolean }> {
  return request<{ unsubscribed: boolean }>('/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint }),
  });
}

export async function sendTestPush(): Promise<{ sent: number }> {
  return request<{ sent: number }>('/push/test', { method: 'POST' });
}

// ─── Zoho Desk Tickets ──────────────────────────────────────────

function zohoQueryString(p: ZohoListParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  (p.department ?? []).forEach((d) => qs.append('department', d));
  if (p.statusType && p.statusType !== 'all') qs.set('statusType', p.statusType);
  (p.priority ?? []).forEach((pr) => qs.append('priority', pr));
  if (p.reviewed && p.reviewed !== 'all') qs.set('reviewed', p.reviewed);
  if (p.hasAttachments) qs.set('hasAttachments', '1');
  if (p.sort) qs.set('sort', p.sort);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  return qs.toString();
}

export async function getZohoTickets(params: ZohoListParams = {}): Promise<ZohoListResult> {
  return request<ZohoListResult>(`/zoho/tickets?${zohoQueryString(params)}`);
}

/**
 * The Tickets list through the filter framework (f-02064): GET /zoho/tickets
 * with the additive `filter` (compact definition), `sf` / `sv` (an applied,
 * unedited filter) and `tz` params — or, when the definition is too large for a
 * URL, the POST /zoho/tickets/query twin. The legacy params are not sent.
 */
export async function getZohoTicketsFiltered(query: ListFilterQuery): Promise<ZohoListResult & TicketsFilterResultExtras> {
  if (query.usePost) {
    const body: ZohoTicketsQueryRequest = {
      definition: query.definition, sf: query.sf, sv: query.sv, q: query.q, sort: query.sort,
      page: query.page, pageSize: query.pageSize, tz: query.tz,
    };
    return request<ZohoListResult & TicketsFilterResultExtras>('/zoho/tickets/query', { method: 'POST', body: JSON.stringify(body) });
  }
  const qs = new URLSearchParams();
  if (query.q) qs.set('q', query.q);
  if (query.filter) qs.set('filter', query.filter);
  if (query.sf) qs.set('sf', query.sf);
  if (query.sv != null) qs.set('sv', String(query.sv));
  qs.set('sort', query.sort);
  qs.set('page', String(query.page));
  qs.set('pageSize', String(query.pageSize));
  qs.set('tz', query.tz);
  return request<ZohoListResult & TicketsFilterResultExtras>(`/zoho/tickets?${qs.toString()}`);
}

export async function getZohoFacets(): Promise<ZohoFacets> {
  return request<ZohoFacets>('/zoho/facets');
}

export async function getZohoInsights(): Promise<ZohoInsights> {
  return request<ZohoInsights>('/zoho/insights');
}

export async function getZohoTopics(): Promise<ZohoTopicsResult> {
  return request<ZohoTopicsResult>('/zoho/topics');
}

// ─── Zoho CRM (Companies, F3) ──────────────────────────────────────

export interface CrmCompaniesParams {
  q?: string;
  industry?: string[];
  owner?: string[];
  sort?: 'name' | 'industry' | 'owner';
  page?: number;
  pageSize?: number;
}

function crmCompaniesQuery(p: CrmCompaniesParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  (p.industry ?? []).forEach((v) => qs.append('industry', v));
  (p.owner ?? []).forEach((v) => qs.append('owner', v));
  if (p.sort) qs.set('sort', p.sort);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  return qs.toString();
}

export async function getCrmCompanies(params: CrmCompaniesParams = {}): Promise<CrmListResult<CrmCompanySummary>> {
  return request<CrmListResult<CrmCompanySummary>>(`/crm/companies?${crmCompaniesQuery(params)}`);
}

export async function getCrmFacets(): Promise<CrmFacets> {
  return request<CrmFacets>('/crm/facets');
}

export async function getCrmCompany(id: string): Promise<CrmCompanyDetail> {
  return request<CrmCompanyDetail>(`/crm/companies/${encodeURIComponent(id)}`);
}

export async function getCrmContact(id: string): Promise<CrmContact & { company: CrmCompanySummary | null }> {
  return request<CrmContact & { company: CrmCompanySummary | null }>(`/crm/contacts/${encodeURIComponent(id)}`);
}

export async function getCrmDeal(id: string): Promise<CrmDeal & { company: CrmCompanySummary | null }> {
  return request<CrmDeal & { company: CrmCompanySummary | null }>(`/crm/deals/${encodeURIComponent(id)}`);
}

export async function getCrmLead(id: string): Promise<CrmLead> {
  return request<CrmLead>(`/crm/leads/${encodeURIComponent(id)}`);
}

/** Trigger the Zoho CRM (Companies) sync — server-side run; progress in the Sync panel. */
export function runCrmSync(onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/crm/sync', {}, onEvent);
}

// ─── Communications Journal (F4.2) ─────────────────────────────────
// The INBOX is the signed-in user's PRIVATE triage staging (server-scoped to the
// acting user); journalize/dismiss are gated by comms.journalize. The JOURNAL is
// the company-visible, per-customer log of journalized communications.

export interface CommsInboxParams {
  /** 'pending' (default), 'auto_journalized', 'journalized', 'dismissed', or 'all'. */
  status?: 'pending' | 'auto_journalized' | 'journalized' | 'dismissed' | 'all';
  page?: number;
  pageSize?: number;
}

function commsInboxQuery(p: CommsInboxParams): string {
  const qs = new URLSearchParams();
  if (p.status) qs.set('status', p.status);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  return qs.toString();
}

export async function getCommsInbox(params: CommsInboxParams = {}): Promise<CommsInboxListResult> {
  return request<CommsInboxListResult>(`/comms/inbox?${commsInboxQuery(params)}`);
}

export async function getCommsInboxItem(id: string): Promise<CommsInboxItem> {
  return request<CommsInboxItem>(`/comms/inbox/${encodeURIComponent(id)}`);
}

export interface JournalizeCommsInput {
  /** Optional override — re-assign onto a specific customer instead of the AI match. */
  customer?: { accountId?: string | null; contactId?: string | null; leadId?: string | null };
}

export async function journalizeComms(id: string, body: JournalizeCommsInput = {}): Promise<JournalEntry> {
  return request<JournalEntry>(`/comms/inbox/${encodeURIComponent(id)}/journalize`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function dismissComms(id: string): Promise<void> {
  await request<void>(`/comms/inbox/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}

export interface CustomerJournalParams {
  accountId?: string;
  contactId?: string;
  leadId?: string;
  page?: number;
  pageSize?: number;
}

function customerJournalQuery(p: CustomerJournalParams): string {
  const qs = new URLSearchParams();
  if (p.accountId) qs.set('accountId', p.accountId);
  if (p.contactId) qs.set('contactId', p.contactId);
  if (p.leadId) qs.set('leadId', p.leadId);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  return qs.toString();
}

export async function getCustomerJournal(params: CustomerJournalParams): Promise<JournalListResult> {
  return request<JournalListResult>(`/comms/journal?${customerJournalQuery(params)}`);
}

// ─── Communications SEND (F4.3a — send as yourself via Microsoft Graph) ──
// Attributable outbound: runs under the signed-in user's connected Microsoft 365
// identity, gated by comms.send. A missing connection throws an ApiError with
// code 'connection_required' (handled centrally by request()), prompting a
// connect. An optional `customer` journals the outbound onto that account/
// contact/lead; the response carries the new journal id when one was written.

export interface CommsSendCustomer {
  accountId?: string | null;
  contactId?: string | null;
  leadId?: string | null;
}

export interface SendCommsResult {
  journalId?: string;
}

export interface SendCommsMailInput {
  to: string[];
  cc?: string[];
  subject: string;
  html: string;
  customer?: CommsSendCustomer;
}

export async function sendCommsMail(body: SendCommsMailInput): Promise<SendCommsResult> {
  return request<SendCommsResult>('/comms/mail/send', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface SendCommsTeamsInput {
  html: string;
  customer?: CommsSendCustomer;
}

export async function sendCommsTeamsMessage(chatId: string, body: SendCommsTeamsInput): Promise<SendCommsResult> {
  return request<SendCommsResult>(`/comms/teams/chats/${encodeURIComponent(chatId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─── Communications reply DRAFT (F4.3b — AI drafts a reply to a staged message) ──
// READ-ONLY: drafting writes nothing. `steer` is a free-form directive for the
// angle of the draft; on a re-draft it carries a steering option's `stance`.
export async function draftCommsReply(id: string, opts?: { steer?: string }): Promise<CommsReplyDraft> {
  return request<CommsReplyDraft>(`/comms/inbox/${encodeURIComponent(id)}/reply/draft`, {
    method: 'POST',
    body: JSON.stringify({ steer: opts?.steer || '' }),
  });
}

// ─── Zoho CRM writes (acting-user write-through) ───────────────────
// Attributable writes: run under the signed-in user's connected Zoho identity.
// A missing/Desk-only connection throws an ApiError with code
// 'connection_required' (handled centrally by request()), prompting a connect.

export interface CrmAccountWriteInput {
  accountName?: string;
  website?: string | null;
  phone?: string | null;
  industry?: string | null;
  billingCity?: string | null;
  billingCountry?: string | null;
  description?: string | null;
}

export async function createCrmAccount(input: CrmAccountWriteInput): Promise<CrmCompanySummary> {
  return request<CrmCompanySummary>('/crm/accounts', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCrmAccount(id: string, input: CrmAccountWriteInput): Promise<CrmCompanySummary> {
  return request<CrmCompanySummary>(`/crm/accounts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export interface CrmContactWriteInput {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  accountId?: string | null;
}

export async function createCrmContact(input: CrmContactWriteInput): Promise<CrmContact> {
  return request<CrmContact>('/crm/contacts', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCrmContact(id: string, input: CrmContactWriteInput): Promise<CrmContact> {
  return request<CrmContact>(`/crm/contacts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deleteCrmContact(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/crm/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface CrmNoteWriteInput {
  title?: string | null;
  content?: string | null;
  /** Required on create — the parent record this note hangs off. */
  parentId?: string;
  parentModule?: string;
}

export async function createCrmNote(input: CrmNoteWriteInput): Promise<CrmNote> {
  return request<CrmNote>('/crm/notes', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCrmNote(id: string, input: CrmNoteWriteInput): Promise<CrmNote> {
  return request<CrmNote>(`/crm/notes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deleteCrmNote(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/crm/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Link a CRM account to a RealEquity tenant (local cross-link; no external call). */
export async function setCrmTenantLink(accountId: string, input: { tenantReId: string }): Promise<{ linked: boolean }> {
  return request<{ linked: boolean }>(`/crm/accounts/${encodeURIComponent(accountId)}/tenant-link`, { method: 'PUT', body: JSON.stringify(input) });
}

export async function removeCrmTenantLink(accountId: string): Promise<{ linked: boolean }> {
  return request<{ linked: boolean }>(`/crm/accounts/${encodeURIComponent(accountId)}/tenant-link`, { method: 'DELETE' });
}

/**
 * POST an endpoint that streams Server-Sent progress events and resolve when it
 * emits `done` (or rejects on `error`). Used for the long-running Zoho AI jobs
 * that now run server-side as tracked sync-runs — the live progress is shown by
 * the global SyncLogPanel, so callers typically pass a no-op onEvent.
 */
export function postSse(
  path: string,
  body: unknown,
  onEvent: (event: { type: string; message?: string; [k: string]: unknown }) => void = () => {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async () => {
          try {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (!part.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(part.slice(6));
                  if (data.type === 'error') { reject(new Error(data.message)); return; }
                  if (data.type === 'done') {
                    onEvent(data);
                    // A runner-reported terminal failure arrives on the 'done' frame
                    // (status:'failed') — surface it as an error, not a green toast.
                    if (data.status === 'failed') reject(new Error(data.message || 'Sync failed'));
                    else resolve();
                    return;
                  }
                  onEvent(data);
                } catch { /* ignore partial frames */ }
              }
            }
            resolve();
          } catch (err) { reject(err); }
        };
        pump();
      })
      .catch(reject);
  });
}

/** Like postSse, but resolves with the final `done` event payload (its stats). */
export function postSseResult<T = Record<string, unknown>>(
  path: string,
  body: unknown,
  onEvent: (event: { type: string; message?: string; [k: string]: unknown }) => void = () => {},
): Promise<T> {
  return new Promise((resolve, reject) => {
    let result: any = {};
    postSse(path, body, (e) => { if (e.type === 'done') result = e; onEvent(e); })
      .then(() => resolve(result as T))
      .catch(reject);
  });
}

/** Recompute AI ticket topics (server-side run; progress shown in the Sync panel). */
export function recomputeZohoTopics(onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/topics/recompute', {}, onEvent);
}

export async function getZohoAgentStats(period: 'week' | 'month'): Promise<ZohoAgentStats> {
  return request<ZohoAgentStats>(`/zoho/agent-stats?period=${period}`);
}

/** Classify uncategorized tickets (server-side run; progress in the Sync panel).
 *  `reset` clears all categories first so everything re-classifies. */
export function classifyZohoTickets(reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/classify-backfill', { reset }, onEvent);
}

/** AI-score customer sentiment for tickets that don't have one (Zoho often
 *  supplies none). `reset` re-scores everything. Progress shows in the Sync panel. */
export function analyzeZohoSentiment(reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/sentiment-backfill', { reset }, onEvent);
}

export interface ZohoAgent { id: string; name: string; email: string | null; status: string | null }

export async function getZohoAgents(): Promise<ZohoAgent[]> {
  return request<ZohoAgent[]>('/zoho/agents');
}

export async function getZohoDraftMetrics(): Promise<ZohoDraftMetrics> {
  return request<ZohoDraftMetrics>('/zoho/draft-metrics');
}

export async function getZohoStatuses(): Promise<string[]> {
  return request<string[]>('/zoho/statuses');
}

export type ZohoDraftHealthSummary = { calibratedConfidence: number | null; faithfulness: number; unsupportedClaims: number; risks: number };

export async function postZohoComment(
  id: string,
  content: string,
  opts?: { aiSuggestion?: { content: string; steer?: ZohoDraftSteer; health?: ZohoDraftHealthSummary }; provider?: string; model?: string },
): Promise<{ id: string | null }> {
  return request<{ id: string | null }>(`/zoho/tickets/${id}/comment`, {
    method: 'POST',
    body: JSON.stringify({ content, ...opts }),
  });
}

export interface ZohoCommentDraft { draft: string; reasoning: string; confidence: number; calibratedConfidence?: number; verification?: ZohoDraftVerification; provider: string; model: string; examplesUsed: number; sources?: DocCitation[]; usage?: AiUsageInfo; steeringOptions?: ZohoSteeringOption[] }

// `steer` is a free-form directive for the angle of the draft (e.g. "explain this is
// expected behaviour"). On the first click it carries the steer-box text; on a
// re-draft it carries the chosen steering option's `stance`.
export async function draftZohoComment(id: string, steer?: string): Promise<ZohoCommentDraft> {
  return request<ZohoCommentDraft>(`/zoho/tickets/${id}/comment/draft`, {
    method: 'POST',
    body: JSON.stringify({ steer: steer || '' }),
  });
}

// Customer-facing reply (emails the customer). Mirrors the comment flow but with
// its own grounding source (past replies + reply playbook).
export async function sendZohoReply(
  id: string,
  content: string,
  opts?: { aiSuggestion?: { content: string; steer?: ZohoDraftSteer; health?: ZohoDraftHealthSummary }; provider?: string; model?: string },
): Promise<{ id: string | null }> {
  return request<{ id: string | null }>(`/zoho/tickets/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ content, ...opts }),
  });
}

export async function draftZohoReply(id: string, steer?: string): Promise<ZohoCommentDraft> {
  return request<ZohoCommentDraft>(`/zoho/tickets/${id}/reply/draft`, {
    method: 'POST',
    body: JSON.stringify({ steer: steer || '' }),
  });
}

// AI proof-read of a reply before sending — gates the Send button.
export async function reviewZohoReply(id: string, content: string): Promise<ZohoReplyReview> {
  return request<ZohoReplyReview>(`/zoho/tickets/${id}/reply/review`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function getReleasePlaybook(): Promise<{ content: string }> {
  return request<{ content: string }>('/releases/playbook');
}
export async function saveReleasePlaybook(content: string): Promise<{ content: string }> {
  return request<{ content: string }>('/releases/playbook', { method: 'PUT', body: JSON.stringify({ content }) });
}

export async function getZohoPlaybook(): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/playbook');
}
export async function saveZohoPlaybook(content: string): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/playbook', { method: 'PUT', body: JSON.stringify({ content }) });
}
export async function distillZohoPlaybook(): Promise<{ updated: boolean; message?: string; length?: number; examples?: number }> {
  return request('/zoho/playbook/distill', { method: 'POST' });
}
export async function getZohoReplyPlaybook(): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/reply-playbook');
}
export async function saveZohoReplyPlaybook(content: string): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/reply-playbook', { method: 'PUT', body: JSON.stringify({ content }) });
}
export async function distillZohoReplyPlaybook(): Promise<{ updated: boolean; message?: string; length?: number; examples?: number }> {
  return request('/zoho/reply-playbook/distill', { method: 'POST' });
}
export async function getZohoWorkitemPlaybook(): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/workitem-playbook');
}
export async function saveZohoWorkitemPlaybook(content: string): Promise<{ content: string }> {
  return request<{ content: string }>('/zoho/workitem-playbook', { method: 'PUT', body: JSON.stringify({ content }) });
}
export async function distillZohoWorkitemPlaybook(): Promise<{ updated: boolean; message?: string; length?: number; examples?: number }> {
  return request('/zoho/workitem-playbook/distill', { method: 'POST' });
}
export async function importZohoHistory(): Promise<{ imported: number }> {
  return request('/zoho/import-history', { method: 'POST' });
}
/** Seed reply examples from existing synced outbound (agent → customer) threads. */
export async function importZohoReplyHistory(): Promise<{ imported: number }> {
  return request('/zoho/import-reply-history', { method: 'POST' });
}
/** Embed comment actions for RAG (server-side run; progress in the Sync panel). */
export function backfillZohoActionEmbeddings(onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/actions/embed-backfill', {}, onEvent);
}
/** Embed reply actions for RAG (server-side run; progress in the Sync panel). */
export function backfillZohoReplyEmbeddings(onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/reply-actions/embed-backfill', {}, onEvent);
}

/** One-shot full dialog backfill (replies + comments + description) for every
 *  ticket still missing it, oldest first. Server-side run; progress in the Sync panel. */
export function backfillZohoDialog(onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse('/zoho/dialog-backfill', {}, onEvent);
}

/** Vision-extract image text from ticket messages modified in the last `days`
 *  days (0 = all time), dropping logos/noise; affected tickets re-embed.
 *  Server-side run; progress in the Sync panel. */
export function backfillZohoTicketImages(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/zoho/image-extract-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}
/** Vision-extract images from Discourse topic posts (last `days` days, 0 = all). */
export function backfillDiscourseTopicImages(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/discourse/image-extract-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}
/** Vision-extract images from Azure work-item description/attachments (last `days` days, 0 = all). */
export function backfillAzureWorkItemImages(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/azure/image-extract-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}

/** Extract text from NON-image file attachments (PDF/Word/…) in the last `days`
 *  days (0 = all), folding it into the embedding; affected entities re-embed. */
export function backfillZohoAttachmentText(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/zoho/attachment-text-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}
export function backfillDiscourseAttachmentText(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/discourse/attachment-text-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}
export function backfillAzureAttachmentText(days: number, reset = false, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/azure/attachment-text-backfill?days=${encodeURIComponent(String(days))}&reset=${reset ? 1 : 0}`, {}, onEvent);
}

/** Pre-download + keep all attachments on entities modified in the last `days`
 *  days (0 = all) so detail-page download links are instant. Server-side run;
 *  progress in the Sync panel. */
export function backfillZohoAttachments(days: number, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/zoho/attachment-backfill?days=${encodeURIComponent(String(days))}`, {}, onEvent);
}
export function backfillDiscourseAttachments(days: number, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/discourse/attachment-backfill?days=${encodeURIComponent(String(days))}`, {}, onEvent);
}
export function backfillAzureAttachments(days: number, onEvent?: (e: { type: string; message?: string }) => void): Promise<void> {
  return postSse(`/azure/attachment-backfill?days=${encodeURIComponent(String(days))}`, {}, onEvent);
}

export async function setZohoStatus(id: string, status: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/zoho/tickets/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function assignZohoTicket(id: string, assigneeId: string): Promise<{ assigneeId: string | null }> {
  return request<{ assigneeId: string | null }>(`/zoho/tickets/${id}/assign`, {
    method: 'POST',
    body: JSON.stringify({ assigneeId }),
  });
}

export async function getZohoTicket(id: string): Promise<ZohoTicketDetail> {
  return request<ZohoTicketDetail>(`/zoho/tickets/${id}`);
}

export async function setZohoTicketReviewed(id: string, reviewed: boolean): Promise<{ id: string; reviewed: boolean }> {
  return request<{ id: string; reviewed: boolean }>(`/zoho/tickets/${id}/reviewed`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

export async function setAllZohoTicketsReviewed(reviewed = true): Promise<{ updated: number; reviewed: boolean }> {
  return request<{ updated: number; reviewed: boolean }>('/zoho/tickets/reviewed-all', {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

/** Clear the synced ticket mirror + cursors so the next sync rebuilds the window. */
export async function resetZohoSync(): Promise<{ tickets: number }> {
  return request<{ tickets: number }>('/zoho/reset', { method: 'POST' });
}

/**
 * Manual historical recovery: scan the LIVE ticket list for the last N days and
 * upsert anything missing from the mirror. Unlike the incremental sync (which
 * relies on the search endpoint and is forward-only), this catches tickets the
 * search endpoint never surfaced. It deliberately does NOT move the sync cursors.
 */
export function resyncZoho(
  days: number,
  onEvent?: (e: { type: string; message?: string; [k: string]: unknown }) => void,
): Promise<{ recovered?: number; upserted?: number; threads?: number; message?: string }> {
  return postSseResult('/zoho/resync', { days }, onEvent);
}

/** Trigger a Zoho ticket sync, streaming SSE progress events. */
export async function syncZoho(
  params: { force?: boolean },
  onEvent: (event: { type: string; message?: string; [k: string]: unknown }) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/zoho/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async () => {
          try {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (!part.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(part.slice(6));
                  if (data.type === 'error') { reject(new Error(data.message)); return; }
                  if (data.type === 'done') {
                    onEvent(data);
                    // A runner-reported terminal failure arrives on the 'done' frame
                    // (status:'failed') — surface it as an error, not a green toast.
                    if (data.status === 'failed') reject(new Error(data.message || 'Sync failed'));
                    else resolve();
                    return;
                  }
                  onEvent(data);
                } catch { /* ignore partial frames */ }
              }
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        pump();
      })
      .catch(reject);
  });
}

// ─── RealEquity (Tenants area) ────────────────────────────────────

function realequityQueryString(p: RealEquityTenantListParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  p.statuses?.forEach((s) => qs.append('statuses', s));
  if (p.isProduction && p.isProduction !== 'all') qs.set('isProduction', p.isProduction);
  if (p.includeMissing) qs.set('includeMissing', '1');
  if (p.sort) qs.set('sort', p.sort);
  qs.set('page', String(p.page ?? 1));
  qs.set('pageSize', String(p.pageSize ?? 50));
  return qs.toString();
}

export async function getRealEquityTenants(params: RealEquityTenantListParams): Promise<RealEquityListResult<RealEquityTenantSummary>> {
  return request<RealEquityListResult<RealEquityTenantSummary>>(`/realequity/tenants?${realequityQueryString(params)}`);
}

export async function getRealEquityFacets(): Promise<RealEquityFacets> {
  return request<RealEquityFacets>('/realequity/facets');
}

export async function getRealEquityTenant(reId: string): Promise<RealEquityTenantDetail> {
  return request<RealEquityTenantDetail>(`/realequity/tenants/${reId}`);
}

export async function getRealEquityResourceGroup(reId: string): Promise<RealEquityResourceGroupDetail> {
  return request<RealEquityResourceGroupDetail>(`/realequity/resource-groups/${reId}`);
}

export async function getRealEquityApplication(reId: string): Promise<RealEquityApplicationDetail> {
  return request<RealEquityApplicationDetail>(`/realequity/applications/${reId}`);
}

export async function getRealEquityDataStore(reId: string): Promise<RealEquityDataStoreDetail> {
  return request<RealEquityDataStoreDetail>(`/realequity/datastores/${reId}`);
}

export async function searchRealEquity(q: string): Promise<RealEquitySearchResults> {
  return request<RealEquitySearchResults>(`/realequity/search?q=${encodeURIComponent(q)}`);
}

/** Trigger a RealEquity sync, streaming SSE progress events (same dialect as syncZoho). */
export async function syncRealEquity(
  onEvent: (event: { type: string; message?: string; [k: string]: unknown }) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/realequity/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async () => {
          try {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (!part.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(part.slice(6));
                  if (data.type === 'error') { reject(new Error(data.message)); return; }
                  if (data.type === 'done') {
                    onEvent(data);
                    // A runner-reported terminal failure arrives on the 'done' frame
                    // (status:'failed') — surface it as an error, not a green toast.
                    if (data.status === 'failed') reject(new Error(data.message || 'Sync failed'));
                    else resolve();
                    return;
                  }
                  onEvent(data);
                } catch { /* ignore partial frames */ }
              }
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        pump();
      })
      .catch(reject);
  });
}

// ─── AI Models ──────────────────────────────────────────────────

export interface AiModelsResponse {
  [key: string]: string[];
  openai: string[];
  ollama: string[];
}

export async function getAiModels(): Promise<AiModelsResponse> {
  return request<AiModelsResponse>('/ai/models');
}

export async function getLatestAiRequest(): Promise<any> {
  return request<any>('/ai/latest-request');
}

// ─── Settings ───────────────────────────────────────────────────

export async function getSetting(key: string): Promise<string | null> {
  try {
    const result = await request<{ key: string; value: string }>(`/settings/${key}`);
    return result.value;
  } catch {
    return null;
  }
}

export async function saveSetting(key: string, value: string): Promise<void> {
  await request(`/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

export async function getAllSettings(): Promise<Record<string, string>> {
  return request<Record<string, string>>('/settings');
}

// ─── AI Conversations & Usage ───────────────────────────────────

export async function getConversations(): Promise<AiConversation[]> {
  return request<AiConversation[]>('/conversations');
}

export async function getConversation(id: string): Promise<AiConversation> {
  return request<AiConversation>(`/conversations/${id}`);
}

export interface DiscussSettings {
  provider?: string;
  model?: string;
  think?: ThinkSetting;
  numCtx?: number | null;
  knowledgeSelection?: KnowledgeSelection | null;
}

export async function createConversationFromTopic(topicId: number, opts?: DiscussSettings): Promise<AiConversation> {
  return request<AiConversation>('/ai/conversations/from-topic', {
    method: 'POST',
    body: JSON.stringify({ topicId, ...opts }),
  });
}

export async function createConversationFromCommit(commitId: string, opts?: DiscussSettings): Promise<AiConversation> {
  return request<AiConversation>('/ai/conversations/from-commit', {
    method: 'POST',
    body: JSON.stringify({ commitId, ...opts }),
  });
}

export async function createConversation(data: Partial<AiConversation>): Promise<AiConversation> {
  return request<AiConversation>('/conversations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateConversation(id: string, data: Partial<AiConversation>): Promise<AiConversation> {
  return request<AiConversation>(`/conversations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteConversation(id: string): Promise<void> {
  return request<void>(`/conversations/${id}`, {
    method: 'DELETE',
  });
}

export async function pushConversationUsage(
  id: string,
  usageDelta: { promptTokens: number; completionTokens: number; totalTokens: number }
): Promise<void> {
  return request<void>(`/conversations/${id}/usage`, {
    method: 'POST',
    body: JSON.stringify(usageDelta),
  });
}

// ─── Support Chat (embedded RealEquity B2B) ─────────────────────
// Plain request/response transport for the customer support widget. The same
// two calls back the future B2B portal — keep them self-contained.

export async function startSupportChat(
  opts: SupportChatStartRequest = {},
): Promise<SupportChatStartResponse> {
  return request<SupportChatStartResponse>('/support-chat/start', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export async function sendSupportMessage(
  conversationId: string,
  message: string,
  quickReplyLabel?: string,
): Promise<SupportChatSendResponse> {
  return request<SupportChatSendResponse>('/support-chat/message', {
    method: 'POST',
    body: JSON.stringify({ conversationId, message, quickReplyLabel } satisfies SupportChatSendRequest),
  });
}

// ── Operator side: the PCP support inbox ──

export async function listSupportChats(
  status?: SupportChatStatus,
): Promise<{ chats: SupportChatSummary[]; counts: SupportChatCounts }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<{ chats: SupportChatSummary[]; counts: SupportChatCounts }>(`/support-chat/chats${qs}`);
}

export async function getSupportChatDetail(id: string): Promise<SupportChatDetail> {
  return request<SupportChatDetail>(`/support-chat/chats/${id}`);
}

export async function updateSupportChatStatus(id: string, status: SupportChatStatus): Promise<SupportChatSummary> {
  return request<SupportChatSummary>(`/support-chat/chats/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/** Operator posts a human reply into a support thread (takes the AI out of auto-reply). */
export async function postSupportReply(id: string, message: string): Promise<SupportChatSendResponse> {
  return request<SupportChatSendResponse>(`/support-chat/chats/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

/** Operator "Draft with AI" — suggest the next reply for a human to edit + send. */
export async function draftSupportReply(id: string): Promise<SupportDraftResponse> {
  return request<SupportDraftResponse>(`/support-chat/chats/${id}/draft`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Customer-widget polling for new agent/human messages since `after` (ISO). */
export async function syncSupportMessages(id: string, after?: string): Promise<SupportChatSyncResponse> {
  const qs = after ? `?after=${encodeURIComponent(after)}` : '';
  return request<SupportChatSyncResponse>(`/support-chat/sync/${id}${qs}`);
}

/** SSE endpoint for realtime support delivery (consume with `new EventSource(url)`). */
export function supportStreamUrl(id: string): string {
  return `${BASE_URL}/support-chat/stream/${id}`;
}

/** SSE endpoint for app-wide realtime change events (RealtimeBridge). */
export function eventsStreamUrl(): string {
  return `${BASE_URL}/events`;
}

/** Reconnect/resync probe — which scope keys changed since `since`, + current cursor. */
export async function getChanges(since: number): Promise<PcpChangesSince> {
  return request<PcpChangesSince>(`/changes?since=${since}`);
}

/** Customer ends the conversation (no more help needed) → closes it server-side. */
export async function endSupportChat(id: string): Promise<SupportChatSummary> {
  return request<SupportChatSummary>(`/support-chat/end/${id}`, { method: 'POST', body: JSON.stringify({}) });
}

/** Fire-and-forget transient "typing…" ping (not persisted). Failures are ignored. */
export function sendSupportTyping(id: string, who: 'customer' | 'agent'): void {
  void fetch(`${BASE_URL}/support-chat/typing/${id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ who }),
  }).catch(() => { /* transient ping — ignore network errors */ });
}

// ── Bulk customer-visibility flagging (feeds the Support assistant's RAG) ──

export async function setCocoConfigAudience(ids: string[], audience: KnowledgeAudience): Promise<{ updated: number }> {
  return request<{ updated: number }>('/coco-config/bulk-audience', {
    method: 'POST',
    body: JSON.stringify({ ids, audience }),
  });
}

export async function setBacklogItemsAudience(ids: number[], audience: KnowledgeAudience): Promise<{ updated: number }> {
  return request<{ updated: number }>('/backlog/items/bulk-audience', {
    method: 'POST',
    body: JSON.stringify({ ids, audience }),
  });
}

export async function getGlobalUsage(): Promise<GlobalTokenUsage[]> {
  return request<GlobalTokenUsage[]>('/usage');
}

export async function getUsageStats(period: UsagePeriod): Promise<UsageStats> {
  return request<UsageStats>(`/usage/stats?period=${period}`);
}

export async function getInsightsOverview(range: InsightsRange): Promise<InsightsOverview> {
  return request<InsightsOverview>(`/insights/overview?range=${range}`);
}

export async function getInsightsAi(range: InsightsRange): Promise<InsightsAi> {
  return request<InsightsAi>(`/insights/ai?range=${range}`);
}

export async function getInsightsEngineering(range: InsightsRange): Promise<InsightsEngineering> {
  return request<InsightsEngineering>(`/insights/engineering?range=${range}`);
}

export async function getInsightsNarrative(range: InsightsRange, refresh = false): Promise<InsightsNarrative> {
  return request<InsightsNarrative>(`/insights/narrative?range=${range}${refresh ? '&refresh=1' : ''}`);
}

// ─── AI Chat ────────────────────────────────────────────────────

export async function sendChatMessage(
  req: AiChatRequest,
): Promise<AiChatResponse> {
  return request<AiChatResponse>('/ai/chat', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function streamChatMessage(
  req: AiChatRequest,
  onChunk: (chunk: AiStreamChunk) => void,
  onDone: () => void,
  onError: (error: Error) => void,
): Promise<void> {
  const url = `${BASE_URL}/ai/chat`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ ...req, stream: true }),
    });

    if (!response.ok) {
      const e = await response.json().catch(() => ({}));
      throw new ApiError(response.status, e.error || 'Request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') {
          onDone();
          return;
        }

        try {
          const chunk = JSON.parse(data) as AiStreamChunk;
          onChunk(chunk);

          if (chunk.type === 'done') {
            onDone();
            return;
          }
          if (chunk.type === 'error') {
            onError(new Error(chunk.content));
            return;
          }
        } catch {
          // Skip unparseable chunks
        }
      }
    }

    onDone();
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
  }
}

// ─── Discourse ──────────────────────────────────────────────────

function discourseQueryString(p: DiscourseListParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.status && p.status !== 'all') qs.set('status', p.status);
  (p.states ?? []).forEach((s) => qs.append('states', s));
  if (p.reviewed && p.reviewed !== 'all') qs.set('reviewed', p.reviewed);
  (p.tags ?? []).forEach((t) => qs.append('tags', t));
  if (p.hasAttachments) qs.set('hasAttachments', '1');
  if (p.sort) qs.set('sort', p.sort);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  return qs.toString();
}

export async function getDiscourseTopics(params: DiscourseListParams = {}): Promise<DiscourseListResult> {
  return request<DiscourseListResult>(`/discourse/topics?${discourseQueryString(params)}`);
}

export async function getDiscourseFacets(): Promise<DiscourseFacets> {
  return request<DiscourseFacets>(`/discourse/facets`);
}

/** Forum composer limits (min post length), so we can gate before posting. */
export async function getDiscourseLimits(): Promise<DiscourseComposerLimits> {
  return request<DiscourseComposerLimits>(`/discourse/limits`);
}

/** Discourse categories (for the Settings category picker). */
export async function getDiscourseCategories(): Promise<DiscourseCategory[]> {
  return request<DiscourseCategory[]>(`/discourse/categories`);
}

// ─── Employees ───────────────────────────────────────────────────

export async function getEmployees(): Promise<Employee[]> {
  return request<Employee[]>(`/employees`);
}
export async function getEmployee(id: string): Promise<EmployeeDetail> {
  return request<EmployeeDetail>(`/employees/${id}`);
}
/** Link the employee to a PCP user (commit attribution + identities follow the
 *  link) or toggle active. Admin only (users.manage). */
export async function updateEmployee(
  id: string,
  body: { authUserId?: string | null; active?: boolean },
): Promise<Employee> {
  return request<Employee>(`/employees/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export async function syncEmployeeRoster(): Promise<{ created: number; total: number }> {
  return request<{ created: number; total: number }>(`/employees/roster-sync`, { method: 'POST', body: '{}' });
}
/** Rebuild employee profiles (SSE-streamed, shows in the Sync log). */
export async function rebuildEmployeeProfiles(onProgress?: (message: string) => void): Promise<{ rebuilt: number }> {
  return streamNextSteps('/employees/rebuild', onProgress);
}

export async function getDiscourseTopic(id: string): Promise<DiscourseTopicDetails> {
  return request<DiscourseTopicDetails>(`/discourse/topics/${id}`);
}

/** Force a re-sync of one topic from Discourse and return the fresh detail. */
export async function resyncTopic(id: string | number): Promise<DiscourseTopicDetails> {
  return request<DiscourseTopicDetails>(`/discourse/topics/${id}/refresh`, { method: 'POST' });
}

// ─── Topic → Azure DevOps work item ──────────────────────────────

/** AI-draft a work item from the topic, plus candidate existing items. Writes nothing. */
export async function draftWorkItem(topicId: number, opts?: { steer?: string; workItemType?: string } & AiOverride): Promise<WorkItemDraftResult> {
  return request<WorkItemDraftResult>(`/discourse/topics/${topicId}/workitem/draft`, {
    method: 'POST',
    body: JSON.stringify(opts ?? {}),
  });
}

/** Create or update a work item in Azure DevOps from the confirmed draft.
 *  `useGlobalConnection` is per-action consent to use the shared ADO connection. */
export async function commitWorkItem(
  topicId: number,
  payload: {
    mode: 'create' | 'update';
    workItemId?: number;
    /** acceptanceCriteria is left out for a type that has none (a Feature, b-02013). */
    fields: Omit<WorkItemDraft, 'acceptanceCriteria'> & { acceptanceCriteria?: string };
    aiSuggestion?: unknown;
    provider?: string;
    model?: string;
    useGlobalConnection?: boolean;
    /** Reorder a new item to the top of the backlog. Server defaults to true. */
    placeAtTop?: boolean;
  },
): Promise<WorkItemCommitResult> {
  return request<WorkItemCommitResult>(`/discourse/topics/${topicId}/workitem/commit`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Link an existing work item to the topic. */
export async function linkWorkItem(topicId: number, workItemId: number): Promise<{ topicId: number; workItemId: number }> {
  return request<{ topicId: number; workItemId: number }>(`/discourse/topics/${topicId}/workitem/link`, {
    method: 'POST',
    body: JSON.stringify({ workItemId }),
  });
}

/** Work items linked to a topic. */
export async function getLinkedWorkItems(topicId: number): Promise<LinkedWorkItem[]> {
  return request<LinkedWorkItem[]>(`/discourse/topics/${topicId}/work-items`);
}

/** Items a ticket relates to (cross-references mined from its conversation). */
export async function getTicketRelatedItems(ticketId: string): Promise<RelatedItem[]> {
  return request<RelatedItem[]>(`/zoho/tickets/${encodeURIComponent(ticketId)}/related-items`);
}

/** Search the local synced work items by title or id (for manual linking). */
export async function searchWorkItems(q: string): Promise<AzureWorkItemSummary[]> {
  return request<AzureWorkItemSummary[]>(`/backlog/items/search?q=${encodeURIComponent(q)}`);
}

/** Valid states for a work item type (from Azure DevOps). */
export async function getWorkItemStates(type: string): Promise<string[]> {
  return request<string[]>(`/azure/states?type=${encodeURIComponent(type)}`);
}

/** The team's ADO Kanban boards + columns (name/order/isSplit/stateMappings) — drives
 *  the board's lanes incl. Doing/Done split columns. `type` is an ado_* code. */
export async function getAzureBoardColumns(type: string): Promise<AdoBoardsResult> {
  return request<AdoBoardsResult>(`/azure/board-columns?type=${encodeURIComponent(type)}`);
}

/** Assignee candidates for the picker. `type` restricts to that work-item
 *  group's configured allow-list (empty list = whole team roster). */
export async function getAzureAssignees(type?: import('@pcp/shared').AdoItemType): Promise<AzureUser[]> {
  return request<AzureUser[]>(`/azure/assignees${type ? `?type=${encodeURIComponent(type)}` : ''}`);
}

/** Full assignee rosters for both groups, each person annotated with the PCP user
 *  they resolve to — drives the Settings "Assignee candidates" editor (admin). */
export async function getAzureAssigneeRoster(): Promise<import('@pcp/shared').AssigneeRosters> {
  return request<import('@pcp/shared').AssigneeRosters>(`/azure/assignee-roster`);
}

// ─── Insights ────────────────────────────────────────────────────

export async function getInsights(): Promise<import('@pcp/shared').DiscourseInsights> {
  return request<import('@pcp/shared').DiscourseInsights>(`/discourse/insights`);
}

// ─── Next-steps recommender ──────────────────────────────────────

/**
 * Shared SSE consumer for the next-steps recommenders (topics, tickets, work
 * items). Streams one-line `progress` events to `onProgress`; resolves with the
 * final `result` payload. Same wire format as the analysis stream.
 */
async function streamNextSteps<T>(path: string, onProgress?: (message: string) => void, body: unknown = {}, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    // Abandoning a draft should stop the stream (and the LLM call behind it)
    // rather than let it run to completion against the API budget.
    signal,
  });
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new ApiError(response.status, e.error || 'Request failed');
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let result: T | null = null;
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(line.slice(6));
        if (event.type === 'progress') onProgress?.(event.message);
        else if (event.type === 'result') result = event.data as T;
        else if (event.type === 'error') throw new Error(event.message);
      } catch (e) {
        if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
      }
    }
  }
  if (!result) throw new Error('No result received from the recommender');
  return result;
}

/** Read the persisted suggestion (no AI). Null if none generated yet. */
export async function fetchNextSteps(topicId: number): Promise<NextStepsResult | null> {
  return request<NextStepsResult | null>(`/discourse/topics/${topicId}/next-steps`);
}

/** Generate a fresh suggestion via AI (and persist it), streaming progress. */
export async function generateNextSteps(topicId: number, onProgress?: (message: string) => void): Promise<NextStepsResult> {
  return streamNextSteps<NextStepsResult>(`/discourse/topics/${topicId}/next-steps`, onProgress);
}

export async function getDiscourseSyncStatus(): Promise<{ lastSyncedAt: string | null; topicCount: number }> {
  return request<{ lastSyncedAt: string | null; topicCount: number }>('/discourse/sync-status');
}

// ─── Global Sync Log ─────────────────────────────────────────────

export interface SyncRun {
  id: string;
  /** Activity type, e.g. discourse | commits | release | azure-devops | zoho | knowledge | documents | … */
  kind: string;
  label: string;
  status: 'running' | 'completed' | 'partial' | 'failed' | 'stopped' | 'interrupted' | 'rejected';
  message: string;
  stats: Record<string, number> | null;
  /** Structured live progress for a determinate bar + finish ETA. */
  progress: { phase?: string; done: number; total: number } | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export async function getSyncRuns(opts?: { kind?: string; limit?: number }): Promise<SyncRun[]> {
  const qs = new URLSearchParams();
  if (opts?.kind) qs.set('kind', opts.kind);
  if (opts?.limit) qs.set('limit', String(opts.limit));
  const q = qs.toString();
  return request<SyncRun[]>(`/syncs${q ? `?${q}` : ''}`);
}

// ─── Maintenance (Danger Zone) ───────────────────────────────────

export interface ClearAllResult {
  counts: Record<string, number>;
  total: number;
}

/** Irreversibly clears ALL synced data, sync logs, and analysis runs. */
export async function clearAllData(): Promise<ClearAllResult> {
  return request<ClearAllResult>('/maintenance/clear-all', { method: 'POST' });
}

export async function stopSyncRun(id: string): Promise<{ stopped: boolean }> {
  return request<{ stopped: boolean }>(`/syncs/${id}/stop`, { method: 'POST' });
}

/** Composite "Run all" for one integration: sync → image knowledge → attachment
 *  text → re-embed the affected items, bounded to `days` (0 = all time). One
 *  tracked run; progress streams into the activity area. */
export function runAllForIntegration(
  integration: 'zoho' | 'azure' | 'discourse', days: number,
  onEvent?: (e: { type: string; message?: string }) => void,
): Promise<void> {
  return postSse(`/sync-control/run-all?integration=${integration}&days=${days > 0 ? days : 'all'}`, {}, onEvent);
}

// ─── Authoring (Feature → Stories) ───────────────────────────────

export interface StoryGroundingRef {
  ref: number;
  source: string;
  id: number;
  title: string;
  snippet: string;
  url: string | null;
}

export interface StoryDraft {
  title: string;
  /** Rich-text HTML. */
  description: string;
  /** Rich-text HTML (an `<ul>` of criteria). */
  acceptanceCriteria: string;
  suggestedTags: string[];
  priority: number;
  rationale: string;
  confidence: number;
  groundingRefs: number[];
  possibleDuplicateOf: number | null;
  groundedIn: Array<{ source: string; id: number; title: string; url: string | null }>;
}

export interface FeatureStoriesDraft {
  feature: { id: number; title: string; url: string | null };
  stories: StoryDraft[];
  grounding: StoryGroundingRef[];
  /** The Feature's linked Feedback boards that were actually read into the prompt.
   *  Absent on an older API — treat as none. (f-01965) */
  feedbackBoards?: DraftFeedbackBoard[];
  provider: string;
  model: string;
  usage?: AiUsageInfo;
}

export interface StoryCommitInput {
  title: string;
  /** Rich-text HTML (from the editor). */
  description?: string;
  /** Rich-text HTML (from the editor). */
  acceptanceCriteria?: string;
  tags?: string[];
  priority?: number;
  /** Azure DevOps System.State; empty → server falls back to the configured default. */
  state?: string;
  /** Azure DevOps assignee (uniqueName/email); empty → unassigned. */
  assignedTo?: string;
  /** Microsoft.VSTS.Scheduling.StoryPoints; null/0 → unset. */
  storyPoints?: number | null;
  aiDraft?: unknown;
  groundedIn?: unknown;
}

/** One scored assignee candidate from an AI suggestion run. */
export interface AssigneeRanking {
  displayName: string;
  uniqueName: string;
  /** 0–1 fit confidence. */
  confidence: number;
  reason: string;
}

export interface AssigneeSuggestion {
  assignee: { displayName: string; uniqueName: string } | null;
  rationale: string;
  /** Every candidate scored (strongest first) — drives the per-person score in the picker. */
  rankings?: AssigneeRanking[];
  usage?: AiUsageInfo;
}

/** AI-suggest the best assignee for an in-progress story draft. `workItemType`
 *  constrains the suggestion to that group's assignee allow-list. */
export async function suggestStoryAssignee(input: {
  title: string; description?: string; acceptanceCriteria?: string; tags?: string[];
  workItemType?: import('@pcp/shared').AdoItemType;
}): Promise<AssigneeSuggestion> {
  return request<AssigneeSuggestion>(
    `/authoring/suggest-assignee`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

/** AI-predict an estimate, snapped to the allowed scale: Story points for a Story/Bug
 *  (grounded in similar past stories), or Effort for a Feature (grounded in similar past
 *  Features) when `workItemType` is 'ado_feature'. */
export async function predictStoryPoints(input: {
  title: string; description?: string; acceptanceCriteria?: string; tags?: string[];
  workItemType?: string;
}): Promise<{ points: number | null; rationale: string; examples: Array<{ id: number; title: string; points: number }>; usage?: AiUsageInfo }> {
  return request(`/authoring/predict-story-points`, { method: 'POST', body: JSON.stringify(input) });
}

export async function draftFeatureStories(
  featureId: number,
  opts?: {
    provider?: string; model?: string; think?: ThinkSetting; numCtx?: number | null;
    /** How many stories to draft (default 1, capped server-side at maxStoriesPerFeature). */
    count?: number;
    /** Free-text steering for how the work should be split. */
    steer?: string;
  },
  onProgress?: (message: string) => void,
  signal?: AbortSignal,
): Promise<FeatureStoriesDraft> {
  return streamNextSteps<FeatureStoriesDraft>(`/authoring/features/${featureId}/stories/draft`, onProgress, opts ?? {}, signal);
}

export async function commitFeatureStories(
  featureId: number,
  stories: StoryCommitInput[],
  opts?: {
    provider?: string; model?: string; placeAtTop?: boolean;
    /** Default ON: also link the Feature's Feedback Boards to every story created
     *  here, so each carries the evidence it was drafted from. (f-01966) */
    includeFeedbackBoards?: boolean;
  },
): Promise<{ created: Array<{ id: number; title: string; url: string | null }>; placedAtTop?: boolean; linkedFeedbackBoards?: number }> {
  return request<{ created: Array<{ id: number; title: string; url: string | null }>; placedAtTop?: boolean; linkedFeedbackBoards?: number }>(
    `/authoring/features/${featureId}/stories/commit`,
    { method: 'POST', body: JSON.stringify({ stories, ...opts }) },
  );
}

/** AI-merge 2+ in-progress story drafts into one combined story (writes nothing to ADO). */
export async function combineFeatureStories(
  featureId: number,
  stories: Array<{
    title: string;
    description?: string;        // rich-text HTML
    acceptanceCriteria?: string; // rich-text HTML
    tags?: string[];
    priority?: number;
    rationale?: string;
    groundedIn?: StoryDraft['groundedIn'];
  }>,
  opts?: { provider?: string; model?: string },
): Promise<{ story: StoryDraft; groundedIn: StoryDraft['groundedIn']; provider: string; model: string; usage?: AiUsageInfo }> {
  return request<{ story: StoryDraft; groundedIn: StoryDraft['groundedIn']; provider: string; model: string; usage?: AiUsageInfo }>(
    `/authoring/features/${featureId}/stories/combine`,
    { method: 'POST', body: JSON.stringify({ stories, ...opts }) },
  );
}

// ─── Authoring (free-text → standalone work item) ────────────────

export interface AuthoredWorkItemDraft {
  workItemType: AdoItemType;
  title: string;
  description: string;
  reproSteps: string;
  acceptanceCriteria: string;
  tags: string[];
  areaPath: string;
  iterationPath: string;
  priority: number;
  confidence: number;
  state: string;
  assignedTo: string;
  suggestedAssignee: string;
}

/** A Feedback board the server actually read into a draft's context. Summary only —
 *  the rendered brief stays server-side. (f-01965) */
export interface DraftFeedbackBoard {
  id: string;
  shortId: string;
  title: string;
}

export interface AuthoredWorkItemDraftResult {
  draft: AuthoredWorkItemDraft;
  sources?: DocCitation[];
  /** Boards folded into the prompt, so the UI can say what the draft was written
   *  from. Absent on an older API — treat as none. */
  feedbackBoards?: DraftFeedbackBoard[];
  confidence?: number;
  calibratedConfidence?: number;
  verification?: ZohoDraftVerification;
  steeringOptions?: ZohoSteeringOption[];
  provider: string;
  model: string;
  usage?: AiUsageInfo;
}

/** AI-draft a standalone work item from a free-text brief, or — when `current`
 *  (+ `itemId`) is supplied — refine an existing item in place (streams progress). */
export async function draftWorkItemFromText(
  params: {
    workItemType: string; input: string; provider?: string; model?: string;
    think?: ThinkSetting; numCtx?: number | null;
    itemId?: number;
    current?: { title?: string; description?: string; reproSteps?: string; acceptanceCriteria?: string };
    /** The Feature this item is being created under. The server uses it to give the
     *  model the parent's own text, its existing children, and — the point of
     *  sending it at draft time rather than only at commit — the Feedback boards
     *  linked to that Feature. (f-01965) */
    parentId?: number;
    /** Boards the caller already supplies through `input`, so the parent's context
     *  does not inject them a second time. */
    excludeFeedbackIds?: string[];
  },
  onProgress?: (message: string) => void,
): Promise<AuthoredWorkItemDraftResult> {
  return streamNextSteps<AuthoredWorkItemDraftResult>('/authoring/backlog-item/draft', onProgress, params);
}

export interface CreateWorkItemFields {
  title: string;
  description?: string;
  reproSteps?: string;
  /** Bug-only System Info (Microsoft.VSTS.TCM.SystemInfo). */
  systemInfo?: string;
  acceptanceCriteria?: string;
  tags?: string[];
  areaPath?: string;
  iterationPath?: string;
  priority?: number;
  state?: string;
  assignedTo?: string;
  /** Microsoft.VSTS.Scheduling.StoryPoints — User Story/Bug only; 0 clears the estimate. */
  storyPoints?: number | null;
  /** Microsoft.VSTS.Scheduling.Effort — Feature only (the Feature-side twin of
   *  storyPoints); 0 clears the estimate. */
  effort?: number | null;
}

/** Create a standalone work item (no parent) in Azure DevOps from a confirmed draft.
 *  `useGlobalConnection` is per-action consent to use the shared ADO connection. */
export async function createBacklogItem(body: {
  workItemType: string;
  fields: CreateWorkItemFields;
  aiDraft?: unknown;
  provider?: string;
  model?: string;
  useGlobalConnection?: boolean;
  /** Default ON: reorder the new item to the top of its product backlog. */
  placeAtTop?: boolean;
  /** Create the item as a child of this work item (the Feature it was started from). */
  parentId?: number;
  /** Default ON: also link the parent's Feedback Boards to the new item. Ignored
   *  without a `parentId` — a standalone create has nothing to inherit. (f-01966) */
  includeFeedbackBoards?: boolean;
}): Promise<{ id: number; title: string; url: string | null; placedAtTop?: boolean; linkedFeedbackBoards?: number }> {
  return request('/authoring/backlog-item/commit', { method: 'POST', body: JSON.stringify(body) });
}

/** Edit an existing work item's fields in Azure DevOps (saved immediately). The
 *  item's type is fixed server-side; type-specific fields are routed by it.
 *  Accepts a PARTIAL field set — omitted fields are left untouched server-side, so
 *  the inline section editor can save just Description + Acceptance Criteria/Repro
 *  without rewriting (and risking a stale revert of) title/tags/state/etc.
 *  `useGlobalConnection` is per-action consent to use the shared ADO connection. */
export async function updateBacklogItem(
  id: number,
  fields: Partial<CreateWorkItemFields>,
  opts?: { useGlobalConnection?: boolean },
): Promise<{ id: number; title: string; url: string | null }> {
  return request(`/backlog/items/${id}`, {
    method: 'POST',
    body: JSON.stringify({ fields, useGlobalConnection: opts?.useGlobalConnection }),
  });
}

// ─── Knowledge base (RAG) ────────────────────────────────────────

export interface KnowledgeStatus {
  enabled: boolean;
  embeddingModel: string;
  workItems: { total: number; embedded: number; commentsSynced: number };
  discourse: { total: number; embedded: number };
  zoho?: { total: number; embedded: number };
  commits?: { total: number; embedded: number };
  documents?: { total: number; embedded: number };
  actions?: { total: number; embedded: number };
  ticketActions?: { total: number; embedded: number };
  ticketReplyActions?: { total: number; embedded: number };
}

export async function getKnowledgeStatus(): Promise<KnowledgeStatus> {
  return request<KnowledgeStatus>('/knowledge/status');
}

export interface EmbedActivityItem { ts: number; source: string; label: string }

export async function getEmbedActivity(): Promise<EmbedActivityItem[]> {
  const r = await request<{ items: EmbedActivityItem[] }>('/knowledge/activity');
  return r.items;
}

/** Full reindex (SSE). Resolves when the stream ends. */
export interface ReindexProgress {
  type: string;
  message?: string;
  counts?: {
    workItems?: { done: number; total: number };
    topics?: { done: number; total: number };
    tickets?: { done: number; total: number };
    documents?: { done: number; total: number };
    discussions?: number;
  };
}

export async function reindexKnowledge(
  source: 'all' | 'work-items' | 'topics' | 'tickets' | 'documents',
  onEvent?: (event: ReindexProgress) => void,
  mode: 'reset' | 'missing' = 'reset',
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/knowledge/reindex?source=${source}&mode=${mode}`, { method: 'POST' })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async () => {
          try {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (!part.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(part.slice(6));
                  if (data.type === 'error') { reject(new Error(data.message)); return; }
                  if (data.type === 'done') { resolve(); return; }
                  onEvent?.(data);
                } catch { /* ignore partial frames */ }
              }
            }
            resolve();
          } catch (err) { reject(err); }
        };
        pump();
      })
      .catch(reject);
  });
}

export async function syncDiscourse(
  params: { category?: string; force?: boolean },
  onEvent: (event: { type: string; message?: string }) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/discourse/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (part.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(part.slice(6));
                    if (data.type === 'error') { reject(new Error(data.message)); return; }
                    if (data.type === 'done') { resolve(); return; }
                    onEvent(data);
                  } catch { /* ignore partial frames */ }
                }
              }
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        processChunk();
      })
      .catch(reject);
  });
}

// ─── Documentation knowledge base ───────────────────────────────

export async function getDocuments(scope?: DocScope): Promise<DocumentListResult> {
  return request<DocumentListResult>(`/documents${scope ? `?scope=${scope}` : ''}`);
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  return request<DocumentDetail>(`/documents/${id}`);
}

export async function searchDocuments(scope: DocScope, q: string): Promise<DocSearchHit[]> {
  const qs = new URLSearchParams({ q });
  if (scope) qs.set('scope', scope);
  return request<DocSearchHit[]>(`/documents/search?${qs.toString()}`);
}

/** Direct URL to the original file (for links / inline preview). */
export function documentRawUrl(id: string): string {
  return `${BASE_URL}/documents/${id}/raw`;
}

export async function uploadDocument(scope: DocScope, file: File): Promise<DocumentItem> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE_URL}/documents/upload?scope=${scope}`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Upload failed');
  }
  return (await res.json()).data as DocumentItem;
}

export async function prepareDocument(id: string): Promise<DocumentItem> {
  return request<DocumentItem>(`/documents/${id}/prepare`, { method: 'POST' });
}

export async function updateDocument(id: string, patch: { title?: string; tags?: string[]; audiences?: string[] }): Promise<DocumentItem> {
  return request<DocumentItem>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteDocument(id: string): Promise<void> {
  await request<void>(`/documents/${id}`, { method: 'DELETE' });
}

/** Delete several documents at once (rows + chunks + files). */
export async function deleteDocuments(ids: string[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/documents/delete`, { method: 'POST', body: JSON.stringify({ ids }) });
}

export async function refreshDocuments(): Promise<{ added: number; changed: number; removed: number; total: number }> {
  return request(`/documents/refresh`, { method: 'POST', body: JSON.stringify({}) });
}

/** Prepare all pending/failed documents — SSE progress. */
export async function prepareAllDocuments(onEvent: (e: { type: string; message?: string }) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/documents/prepare-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (!part.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(part.slice(6));
                  if (data.type === 'error') { reject(new Error(data.message)); return; }
                  if (data.type === 'done') { resolve(); return; }
                  onEvent(data);
                } catch { /* partial frame */ }
              }
            }
            resolve();
          } catch (err) { reject(err); }
        };
        pump();
      })
      .catch(reject);
  });
}

/**
 * Clean & rebuild one area: clear extracted text, image data & embeddings for
 * every document in the scope, then re-extract (incl. image transcription) and
 * re-embed each from the file on disk — SSE progress. The files are kept.
 */
export function cleanRebuildDocuments(
  scope: DocScope,
  onEvent?: (e: { type: string; message?: string; [k: string]: unknown }) => void,
): Promise<void> {
  return postSse(`/documents/clean-rebuild?scope=${scope}`, {}, onEvent);
}

// ─── Solution Design (page specifications, f-00127/f-01785/f-01786) ─

/** The folder tree with its rolled-up subtree aggregates. */
export async function getSpecFolders(): Promise<SpecFolderTree> {
  return request<SpecFolderTree>('/specs/folders');
}

/** The tag vocabulary with usage counts, most-used first — feeds both the
 *  detail-page typeahead and the Tags filter. */
export async function getSpecTags(): Promise<SpecTagCount[]> {
  return request<SpecTagCount[]>('/specs/tags');
}

/** The "All specifications" tab. Every filter is a URL param so a search is
 *  shareable and the Back button restores it. */
export async function getSpecs(params: SpecListParams): Promise<SpecListResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.tags?.length) qs.set('tags', params.tags.join(','));
  if (params.unfiled) qs.set('folderId', 'unfiled');
  else if (params.folderId) qs.set('folderId', params.folderId);
  if (params.direct) qs.set('direct', 'true');
  if (params.updated && params.updated !== 'any') qs.set('updated', params.updated);
  if (params.sort) qs.set('sort', params.sort);
  if (params.page && params.page > 1) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const s = qs.toString();
  return request<SpecListResult>(`/specs${s ? `?${s}` : ''}`);
}

export async function createSpecFolder(input: { name: string; parentId?: string | null }): Promise<SpecFolder> {
  return request<SpecFolder>('/specs/folders', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateSpecFolder(id: string, patch: { name?: string; parentId?: string | null }): Promise<SpecFolder> {
  return request<SpecFolder>(`/specs/folders/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteSpecFolder(id: string): Promise<{ movedFolders: number; unfiledSpecs: number }> {
  return request(`/specs/folders/${id}`, { method: 'DELETE' });
}

/** Rewrite one level's manual order. The client sends the COMPLETE new order for
 *  the scope; the server validates the projected result and writes it in one go. */
export async function reorderSpecFolders(parentId: string | null, orderedIds: string[]): Promise<{ changed: number }> {
  return request('/specs/folders/reorder', { method: 'POST', body: JSON.stringify({ parentId, orderedIds }) });
}

export async function reorderSpecsInFolder(folderId: string | null, orderedIds: string[]): Promise<{ changed: number }> {
  return request('/specs/reorder', { method: 'POST', body: JSON.stringify({ folderId, orderedIds }) });
}

/** The Canvas tab's whole board in one read: the folder tree it groups by, plus
 *  every specification with its outline and stored position. No bodies — a page's
 *  content is fetched per sheet (through `getKbArticle`, so it shares the detail
 *  page's cache) only while that page is on screen at reading zoom. */
export async function getSpecCanvasBoard(): Promise<SpecCanvasBoard> {
  return request<SpecCanvasBoard>('/specs/canvas');
}

/** Persist page positions on the shared board. Debounced by the canvas, so this
 *  usually carries the one page that moved. */
export async function saveSpecCanvasPositions(positions: SpecCanvasPosition[]): Promise<{ changed: number }> {
  return request('/specs/canvas', { method: 'POST', body: JSON.stringify({ positions }) });
}

/** Re-parenting is a dialog, not a drag — a move dialog tested better than
 *  cross-level dragging for everyone, not only screen-reader users. */
export async function moveSpecToFolder(id: string, folderId: string | null): Promise<void> {
  await request(`/specs/${id}/move`, { method: 'POST', body: JSON.stringify({ folderId }) });
}

// ─── Knowledge articles (authored .md) ──────────────────────────

export async function getKbArticles(params?: { audience?: string; status?: string; appName?: string; q?: string; docType?: string }): Promise<KnowledgeArticleListResult> {
  const qs = new URLSearchParams();
  if (params?.audience && params.audience !== 'all') qs.set('audience', params.audience);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.appName) qs.set('appName', params.appName);
  if (params?.q) qs.set('q', params.q);
  if (params?.docType) qs.set('docType', params.docType);
  const s = qs.toString();
  return request<KnowledgeArticleListResult>(`/knowledge/articles${s ? `?${s}` : ''}`);
}

export async function getKbArticle(id: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${id}`);
}

export async function createKbArticle(input: { title: string; bodyMd?: string; bodyJson?: unknown; summary?: string; audiences?: string[]; tags?: string[]; appName?: string; docType?: string; folderId?: string | null }): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles`, { method: 'POST', body: JSON.stringify(input) });
}

export async function updateKbArticle(id: string, patch: { title?: string; bodyMd?: string; bodyJson?: unknown; summary?: string; audiences?: string[]; tags?: string[]; appName?: string; status?: string }): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteKbArticle(id: string): Promise<void> {
  await request<void>(`/knowledge/articles/${id}`, { method: 'DELETE' });
}

export async function publishKbArticle(id: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${id}/publish`, { method: 'POST', body: '{}' });
}

export async function archiveKbArticle(id: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${id}/archive`, { method: 'POST', body: '{}' });
}

export async function prepareKbArticle(id: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${id}/prepare`, { method: 'POST', body: '{}' });
}

/** Direct URL to download the article as a .md file (front-matter + body). */
export function kbArticleDownloadUrl(id: string): string {
  return `${BASE_URL}/knowledge/articles/${id}/download`;
}

/** ws/wss origin for realtime connections (BlockNote collaboration). */
export function wsBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

/** Resolve display names/avatars for user ids (comment authors / cursors). */
export async function resolveUsers(ids: string[]): Promise<Array<{ id: string; name: string; avatarUrl: string | null }>> {
  if (!ids.length) return [];
  const q = ids.map((i) => encodeURIComponent(i)).join(',');
  return request<Array<{ id: string; name: string; avatarUrl: string | null }>>(`/users/resolve?ids=${q}`);
}

/** Load the reader detail for an article by SLUG (incl. toc + backlinks). */
export async function getKbArticleBySlug(slug: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/by-slug/${encodeURIComponent(slug)}`);
}

/** Articles that reference this one ("Referenced by"). */
export async function getKbArticleBacklinks(id: string): Promise<KnowledgeArticleBacklink[]> {
  return request<KnowledgeArticleBacklink[]>(`/knowledge/articles/${id}/backlinks`);
}

/** Direct URL to the themed PDF export of an article (audience-gated server-side). */
export function kbArticlePdfUrl(id: string): string {
  return `${BASE_URL}/knowledge/articles/${id}/pdf`;
}

/** Direct URL to download the native BlockNote document JSON (audience-gated). */
export function kbArticleJsonUrl(id: string): string {
  return `${BASE_URL}/knowledge/articles/${id}/json`;
}

/** Upload an inline image for an article; returns its servable URL. */
/** Reference search for the editor's [[wiki-link]] menu (articles + their sections). */
export async function searchKbReferences(q: string): Promise<Array<{ id: string; slug: string; title: string; headings: { section: string; anchor: string }[] }>> {
  return request<Array<{ id: string; slug: string; title: string; headings: { section: string; anchor: string }[] }>>(`/knowledge/articles/search?q=${encodeURIComponent(q)}`);
}

// Page Specification cross-references: pages + their areas/sections (by path), for
// the spec editor's "@" reference picker. Empty q returns the full (capped) index.
export interface PageSpecRefEntry {
  id: string;
  /** Human short id of the page (e.g. "kb-42"), or null for legacy pre-backfill rows. */
  shortId: string | null;
  pagePath: string;
  areas: { name: string; anchor: string; sections: { name: string; anchor: string; fields: { name: string; anchor: string }[] }[] }[];
}
export async function searchPageSpecRefs(q: string): Promise<PageSpecRefEntry[]> {
  return request<PageSpecRefEntry[]>(`/knowledge/specs/refs${q ? `?q=${encodeURIComponent(q)}` : ''}`);
}

// Synced CoCo `uioptionlist`s — for the Page-Spec field "Option list" picker.
// A field can BIND to one of these (read-only mirror from RealEquity) or define a
// NEW list inline in the spec.
export interface SpecOptionList {
  id: string;
  namespace: string;
  name: string;
  description?: string;
  options: string[];
}
export async function getSpecOptionLists(q?: string): Promise<SpecOptionList[]> {
  return request<SpecOptionList[]>(`/coco-config/option-lists${q ? `?q=${encodeURIComponent(q)}` : ''}`);
}

export async function uploadKbArticleImage(file: File | Blob, articleId?: string): Promise<{ url: string; filename: string }> {
  const fd = new FormData();
  fd.append('file', file, (file as File).name || 'image.png');
  // When known, link the upload to its article so the serve route can gate
  // internal-only screenshots (see GET /api/knowledge/images/:file).
  if (articleId) fd.append('articleId', articleId);
  const res = await fetch(`${BASE_URL}/knowledge/articles/images`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Image upload failed');
  }
  return (await res.json()).data as { url: string; filename: string };
}

export async function uploadKbArticleMd(file: File): Promise<KnowledgeArticleDetail> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE_URL}/knowledge/articles/upload`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Upload failed');
  }
  return (await res.json()).data as KnowledgeArticleDetail;
}

/** Prepare/embed all published articles — SSE progress. */
export function prepareAllKbArticles(onEvent?: (e: { type: string; message?: string; [k: string]: unknown }) => void): Promise<void> {
  return postSse(`/knowledge/articles/prepare-all`, {}, onEvent);
}

/** Articles that link to a given target (reverse "linked knowledge"). */
export async function getKbArticlesByTarget(targetType: string, targetId: string): Promise<KnowledgeArticle[]> {
  return request<KnowledgeArticle[]>(`/knowledge/articles/by-target?targetType=${encodeURIComponent(targetType)}&targetId=${encodeURIComponent(targetId)}`);
}

export type KbLinkSuggestion = { targetType: string; targetId: string; name: string; similarity: number };

/** AI-suggested links for an article (embedding similarity to the hierarchy). */
export async function getKbArticleLinkSuggestions(articleId: string): Promise<KbLinkSuggestion[]> {
  return request<KbLinkSuggestion[]>(`/knowledge/articles/${articleId}/link-suggestions`);
}

export interface KbCitationStats {
  mostCited: Array<{ refId: string; title: string | null; count: number }>;
  bySource: Array<{ source: string; count: number }>;
  windowDays: number;
}

/** Citation stats — most-cited articles + counts by source (last N days). */
export async function getKbCitationStats(days = 30): Promise<KbCitationStats> {
  return request<KbCitationStats>(`/knowledge/stats/citations?days=${days}`);
}

/** Draft one field (title|summary|body) with AI, steered by all the other fields. */
export async function aiDraftKbArticle(input: { field: 'title' | 'summary' | 'body'; title?: string; summary?: string; bodyMd?: string; tags?: string[]; references?: string[]; appName?: string }): Promise<{ text: string }> {
  return request<{ text: string }>(`/knowledge/articles/ai-draft`, { method: 'POST', body: JSON.stringify(input) });
}

// Article links (application / entitlement / capability / domain / nav-node).
export async function addKbArticleLink(articleId: string, link: { targetType: string; targetId: string; relation?: string }): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${articleId}/links`, { method: 'POST', body: JSON.stringify(link) });
}

export async function removeKbArticleLink(articleId: string, linkId: string): Promise<KnowledgeArticleDetail> {
  return request<KnowledgeArticleDetail>(`/knowledge/articles/${articleId}/links/${linkId}`, { method: 'DELETE' });
}

// ─── App navigation registry ────────────────────────────────────

export async function getAppNavApps(): Promise<AppNavApp[]> {
  return request<AppNavApp[]>(`/knowledge/nav/apps`);
}

export async function getAppNavTree(appName: string): Promise<AppNavNode[]> {
  return request<AppNavNode[]>(`/knowledge/nav/tree?appName=${encodeURIComponent(appName)}`);
}

export async function createAppNavNode(input: Partial<AppNavNode> & { appName: string; label: string }): Promise<AppNavNode> {
  return request<AppNavNode>(`/knowledge/nav`, { method: 'POST', body: JSON.stringify(input) });
}

export async function updateAppNavNode(id: string, patch: Partial<AppNavNode>): Promise<AppNavNode> {
  return request<AppNavNode>(`/knowledge/nav/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteAppNavNode(id: string): Promise<void> {
  await request<void>(`/knowledge/nav/${id}`, { method: 'DELETE' });
}

export async function reorderAppNavNodes(ids: string[]): Promise<void> {
  await request<void>(`/knowledge/nav/reorder`, { method: 'POST', body: JSON.stringify({ ids }) });
}

/** Embed all nav nodes for AI retrieval — SSE progress. */
export function prepareAllAppNav(onEvent?: (e: { type: string; message?: string; [k: string]: unknown }) => void): Promise<void> {
  return postSse(`/knowledge/nav/prepare-all`, {}, onEvent);
}

// ─── Discourse Analysis ─────────────────────────────────────────

export async function getDiscourseAnalyses(): Promise<DiscourseTopicAnalysis[]> {
  return request<DiscourseTopicAnalysis[]>('/discourse/analyses');
}

/** Newest analysis per topic, trimmed to what a list row shows (b-01718). */
export async function getDiscourseAnalysisSummaries(): Promise<DiscourseAnalysisSummary[]> {
  return request<DiscourseAnalysisSummary[]>('/discourse/analyses/summary');
}

export async function getDiscourseTopicAnalyses(topicId: number): Promise<DiscourseTopicAnalysis[]> {
  return request<DiscourseTopicAnalysis[]>(`/discourse/analyses/${topicId}`);
}

export async function analyzeDiscourseTopic(
  params: {
    topicId: number;
    provider: string;
    model: string;
    think?: ThinkSetting;
    numCtx?: number | null;
  },
  onProgress?: (message: string) => void,
): Promise<DiscourseTopicAnalysis> {
  const response = await fetch(`${BASE_URL}/discourse/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new ApiError(response.status, e.error || 'Request failed');
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let result: DiscourseTopicAnalysis | null = null;
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'progress' && onProgress) {
            onProgress(event.message);
          } else if (event.type === 'result') {
            result = event.data;
          } else if (event.type === 'error') {
            throw new Error(event.message);
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
        }
      }
    }
  }

  if (!result) throw new Error('No result received from analysis');
  return result;
}

export async function deleteDiscourseAnalysis(id: string): Promise<void> {
  await request<void>(`/discourse/analyses/${id}`, { method: 'DELETE' });
}

export async function deleteDiscourseAnalysesForTopics(topicIds: number[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/discourse/analyses/delete`, {
    method: 'POST',
    body: JSON.stringify({ topicIds }),
  });
}

// ─── Zoho Ticket Analysis (mirrors Discourse analysis) ──────────

export async function getZohoAnalyses(): Promise<ZohoTicketAnalysis[]> {
  return request<ZohoTicketAnalysis[]>('/zoho/analyses');
}

export async function fetchZohoNextSteps(ticketId: string): Promise<ZohoNextStepsResult | null> {
  return request<ZohoNextStepsResult | null>(`/zoho/tickets/${ticketId}/next-steps`);
}

export interface TicketWorkItemDraftResult {
  draft: WorkItemDraft;
  ticketUrl: string | null;
  provider: string;
  model: string;
  confidence?: number;
  calibratedConfidence?: number;
  verification?: ZohoDraftVerification;
  steeringOptions?: ZohoSteeringOption[];
  sources?: DocCitation[];
  examplesUsed?: number;
  usage?: AiUsageInfo;
}

export async function draftTicketWorkItem(ticketId: string, steer?: string): Promise<TicketWorkItemDraftResult> {
  return request(`/zoho/tickets/${ticketId}/workitem/draft`, { method: 'POST', body: JSON.stringify({ steer: steer || '' }) });
}

export async function commitTicketWorkItem(
  ticketId: string,
  fields: Record<string, unknown>,
  opts?: {
    aiSuggestion?: unknown; provider?: string; model?: string; useGlobalConnection?: boolean;
    /** Reorder the new item to the top of the backlog. Server defaults to true. */
    placeAtTop?: boolean;
  },
): Promise<{ workItemId: number; url: string | null; placedAtTop?: boolean }> {
  return request(`/zoho/tickets/${ticketId}/workitem/commit`, { method: 'POST', body: JSON.stringify({ fields, ...opts }) });
}

export async function generateZohoNextSteps(ticketId: string, onProgress?: (message: string) => void): Promise<ZohoNextStepsResult> {
  return streamNextSteps<ZohoNextStepsResult>(`/zoho/tickets/${ticketId}/next-steps`, onProgress);
}

export async function getZohoTicketAnalyses(ticketId: string): Promise<ZohoTicketAnalysis[]> {
  return request<ZohoTicketAnalysis[]>(`/zoho/analyses/${ticketId}`);
}

export async function analyzeZohoTicket(
  params: {
    ticketId: string;
    provider: string;
    model: string;
    think?: ThinkSetting;
    numCtx?: number | null;
  },
  onProgress?: (message: string) => void,
): Promise<ZohoTicketAnalysis> {
  const response = await fetch(`${BASE_URL}/zoho/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new ApiError(response.status, e.error || 'Request failed');
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let result: ZohoTicketAnalysis | null = null;
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'progress' && onProgress) {
            onProgress(event.message);
          } else if (event.type === 'result') {
            result = event.data;
          } else if (event.type === 'error') {
            throw new Error(event.message);
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
        }
      }
    }
  }

  if (!result) throw new Error('No result received from analysis');
  return result;
}

export async function deleteZohoAnalysis(id: string): Promise<void> {
  await request<void>(`/zoho/analyses/${id}`, { method: 'DELETE' });
}

export async function deleteZohoAnalysesForTickets(ticketIds: string[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/zoho/analyses/delete`, {
    method: 'POST',
    body: JSON.stringify({ ticketIds }),
  });
}

// ─── Azure DevOps ────────────────────────────────────────────────

export interface AzureListParams {
  type: 'feature' | 'story-bug';
  states?: string[];
  tags?: string[];
  itemTypes?: string[];
  reviewed?: 'all' | 'reviewed' | 'unreviewed';
  q?: string;
  hasAttachments?: boolean;
  /** People filter: include the signed-in user (created-or-assigned per peopleScope). */
  mine?: boolean;
  /** People filter: match these assignee/creator display names. */
  people?: string[];
  /** Which field(s) the People filter matches against. Defaults to 'either'. */
  peopleScope?: 'either' | 'assigned' | 'created';
  /** People filter: also include items with no assignee. ORs with `mine`/`people`
   *  and ignores `peopleScope` — "no assignee" is not a name to match. (f-01952) */
  unassigned?: boolean;
  /** Board lane: restrict to one ADO board column (System.BoardColumn). */
  boardColumn?: string;
  /** Board lane: restrict to one side of a split column (false=Doing, true=Done). */
  boardColumnDone?: boolean;
  /** Catch-all board lane: items NOT on any of these known columns (null or other). */
  boardColumnOther?: string[];
  /** Children of one parent work item — combine with `itemTypes` to get e.g. just the
   *  User Stories under a Feature. A parent-scoped read returns ALL children, Closed
   *  included (it skips the implicit terminal-state hide). */
  parentId?: number;
  sort?: string;
  page?: number;
  pageSize?: number;
}

export interface AzureListResult {
  items: AzureWorkItemSummary[];
  total: number;
  unreviewed: number;
  page: number;
  pageSize: number;
}

export interface AzureFacets {
  states: string[];
  /** The tag vocabulary, most-used first (ties alphabetical). Plain strings — an
   *  API old enough to predate `tagCounts` answers `{tag, count}` objects here
   *  instead, so read it through `tagFacetsOf`, never directly. */
  tags: (string | AzureTagFacet)[];
  /** The same tags WITH their usage counts. Added beside `tags` rather than
   *  reshaping it, so a cached bundle keeps working across a deploy (b-01795). */
  tagCounts?: AzureTagFacet[];
  types: string[];
  total: number;
}

/** The filter portion of list params, shared with bulk-review. */
export type AzureFilter = Pick<AzureListParams, 'type' | 'states' | 'tags' | 'itemTypes' | 'reviewed' | 'q' | 'hasAttachments' | 'mine' | 'people' | 'peopleScope' | 'unassigned'>;

function azureQueryString(p: AzureListParams): string {
  const qs = new URLSearchParams();
  qs.set('type', p.type);
  p.states?.forEach((s) => qs.append('states', s));
  p.tags?.forEach((t) => qs.append('tags', t));
  p.itemTypes?.forEach((t) => qs.append('itemTypes', t));
  if (p.reviewed && p.reviewed !== 'all') qs.set('reviewed', p.reviewed);
  if (p.q) qs.set('q', p.q);
  if (p.hasAttachments) qs.set('hasAttachments', '1');
  if (p.mine) qs.set('mine', '1');
  p.people?.forEach((n) => qs.append('people', n));
  if (p.peopleScope && p.peopleScope !== 'either' && (p.mine || p.people?.length)) qs.set('peopleScope', p.peopleScope);
  if (p.unassigned) qs.set('unassigned', '1');
  if (p.boardColumn != null) qs.set('boardColumn', p.boardColumn);
  if (p.boardColumnDone != null) qs.set('boardColumnDone', p.boardColumnDone ? '1' : '0');
  // Catch-all lane (present only when ≥1 known column exists, so the param's presence
  // alone signals "other" mode to the server).
  p.boardColumnOther?.forEach((c) => qs.append('boardColumnOther', c));
  if (p.parentId != null) qs.set('parentId', String(p.parentId));
  if (p.sort) qs.set('sort', p.sort);
  qs.set('page', String(p.page ?? 1));
  qs.set('pageSize', String(p.pageSize ?? 50));
  return qs.toString();
}

export async function getAzureWorkItems(params: AzureListParams): Promise<AzureListResult> {
  return request<AzureListResult>(`/backlog/items?${azureQueryString(params)}`);
}

export async function getAzureFacets(type: 'feature' | 'story-bug'): Promise<AzureFacets> {
  return request<AzureFacets>(`/backlog/facets?type=${type}`);
}

export async function getAzureWorkItem(id: string | number): Promise<AzureWorkItemDetail> {
  return request<AzureWorkItemDetail>(`/backlog/items/${id}`);
}

/** Where an item sits in its board lane — the same lane the Kanban board would show
 *  it in, so "3 of 11" matches the eleven cards in that column. Read-only: it counts
 *  ADO's StackRank order, it never sets it. Null when the item carries no rank. */
export interface BacklogLaneRank {
  rank: number;
  total: number;
  lane: { column: string | null; done: boolean; isSplit: boolean; label: string };
}

/** Fetched separately from the detail (unindexed jsonb board predicates), so the
 *  rail can show a loading bar instead of holding up the page. */
export async function getBacklogLaneRank(id: string | number): Promise<BacklogLaneRank | null> {
  return request<BacklogLaneRank | null>(`/backlog/items/${id}/rank`);
}

/** Batch-resolve work-item summaries by id. The backlog Mentions rail resolves
 *  through {@link resolveRelatedItems} instead since b-01823 (it needs topics and
 *  tickets too); this stays the plain work-item-only lookup. Unresolved ids
 *  (deleted / not synced) are simply absent. */
export async function getBacklogItemSummaries(ids: number[]): Promise<AzureWorkItemSummary[]> {
  if (!ids.length) return [];
  return request<AzureWorkItemSummary[]>(`/backlog/items/summaries?ids=${ids.join(',')}`);
}

/** Batch-resolve referenced items across all three PCP-enabled kinds into the rich
 *  rows the RELATED ITEMS / MENTIONS rails render. Feed it the ids a detail page
 *  parsed out of its field text (`parseMentionedRefs`); ids that don't resolve
 *  against the local mirror — a deleted item, or a bare "#123" that was really a PR
 *  number — are simply absent from the result. (b-01823) */
export async function resolveRelatedItems(
  refs: { workItemIds?: number[]; topicIds?: number[]; ticketIds?: string[] },
): Promise<RelatedItem[]> {
  const qs = new URLSearchParams();
  if (refs.workItemIds?.length) qs.set('workItems', refs.workItemIds.join(','));
  if (refs.topicIds?.length) qs.set('topics', refs.topicIds.join(','));
  if (refs.ticketIds?.length) qs.set('tickets', refs.ticketIds.join(','));
  const q = qs.toString();
  if (!q) return [];
  return request<RelatedItem[]>(`/related-items/resolve?${q}`);
}

export async function getAzureSyncStatus(): Promise<{ lastSyncedAt: string | null; itemCount: number; unreviewedCount: number }> {
  return request<{ lastSyncedAt: string | null; itemCount: number; unreviewedCount: number }>('/azure/sync-status');
}

/** Delete a work item in Azure DevOps (recycle bin — recoverable there) and mark it
 *  deleted in PCP. `useGlobalConnection` = per-action consent for the shared ADO
 *  connection (admin-gated). */
export async function deleteBacklogItem(
  id: number,
  opts?: { useGlobalConnection?: boolean },
): Promise<{ id: number; deleted: boolean }> {
  return request(`/backlog/items/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ useGlobalConnection: opts?.useGlobalConnection ?? false }),
  });
}

export async function resyncAzureWorkItem(id: number): Promise<{ deleted: boolean; deletedAt?: string | null }> {
  return request(`/backlog/items/${id}/resync`, { method: 'POST', body: JSON.stringify({}) });
}

/** Change a work item's state in Azure DevOps (saved immediately). Resolves with where
 *  the item now IS: the stored state and the board column / split side Azure DevOps
 *  placed it on — a state write does not choose those. The two board fields are absent
 *  from an API older than b-02000. */
export async function setAzureWorkItemState(id: number, state: string): Promise<{ id: number; state: string | null; boardColumn?: string | null; boardColumnDone?: boolean | null }> {
  return request(`/backlog/items/${id}/state`, { method: 'POST', body: JSON.stringify({ state }) });
}

/** Move a work item on the ADO Kanban board: set the target state (cross-state move)
 *  and/or the column / split-column side (Doing/Done). `team`+`board` are required
 *  whenever a column/done change is sent so the server can resolve the board's
 *  writable WEF fields. Saved immediately to ADO.
 *
 *  Resolves with where the card now IS. The server checks that against the request
 *  and rejects with `ado_board_move_not_landed` when Azure DevOps put the card
 *  somewhere else — see `boardMoveLanding`. (b-01999) */
export async function moveAzureWorkItemOnBoard(id: number, move: AzureBoardMoveRequest): Promise<AzureBoardMoveLanding> {
  return request(`/backlog/items/${id}/board-move`, { method: 'POST', body: JSON.stringify(move) });
}

/** Where a board move left the card, as the board-move route reports it. */
export interface AzureBoardMoveLanding {
  id: number;
  state: string | null;
  boardColumn: string | null;
  boardColumnDone: boolean | null;
}

/** Where the card ended up after a board move that was WRITTEN but did not land
 *  where it was asked to — or null for any other failure (nothing to show). */
export function boardMoveLanding(e: unknown): AzureBoardMoveLanding | null {
  if (!(e instanceof ApiError) || e.code !== ERR_ADO_BOARD_MOVE_NOT_LANDED) return null;
  const d = e.data as Partial<AzureBoardMoveLanding> | null | undefined;
  if (!d || typeof d !== 'object') return null;
  return {
    id: Number(d.id),
    state: d.state ?? null,
    boardColumn: d.boardColumn ?? null,
    boardColumnDone: d.boardColumnDone ?? null,
  };
}

/** Copy a backlog item into a new item of the same type ("Copy of …"), optionally
 *  carrying parent link / children (as Related) / other links / attachments. */
export async function copyBacklogItem(
  id: number,
  opts: {
    includeParent: boolean; includeChildren: boolean; includeOtherLinks: boolean; includeAttachments: boolean;
    /** Default ON, unlike its four siblings: link the copy to the same Feedback
     *  Boards as the item it was copied from. REQUIRED rather than optional, so the
     *  call site has to make the choice instead of silently inheriting one. (f-01966) */
    includeFeedbackBoards: boolean;
  },
): Promise<{ id: number; title: string; linkedFeedbackBoards?: number }> {
  return request(`/backlog/items/${id}/copy`, { method: 'POST', body: JSON.stringify(opts) });
}

/** Add a work-item link from this item to another (relation = friendly label). */
export async function linkBacklogItem(id: number, targetId: number, relation: string): Promise<{ id: number; targetId: number; relation: string }> {
  return request(`/backlog/items/${id}/link`, { method: 'POST', body: JSON.stringify({ targetId, relation }) });
}

/** Remove a work-item link from this item to another (relation = friendly label). */
export async function unlinkBacklogItem(id: number, targetId: number, relation: string): Promise<{ id: number; targetId: number; relation: string }> {
  return request(`/backlog/items/${id}/unlink`, { method: 'POST', body: JSON.stringify({ targetId, relation }) });
}

/** Type-ahead search over synced work items (by title or id), for the link picker. */
export async function searchBacklogItems(q: string): Promise<AzureWorkItemSummary[]> {
  return request(`/backlog/items/search?q=${encodeURIComponent(q)}`);
}

/** The work-item link types (friendly labels) offered in the add-link picker. */
export async function getBacklogRelationTypes(): Promise<{ relations: string[] }> {
  return request('/backlog/relation-types');
}

/** Reorder a backlog item between two visible neighbors (0 = start/end). Persists to ADO.
 *  ADO reorder is parent-scoped: pass `parentId` (the item's parent, 0 = top level) so the
 *  move is scoped to that parent's siblings (the board); omit it for the whole-backlog list. */
export async function reorderWorkItem(id: number, previousId: number, nextId: number, parentId?: number | null): Promise<{ results: Array<{ id: number; order: number }> }> {
  const body: { id: number; previousId: number; nextId: number; parentId?: number } = { id, previousId, nextId };
  if (parentId !== undefined) body.parentId = parentId ?? 0; // null parent → 0 (root level)
  return request(`/backlog/items/reorder`, { method: 'POST', body: JSON.stringify(body) });
}

export async function setAzureWorkItemReviewed(id: number, reviewed: boolean): Promise<{ id: number; reviewed: boolean }> {
  return request<{ id: number; reviewed: boolean }>(`/backlog/items/${id}/reviewed`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

// ─── Work-item next-steps recommender ────────────────────────────

/** Read the persisted suggestion (no AI). Null if none generated yet. */
export async function fetchAzureNextSteps(id: number): Promise<AzureNextStepsResult | null> {
  return request<AzureNextStepsResult | null>(`/backlog/items/${id}/next-steps`);
}

/** Generate a fresh suggestion via AI (and persist it), streaming progress. */
export async function generateAzureNextSteps(id: number, onProgress?: (message: string) => void): Promise<AzureNextStepsResult> {
  return streamNextSteps<AzureNextStepsResult>(`/backlog/items/${id}/next-steps`, onProgress);
}

// ─── Work-item change history + AI summary ───────────────────────

/** Fetch a backlog item's live change history (audit trail) from Azure DevOps. */
export async function getBacklogItemHistory(id: number): Promise<{ entries: AzureWorkItemHistoryEntry[] }> {
  return request<{ entries: AzureWorkItemHistoryEntry[] }>(`/backlog/items/${id}/history`);
}

/** Read the persisted AI history summary (no AI). Null if none generated yet. */
export async function getBacklogHistorySummary(id: number): Promise<AzureWorkItemHistorySummary | null> {
  return request<AzureWorkItemHistorySummary | null>(`/backlog/items/${id}/history/summary`);
}

/** Generate (and persist) the AI history summary, returning the saved record. */
export async function generateBacklogHistorySummary(id: number): Promise<AzureWorkItemHistorySummary> {
  return request<AzureWorkItemHistorySummary>(`/backlog/items/${id}/history/summary`, { method: 'POST' });
}

/** Set a work item's assignee in Azure DevOps (empty string clears it). */
export async function setAzureAssignee(id: number, assignedTo: string): Promise<{ id: number; assignedTo: string | null }> {
  return request(`/backlog/items/${id}/assignee`, { method: 'POST', body: JSON.stringify({ assignedTo }) });
}

/** Post a comment to a work item's discussion thread. */
export async function addAzureComment(id: number, text: string): Promise<{ id: number; author: string | null; createdDate: string | null; text: string }> {
  return request(`/backlog/items/${id}/comment`, { method: 'POST', body: JSON.stringify({ text }) });
}

/** Edit a comment in a work item's discussion thread (author-only; ADO enforces). */
export async function updateAzureComment(id: number, commentId: number, text: string): Promise<{ id: number; author: string | null; createdDate: string | null; text: string }> {
  return request(`/backlog/items/${id}/comment/${commentId}`, { method: 'PATCH', body: JSON.stringify({ text }) });
}

/** Delete a comment from a work item's discussion thread (author-only; ADO enforces). */
export async function deleteAzureComment(id: number, commentId: number): Promise<{ id: number; commentId: number }> {
  return request(`/backlog/items/${id}/comment/${commentId}`, { method: 'DELETE' });
}

/** The just-stored file, addressed through PCP's authenticated ADO attachment proxy.
 *  `url` is what an <img src> should carry: the browser can render it, and the API
 *  turns it back into the real ADO attachment URL when the body is written to ADO. */
export interface UploadedBacklogAttachment { id: string; name: string; url: string }

/** Upload a file attachment to a work item; returns the item's refreshed attachments
 *  plus the file just stored. `fileName` is required for a bare Blob (an image pasted
 *  into a rich-text editor has no name, and the API rejects an extension-less upload).
 *  Mirrors request()'s 412 handling so an unconnected user gets the connect prompt. */
export async function uploadBacklogAttachment(
  id: number,
  file: File | Blob,
  fileName?: string,
): Promise<{ attachments: EntityAttachment[]; uploaded: UploadedBacklogAttachment }> {
  const fd = new FormData();
  fd.append('file', file, fileName || (file as File).name || 'file');
  const res = await fetch(`${BASE_URL}/backlog/items/${id}/attachments`, { method: 'POST', body: fd });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    const apiError = new ApiError(res.status, error.error || 'Attachment upload failed', {
      code: typeof error.code === 'string' ? error.code : undefined,
      provider: typeof error.provider === 'string' ? error.provider : undefined,
      globalFallbackAvailable: error.globalFallbackAvailable === true,
    });
    if (isConnectionRequired(apiError)) notifyConnectionRequired(apiError.provider as ConnectionProvider);
    throw apiError;
  }
  return (await res.json()).data as { attachments: EntityAttachment[]; uploaded: UploadedBacklogAttachment };
}

/** Remove one attachment from a work item in Azure DevOps (f-01863). `removed`
 *  is false when the file was already gone — not an error; `attachments` is the
 *  refreshed list either way. */
export async function deleteBacklogAttachment(
  id: number,
  attachmentId: string,
): Promise<{ removed: boolean; attachments: EntityAttachment[] }> {
  return request<{ removed: boolean; attachments: EntityAttachment[] }>(
    `/backlog/items/${id}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE' },
  );
}

// ─── Recently opened items (per user, across devices — f-01866) ───

/** Record that the signed-in user opened this item's detail page. Best-effort by
 *  contract: the caller must never block a render on it, and the recents list is
 *  a convenience, not state anything depends on. */
export async function recordItemOpened(itemType: RecentItemType, itemId: string | number): Promise<void> {
  await request<{ recorded: boolean }>('/recent-items', {
    method: 'POST',
    body: JSON.stringify({ itemType, itemId: String(itemId) }),
  });
}

/** The user's most recently opened work items, newest first — the link picker's
 *  empty state. `exclude` drops the item the picker was opened from. */
export async function getRecentBacklogItems(limit: number, exclude?: number | null): Promise<AzureWorkItemSummary[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (exclude != null) qs.set('exclude', String(exclude));
  return request<AzureWorkItemSummary[]>(`/backlog/items/recent?${qs.toString()}`);
}

/** The user's most recently opened feedback boards, newest first. */
export async function getRecentFeedbackBoards(limit: number, exclude?: string | null): Promise<FeedbackSummary[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (exclude) qs.set('exclude', exclude);
  return request<FeedbackSummary[]>(`/feedback/recent?${qs.toString()}`);
}

/** Mark reviewed/unreviewed for ALL Azure DevOps work items (no filter). */
export async function setAllAzureWorkItemsReviewed(reviewed = true): Promise<{ updated: number; reviewed: boolean }> {
  return request<{ updated: number; reviewed: boolean }>(`/backlog/items/reviewed-all`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

export async function syncAzureDevOps(
  params: { force?: boolean },
  onEvent: (event: { type: string; message?: string }) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/azure/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';
              for (const part of parts) {
                if (part.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(part.slice(6));
                    if (data.type === 'error') { reject(new Error(data.message)); return; }
                    if (data.type === 'done') { resolve(); return; }
                    onEvent(data);
                  } catch { /* ignore partial frames */ }
                }
              }
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        processChunk();
      })
      .catch(reject);
  });
}

// ─── Release Planning ────────────────────────────────────────────

export async function getReleaseStatus(force = false, cachedOnly = false): Promise<ReleaseStatusItem[]> {
  const qs = new URLSearchParams();
  if (force) qs.set('force', 'true');
  if (cachedOnly) qs.set('cachedOnly', 'true');
  const s = qs.toString();
  return request<ReleaseStatusItem[]>(`/releases/status${s ? `?${s}` : ''}`);
}

export async function getReleaseSessions(): Promise<ReleaseSession[]> {
  return request<ReleaseSession[]>('/releases/sessions');
}

export async function getReleaseSession(id: string): Promise<ReleaseSessionDetail> {
  return request<ReleaseSessionDetail>(`/releases/sessions/${id}`);
}

/** Period-scope preview (local commits only — never triggers a GitHub sync). */
export async function getReleaseScope(
  from?: string,
  to?: string,
): Promise<Array<{ app: string; commitCount: number; latestCommitAt: string | null }>> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const s = qs.toString();
  // The endpoint wraps the apps array alongside the resolved window:
  // { from, to, apps }. Unwrap to the apps list the dialog consumes.
  const res = await request<{
    from: string;
    to: string;
    apps: Array<{ app: string; commitCount: number; latestCommitAt: string | null }>;
  }>(`/releases/scope${s ? `?${s}` : ''}`);
  return res.apps;
}

/** Draft a release name via the configured AI feature (period/scope-aware). */
export async function draftReleaseName(params: {
  scopeMode: string;
  periodStart?: string;
  periodEnd?: string;
  apps?: string[];
}): Promise<string> {
  const data = await request<{ name: string }>(`/releases/draft-name`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return data.name;
}

// ─── Upcoming release (queued for prod) ──────────────────────────

/** What production has not seen yet. Local reads server-side — safe on page load. */
export async function getUpcomingRelease(): Promise<import('@pcp/shared').UpcomingRelease> {
  return request('/releases/upcoming');
}

// ─── Release Tracking ────────────────────────────────────────────

export async function getTrackedReleases(limit?: number): Promise<import('@pcp/shared').TrackedRelease[]> {
  return request(`/releases/tracking${limit ? `?limit=${limit}` : ''}`);
}

export async function getTrackedRelease(id: string): Promise<import('@pcp/shared').TrackedReleaseDetail> {
  return request(`/releases/tracking/${id}`);
}

export async function getReleaseTrackingMetrics(days = 30): Promise<import('@pcp/shared').ReleaseTrackingMetrics> {
  return request(`/releases/tracking/metrics?days=${days}`);
}

export async function completeTrackedRelease(id: string): Promise<{ id: string }> {
  return request(`/releases/tracking/${id}/complete`, { method: 'POST', body: '{}' });
}

export async function reopenTrackedRelease(id: string): Promise<{ id: string }> {
  return request(`/releases/tracking/${id}/reopen`, { method: 'POST', body: '{}' });
}

export async function patchTrackedRelease(id: string, body: { title?: string; notes?: string; type?: string }): Promise<import('@pcp/shared').TrackedRelease> {
  return request(`/releases/tracking/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteTrackedRelease(id: string): Promise<{ id: string }> {
  return request(`/releases/tracking/${id}`, { method: 'DELETE' });
}

export async function generateReleaseAiNotes(id: string): Promise<{ aiSummary: string; usage?: AiUsageInfo }> {
  return request(`/releases/tracking/${id}/ai-notes`, { method: 'POST', body: '{}' });
}

export async function createManualRelease(body: { title?: string; apps: string[] }): Promise<{ id: string }> {
  return request(`/releases/tracking`, { method: 'POST', body: JSON.stringify(body) });
}

/** On-demand related items (uses an embedding call) — returns items + usage (persisted server-side). */
export async function findReleaseRelated(id: string): Promise<{ items: import('@pcp/shared').TrackedReleaseRelatedItem[]; usage: AiUsageInfo }> {
  return request(`/releases/tracking/${id}/related`, { method: 'POST', body: '{}' });
}

/** Drop auto-detected releases and rebuild them from the prod-manifest history. */
export async function rebuildReleaseTracking(): Promise<{ reconciled: number }> {
  return request(`/releases/tracking/rebuild`, { method: 'POST', body: '{}' });
}

export interface ReleasePrepareEvent {
  type: 'progress' | 'app_start' | 'thinking' | 'content' | 'app_done' | 'error' | 'done' | 'section_start' | 'section_done' | 'connected' | 'status';
  message?: string;
  app?: string;
  section?: string;
  content?: string;
  index?: number;
  total?: number;
  reportId?: string;
  sessionId?: string;
  status?: string;
}

export async function prepareRelease(
  params: {
    name?: string;
    scopeMode?: string;
    periodStart?: string | null;
    periodEnd?: string | null;
    selectedApps?: string[];
    provider?: string;
    model?: string;
    think?: string | boolean | null;
    numCtx?: number | null;
  },
  onEvent?: (event: ReleasePrepareEvent) => void,
): Promise<{ sessionId: string }> {
  const res = await fetch(`${BASE_URL}/releases/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new ApiError(res.status, e.error || 'Request failed');
  }
  if (!res.body) throw new Error('No response body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sessionId = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      try {
        const event: ReleasePrepareEvent = JSON.parse(trimmed.slice(5).trim());
        if (event.type === 'done' && event.sessionId) sessionId = event.sessionId;
        onEvent?.(event);
      } catch { /* skip malformed */ }
    }
  }
  return { sessionId };
}

// ─── Durable release preparation (Temporal workflow path) ───────
//
// The durable path replaces the legacy in-request SSE pipeline above for the
// UI: createReleaseSessionDraft() inserts the session ROW only (fast JSON),
// then startReleasePrepare() runs the checkpointed `releasePrepare` workflow.
// Progress lands on a 'release' sync_runs row (the Activity panel shows it;
// stop via stopSyncRun(runId)) and the session detail fills in section by
// section — poll GET /releases/sessions/:id while status === 'preparing'.
// Re-calling startReleasePrepare() for a failed/stopped session RESUMES at
// the first incomplete step; finished work is never redone.

export interface ReleasePrepareStart {
  started: boolean;
  alreadyRunning?: boolean;
  sessionId: string;
  /** sync_runs row id — drives the Activity panel and is the Stop handle. */
  runId: string | null;
  workflowId: string;
  temporalRunId: string | null;
}

/** Run ids of preparations started in THIS browser session, keyed by release
 *  session id — lets the detail page wire Stop immediately. After a reload the
 *  page falls back to matching the running 'release' sync run. */
const releasePrepareRunIds = new Map<string, string>();

export function getReleasePrepareRunId(sessionId: string): string | undefined {
  return releasePrepareRunIds.get(sessionId);
}

/** Create the release session ROW only (no AI pipeline, no SSE). */
export async function createReleaseSessionDraft(params: {
  name?: string;
  scopeMode?: 'out-of-sync' | 'custom-period';
  periodStart?: string | null;
  periodEnd?: string | null;
  provider?: string;
  model?: string;
  think?: string | boolean | null;
  numCtx?: number | null;
}): Promise<ReleaseSession> {
  return request<ReleaseSession>('/releases/sessions/draft', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/** Start (or attach to) the durable releasePrepare workflow for a session.
 *  Idempotent: when a preparation is already running it returns that run's
 *  info with `alreadyRunning: true` instead of erroring. */
export async function startReleasePrepare(id: string, selectedApps?: string[]): Promise<ReleasePrepareStart> {
  const data = await request<ReleasePrepareStart>(`/releases/${id}/prepare`, {
    method: 'POST',
    body: JSON.stringify(selectedApps && selectedApps.length > 0 ? { selectedApps } : {}),
  });
  if (data.runId) releasePrepareRunIds.set(id, data.runId);
  return data;
}

/** Pause the running preparation (between-step gate). 409 = no live workflow. */
export async function pauseReleasePrepare(id: string): Promise<void> {
  await request(`/releases/${id}/prepare/pause`, { method: 'POST', body: '{}' });
}

/** Resume a paused preparation. 409 = no live workflow. */
export async function resumeReleasePrepare(id: string): Promise<void> {
  await request(`/releases/${id}/prepare/resume`, { method: 'POST', body: '{}' });
}

export async function updateReleaseSummary(sessionId: string, markdown: string): Promise<void> {
  await request(`/releases/sessions/${sessionId}/summary`, {
    method: 'PUT',
    body: JSON.stringify({ markdown }),
  });
}

export async function updateReleaseNotes(sessionId: string, lang: 'en' | 'dk', markdown: string): Promise<void> {
  await request(`/releases/sessions/${sessionId}/notes/${lang}`, {
    method: 'PUT',
    body: JSON.stringify({ markdown }),
  });
}

export async function updateReleaseReport(sessionId: string, reportId: string, markdown: string): Promise<void> {
  await request(`/releases/sessions/${sessionId}/reports/${reportId}`, {
    method: 'PUT',
    body: JSON.stringify({ markdown }),
  });
}

export async function deleteReleaseSession(id: string): Promise<void> {
  await request(`/releases/sessions/${id}`, { method: 'DELETE' });
}

// ─── Release Progress SSE ───────────────────────────────────────

export function subscribeReleaseProgress(
  sessionId: string,
  onEvent: (event: ReleasePrepareEvent & { type: string }) => void,
  onError?: (error: Event) => void,
  onClose?: () => void,
): () => void {
  const url = `${BASE_URL}/releases/sessions/${sessionId}/progress`;
  const es = new EventSource(url);

  es.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data);
      // If the server says the session is already done, close
      if (data.type === 'status' && data.status !== 'preparing') {
        onEvent(data);
        es.close();
        onClose?.();
        return;
      }
      if (data.type === 'done') {
        onEvent(data);
        es.close();
        onClose?.();
        return;
      }
      onEvent(data);
    } catch { /* skip malformed */ }
  };

  es.onerror = (ev) => {
    onError?.(ev);
    es.close();
    onClose?.();
  };

  // Return unsubscribe function
  return () => {
    es.close();
  };
}

// ─── Commit Tracker ──────────────────────────────────────────────

export async function getCommits(): Promise<Commit[]> {
  return request<Commit[]>('/commits');
}

/** Recompute the CI badge state (ciState/ciRuns) for the latest commit per repo
 *  from GitHub's check-runs rollup. Used by the /releases "GitHub Refresh" button
 *  so the CI badges update alongside the release snapshots (they read separate
 *  sources). Lightweight — no new-commit fetch. */
export async function refreshCommitCi(): Promise<{ updated: number }> {
  return request<{ updated: number }>('/commits/refresh-ci', { method: 'POST' });
}

/** Build-time trend for a commit's repo (recent commits, oldest→newest). */
export async function getCommitCiTrend(id: string, count = 25): Promise<CiTrendPoint[]> {
  return request<CiTrendPoint[]>(`/commits/${id}/ci-trend?count=${count}`);
}

/** Open PRs targeting this commit's repo + each head's CI state (live GitHub read). */
export async function getCommitOpenPrs(id: string): Promise<RepoOpenPr[]> {
  return request<RepoOpenPr[]>(`/commits/${id}/open-prs`);
}

/** Build-time trend for a repo (recent commits, oldest→newest) — repo-keyed. */
export async function getRepoCiTrend(repo: string, count = 25): Promise<CiTrendPoint[]> {
  return request<CiTrendPoint[]>(`/commits/repo/${encodeURIComponent(repo)}/ci-trend?count=${count}`);
}

/** Open PRs targeting a repo + each head's CI state (live GitHub read) — repo-keyed. */
export async function getRepoOpenPrs(repo: string): Promise<RepoOpenPr[]> {
  return request<RepoOpenPr[]>(`/commits/repo/${encodeURIComponent(repo)}/open-prs`);
}

export async function getCommit(id: string): Promise<Commit> {
  return request<Commit>(`/commits/${id}`);
}

export async function getCommitDiff(id: string): Promise<string> {
  return request<string>(`/commits/${id}/diff`);
}

export async function setCommitReviewed(id: string, reviewed: boolean): Promise<{ id: string; reviewed: boolean }> {
  return request<{ id: string; reviewed: boolean }>(`/commits/${id}/reviewed`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

export async function setTopicReviewed(id: number, reviewed: boolean): Promise<{ id: number; reviewed: boolean }> {
  return request<{ id: number; reviewed: boolean }>(`/discourse/topics/${id}/reviewed`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

export async function setAllCommitsReviewed(reviewed = true): Promise<{ updated: number; reviewed: boolean }> {
  return request<{ updated: number; reviewed: boolean }>(`/commits/reviewed-all`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

export async function setAllTopicsReviewed(reviewed = true): Promise<{ updated: number; reviewed: boolean }> {
  return request<{ updated: number; reviewed: boolean }>(`/discourse/topics/reviewed-all`, {
    method: 'POST',
    body: JSON.stringify({ reviewed }),
  });
}

// ─── Discourse Topic Actions (reply / tags / complete) ───────────

/** AI-draft a reply. Writes nothing to Discourse — confirm with postTopicReply. */
export async function draftTopicReply(
  topicId: number,
  opts?: AiOverride & { instructions?: string },
): Promise<DiscourseReplyDraft> {
  return request<DiscourseReplyDraft>(`/discourse/topics/${topicId}/reply/draft`, {
    method: 'POST',
    body: JSON.stringify(opts ?? {}),
  });
}

/** Search Discourse usernames for @-mention autocomplete. */
export async function searchDiscourseUsers(term: string): Promise<Array<{ username: string; name: string | null }>> {
  return request(`/discourse/users?q=${encodeURIComponent(term)}`);
}

/** The AI suggestion captured alongside a committed action (for the learning loop). */
export interface ReplyCapture {
  aiDraft?: string;
  reasoning?: string;
  confidence?: number;
  provider?: string;
  model?: string;
}

/** Post a (user-confirmed) reply to the topic. Pass the original AI draft to capture the edit. */
export async function postTopicReply(
  topicId: number,
  raw: string,
  capture?: ReplyCapture,
  markSolution = false,
): Promise<DiscourseReplyResult> {
  return request<DiscourseReplyResult>(`/discourse/topics/${topicId}/reply/post`, {
    method: 'POST',
    body: JSON.stringify({ raw, ...capture, markSolution }),
  });
}

export async function createDiscourseTopic(input: DiscourseTopicCreateInput): Promise<DiscourseTopicCreateResult> {
  return request<DiscourseTopicCreateResult>('/discourse/topics', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function editDiscoursePost(
  topicId: number,
  postId: number,
  raw: string,
  editReason?: string,
): Promise<DiscoursePostEditResult> {
  return request<DiscoursePostEditResult>(`/discourse/topics/${topicId}/posts/${postId}`, {
    method: 'PUT',
    body: JSON.stringify({ raw, editReason }),
  });
}

/** Accept a specific post as the topic's solution (marks it solved in Discourse). */
export async function markStillRelevant(topicId: number): Promise<{ id: number; postNumber: number; topicId: number; weeks: number; timerSet: boolean; timerError?: string }> {
  return request(`/discourse/topics/${topicId}/still-relevant`, { method: 'POST', body: JSON.stringify({}) });
}

/** Post a "deemed stale" Danish reply and close the topic immediately. */
export async function closeStaleTopic(topicId: number): Promise<{ id: number; postNumber: number; topicId: number; closed: boolean; closeError?: string }> {
  return request(`/discourse/topics/${topicId}/close-stale`, { method: 'POST', body: JSON.stringify({}) });
}

export async function acceptSolution(topicId: number, postId: number): Promise<{ topicId: number; postId: number; solved: boolean }> {
  return request(`/discourse/topics/${topicId}/solution/accept`, {
    method: 'POST',
    body: JSON.stringify({ postId }),
  });
}

/** Unmark the topic's accepted solution. */
export async function unacceptSolution(topicId: number): Promise<{ topicId: number; solved: boolean }> {
  return request(`/discourse/topics/${topicId}/solution/unaccept`, { method: 'POST' });
}

/** The forum's allowed tag vocabulary (cached server-side). */
export async function getDiscourseTags(force = false): Promise<string[]> {
  return request<string[]>(`/discourse/tags${force ? '?force=true' : ''}`);
}

/** Tags allowed in a specific topic's category (not the whole forum). */
export async function getAvailableTopicTags(
  topicId: number,
): Promise<{ allowed: string[]; current: string[]; categoryId: number | null }> {
  return request(`/discourse/topics/${topicId}/available-tags`);
}

/** AI tag suggestions, constrained to the forum vocabulary. Writes nothing. */
export async function suggestTopicTags(topicId: number, opts?: AiOverride): Promise<DiscourseTagSuggestion> {
  return request<DiscourseTagSuggestion>(`/discourse/topics/${topicId}/tags/suggest`, {
    method: 'POST',
    body: JSON.stringify(opts ?? {}),
  });
}

/** Apply the topic's full tag set (writes to Discourse). Pass aiSuggested to capture the edit. */
export async function setTopicTags(
  topicId: number,
  tags: string[],
  capture?: { aiSuggested?: string[]; provider?: string; model?: string },
): Promise<{ topicId: number; tags: string[] }> {
  return request<{ topicId: number; tags: string[] }>(`/discourse/topics/${topicId}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags, ...capture }),
  });
}

/** Recorded actions for a topic (newest first). */
export async function getTopicActions(topicId: number): Promise<DiscourseAction[]> {
  return request<DiscourseAction[]>(`/discourse/topics/${topicId}/actions`);
}

// ─── Learning loop: playbook & embeddings ────────────────────────

export async function getPlaybook(): Promise<{ content: string }> {
  return request<{ content: string }>(`/discourse/playbook`);
}

export async function savePlaybook(content: string): Promise<{ content: string }> {
  return request<{ content: string }>(`/discourse/playbook`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function distillPlaybook(): Promise<{ content: string; examplesUsed: number }> {
  return request<{ content: string; examplesUsed: number }>(`/discourse/playbook/distill`, {
    method: 'POST',
  });
}

export async function backfillActionEmbeddings(
  onEvent?: (e: { type: string; message?: string }) => void,
): Promise<{ embedded: number; skipped: number; failed: number; model: string }> {
  return postSseResult('/discourse/actions/embed-backfill', {}, onEvent);
}

/** Seed the learning corpus from existing topic history (reply/tag examples + optional playbook). */
export async function importTopicHistory(
  bootstrapPlaybook = true,
  onEvent?: (e: { type: string; message?: string }) => void,
): Promise<{ topicsScanned: number; replyExamples: number; tagExamples: number; playbookUpdated: boolean }> {
  return postSseResult('/discourse/import-history', { bootstrapPlaybook }, onEvent);
}

/** Embed Azure DevOps work items so semantic duplicate detection can use them. */
export async function backfillWorkItemEmbeddings(): Promise<{ embedded: number; skipped: number; failed: number; model: string }> {
  return request<{ embedded: number; skipped: number; failed: number; model: string }>(
    `/azure/work-items/embed-backfill`,
    { method: 'POST' },
  );
}


export async function getCommitAnalyses(commitId?: string): Promise<CommitAnalysis[]> {
  const query = commitId ? `?commitId=${encodeURIComponent(commitId)}` : '';
  return request<CommitAnalysis[]>(`/commits/analyses${query}`);
}

export async function getCommitAnalysis(id: string): Promise<CommitAnalysisDetail> {
  return request<CommitAnalysisDetail>(`/commits/analyses/${id}`);
}

export async function deleteCommitAnalysis(id: string): Promise<void> {
  await request<void>(`/commits/analyses/${id}`, { method: 'DELETE' });
}

export async function deleteAnalysesForCommits(commitIds: string[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/commits/analyses/delete`, {
    method: 'POST',
    body: JSON.stringify({ commitIds }),
  });
}

export async function syncCommits(onEvent: (event: { type: string; message?: string }) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}/commits/sync`, { method: 'POST' })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';

              for (const part of parts) {
                if (part.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(part.slice(6));
                    if (data.type === 'error') {
                      reject(new Error(data.message));
                      return;
                    }
                    if (data.type === 'done') {
                      resolve();
                      return;
                    }
                    onEvent(data);
                  } catch (e) {
                    // Ignore parse errors on partial streams
                  }
                }
              }
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        processChunk();
      })
      .catch(reject);
  });
}

export interface AnalyzeCommitsParams {
  commitIds: string[];
  provider: string;
  model: string;
  think?: ThinkSetting;
  numCtx?: number | null;
}

export async function analyzeCommits(
  params: AnalyzeCommitsParams,
  onEvent: (event: { type: string; message?: string; text?: string; analysisId?: string }) => void
): Promise<{ analysisId: string }> {
  return new Promise((resolve, reject) => {
    let finalId = '';
    fetch(`${BASE_URL}/commits/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() || '';

              for (const part of parts) {
                if (part.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(part.slice(6));
                    if (data.type === 'error') {
                      reject(new Error(data.message));
                      return;
                    }
                    if (data.type === 'done') {
                      if (data.analysisId) finalId = data.analysisId;
                      resolve({ analysisId: finalId });
                      return;
                    }
                    onEvent(data);
                  } catch (e) {
                    // Ignore parse errors
                  }
                }
              }
            }
            resolve({ analysisId: finalId });
          } catch (err) {
            reject(err);
          }
        };

        processChunk();
      })
      .catch(reject);
  });
}

// ─── Entitlements & Release Trains ───────────────────────────────

import type {
  Entitlement, EntitlementDetail, EntitlementSuggestion, ReleaseTrain,
  EntitlementGroup, EntitlementGroupTree, EntitlementGenerationLens,
} from '@pcp/shared';

export interface EntitlementHierarchy {
  tree: EntitlementGroupTree[];
  ungrouped: Entitlement[];
  /** Optional top level: Applications, each owning a set of domain ids. */
  applications?: { application: EntitlementGroup; domainIds: string[] }[];
}

export async function getEntitlementGroups(): Promise<EntitlementHierarchy> {
  return request('/entitlement-groups');
}

export async function createEntitlementGroup(body: Record<string, unknown>): Promise<EntitlementGroup> {
  return request('/entitlement-groups', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateEntitlementGroup(id: string, body: Record<string, unknown>): Promise<EntitlementGroup> {
  return request(`/entitlement-groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteEntitlementGroup(id: string): Promise<{ id: string }> {
  return request(`/entitlement-groups/${id}`, { method: 'DELETE' });
}

/** Bulk-apply a status patch to every entitlement under a Domain/Capability.
 *  Only the keys present in `body` are written; returns the affected count. */
export async function applyEntitlementGroupStatus(
  id: string,
  body: { exposure?: string; stage?: string; noteVisibility?: string; customerAvailableFrom?: string | null },
): Promise<{ updated: number }> {
  return request(`/entitlement-groups/${id}/apply-status`, { method: 'POST', body: JSON.stringify(body) });
}

export interface EntitlementFilters {
  exposure?: string;
  stage?: string;
  area?: string;
  groupId?: string;
  trainId?: string;
  includeArchived?: boolean;
}

export async function getEntitlements(filters: EntitlementFilters = {}): Promise<Entitlement[]> {
  const p = new URLSearchParams();
  if (filters.exposure) p.set('exposure', filters.exposure);
  if (filters.stage) p.set('stage', filters.stage);
  if (filters.area) p.set('area', filters.area);
  if (filters.trainId) p.set('trainId', filters.trainId);
  if (filters.groupId) p.set('groupId', filters.groupId);
  if (filters.includeArchived) p.set('includeArchived', '1');
  const qs = p.toString();
  return request(`/entitlements${qs ? `?${qs}` : ''}`);
}

export async function getEntitlement(id: string): Promise<EntitlementDetail> {
  return request(`/entitlements/${id}`);
}

export async function createEntitlement(body: Record<string, unknown>): Promise<EntitlementDetail> {
  return request('/entitlements', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateEntitlement(id: string, body: Record<string, unknown>): Promise<EntitlementDetail> {
  return request(`/entitlements/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteEntitlement(id: string): Promise<{ id: string }> {
  return request(`/entitlements/${id}`, { method: 'DELETE' });
}

export async function addEntitlementLink(id: string, body: Record<string, unknown>): Promise<EntitlementDetail> {
  return request(`/entitlements/${id}/links`, { method: 'POST', body: JSON.stringify(body) });
}

export async function removeEntitlementLink(id: string, linkId: string): Promise<EntitlementDetail> {
  return request(`/entitlements/${id}/links/${linkId}`, { method: 'DELETE' });
}

export async function getEntitlementSuggestions(status = 'pending'): Promise<EntitlementSuggestion[]> {
  return request(`/entitlements/suggestions${status ? `?status=${status}` : ''}`);
}

export async function acceptEntitlementSuggestion(id: string, draft?: Record<string, unknown>): Promise<EntitlementDetail | null> {
  return request(`/entitlements/suggestions/${id}/accept`, { method: 'POST', body: JSON.stringify({ draft }) });
}

export async function rejectEntitlementSuggestion(id: string): Promise<EntitlementSuggestion> {
  return request(`/entitlements/suggestions/${id}/reject`, { method: 'POST', body: '{}' });
}

export async function refreshEntitlementSuggestions(): Promise<{ scanned: number; pruned: number; rescored: number; remaining: number }> {
  return request('/entitlements/suggestions/refresh', { method: 'POST', body: '{}' });
}

export async function generateEntitlements(
  lens: EntitlementGenerationLens,
  opts: { prompt?: string; text?: string; domainId?: string } = {},
  onProgress?: (message: string) => void,
): Promise<{ created: number; scanned: number; deduped: number; skipped?: boolean; reason?: string }> {
  // SSE-streamed + tracked as a sync run (shows in the floating Sync log).
  return streamNextSteps('/entitlements/generate', onProgress, { lens, ...opts });
}

export async function getReleaseTrains(): Promise<ReleaseTrain[]> {
  return request('/release-trains');
}

export async function getCurrentReleaseTrain(): Promise<ReleaseTrain> {
  return request('/release-trains/current');
}

export async function createReleaseTrain(body: Record<string, unknown>): Promise<ReleaseTrain> {
  return request('/release-trains', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateReleaseTrain(id: string, body: Record<string, unknown>): Promise<ReleaseTrain> {
  return request(`/release-trains/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function releaseReleaseTrain(id: string): Promise<{ released: ReleaseTrain; next: ReleaseTrain }> {
  return request(`/release-trains/${id}/release`, { method: 'POST', body: '{}' });
}

export async function deleteReleaseTrain(id: string): Promise<{ id: string }> {
  return request(`/release-trains/${id}`, { method: 'DELETE' });
}

export async function generateTrainReleaseNotes(id: string, lang: 'en' | 'dk'): Promise<ReleaseTrain> {
  return request(`/release-trains/${id}/notes/${lang}`, { method: 'POST', body: '{}' });
}

export async function getReleaseTrainScope(id: string): Promise<import('@pcp/shared').EntitlementScope> {
  return request(`/release-trains/${id}/scope`);
}

// ─── CoCo (Core Configuration) ──────────────────────────────────

export interface CocoConfigStatus {
  repo: { exists: boolean; head: string | null; lastSyncAt: string | null; fileCount: number };
  embeddingModel: string;
  total: number;
  present: number;
  embedded: number;
  pending: number;
  byKind: Array<{ kind: string; n: number }>;
  byScope: Array<{ scope: string; n: number }>;
  links: { total: number; mention: number; semantic: number; entitiesLinked: number };
  cocoConfigSource: boolean;
  autoEmbed: boolean;
  autoLink: boolean;
}
export interface CocoConfigItem {
  id: string; namespace: string; kind: string; scope: string;
  displayName: string | null; description: string | null; relativePath: string; embedded: boolean;
  linkCount?: number;
  /** Customer-visibility for the Support assistant's RAG. */
  audience?: KnowledgeAudience;
}
export interface CocoConfigDetail extends CocoConfigItem {
  language: string | null; rawJson: unknown; embedText: string | null; refs: string[];
  gitSha: string | null; presentInRepo: boolean;
  references: Array<{ id: string; namespace: string; kind: string; scope: string; displayName: string | null }>;
  referencedBy: Array<{ id: string; namespace: string; kind: string; scope: string; displayName: string | null }>;
  relatedEntities: CocoRelatedEntity[];
}
export interface CocoRelatedConfig { id: string; namespace: string; method: 'mention' | 'semantic'; similarity: number | null; kind: string; scope: string; displayName: string | null; }
export interface CocoRelatedEntity { entityType: 'work-item' | 'zoho' | 'commit' | 'discourse'; entityId: string; method: 'mention' | 'semantic'; similarity: number | null; title: string; }
export interface CocoVariantMember { id: string; scope: string; relativePath: string; displayName: string | null; kind: string; namespace: string; }
export interface CocoVariantCluster { reason: 'namespace' | 'identical' | 'semantic'; key: string; kind?: string; similarity?: number; members: CocoVariantMember[]; }

export async function getCocoConfigStatus(): Promise<CocoConfigStatus> {
  return request('/coco-config/status');
}

export async function listCocoConfig(params: { q?: string; kind?: string; scope?: string; linked?: boolean; limit?: number; offset?: number } = {}):
  Promise<{ items: CocoConfigItem[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.kind) qs.set('kind', params.kind);
  if (params.scope) qs.set('scope', params.scope);
  if (params.linked) { qs.set('linked', 'true'); qs.set('sort', 'links'); }
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const s = qs.toString();
  return request(`/coco-config${s ? `?${s}` : ''}`);
}

export async function getCocoConfigDetail(id: string): Promise<CocoConfigDetail> {
  return request(`/coco-config/${id}`);
}

export async function getCocoStructuralVariants(): Promise<{ namespaceClusters: CocoVariantCluster[]; identicalClusters: CocoVariantCluster[] }> {
  return request('/coco-config/variants/structural');
}

export async function getCocoSemanticVariants(kind: string, threshold = 0.9): Promise<CocoVariantCluster[]> {
  return request(`/coco-config/variants/semantic?kind=${encodeURIComponent(kind)}&threshold=${threshold}`);
}

/** Stream an SSE endpoint, resolving on `done`/stream-end, rejecting on `error`. */
function streamSse(path: string, init: RequestInit, onEvent: (e: { type: string; message?: string; [k: string]: unknown }) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(`${BASE_URL}${path}`, init)
      .then(async (res) => {
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new ApiError(res.status, e.error || 'Request failed');
        }
        if (!res.body) throw new Error('No readable stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const pump = async (): Promise<void> => {
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';
            for (const part of parts) {
              if (!part.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(part.slice(6));
                if (data.type === 'error') { reject(new Error(String(data.message ?? 'error'))); return; }
                onEvent(data);
                if (data.type === 'done' || data.type === 'stopped') { resolve(); return; }
              } catch { /* ignore partial */ }
            }
          }
          resolve();
        };
        return pump();
      })
      .catch(reject);
  });
}

/** Trigger the CoCo repo mirror sync (git pull + parse/upsert — AI-free). */
export async function syncRepos(onEvent: (e: { type: string; message?: string }) => void): Promise<void> {
  return streamSse('/repos/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }, onEvent);
}

/** Manually embed CoCo config (calls OpenAI). `reset` re-embeds everything. */
export async function embedCocoConfig(reset: boolean, onEvent: (e: { type: string; message?: string }) => void): Promise<void> {
  return streamSse('/coco-config/embed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reset }) }, onEvent);
}

/** Rebuild config↔entity prediction links (AI-free). `missing` only links
 *  entities with no semantic link yet (resumable, additive); `all` is a full refresh. */
export async function rebuildCocoLinks(mode: 'all' | 'missing', onEvent: (e: { type: string; message?: string }) => void): Promise<void> {
  return streamSse('/coco-config/links/rebuild', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode }) }, onEvent);
}

/** Forward lookup: the CoCo config a ticket/work-item/commit/topic relates to. */
export async function getRelatedCocoConfig(entityType: string, entityId: string): Promise<CocoRelatedConfig[]> {
  return request(`/coco-config/related?type=${encodeURIComponent(entityType)}&id=${encodeURIComponent(entityId)}`);
}

// ─── Auth (Entra enroll + WebAuthn passkey) ─────────────────────

export type AuthStatus =
  | 'disabled'
  | 'authenticated'
  | 'needs_enroll'
  | 'needs_unlock'
  | 'needs_entra';

export interface AuthUser {
  id: string;
  email: string | null;
  displayName: string | null;
  /** Effective permission keys (may include the '*' wildcard for Administrators).
   *  Reduced while the user is impersonating role(s) via "use the system as". */
  permissions?: string[];
  /** Role names this user holds. */
  roles?: string[];
  /** True when the user's REAL perms include '*' (i.e. may self-impersonate). */
  canImpersonate?: boolean;
  /** Active "use the system as" override — role ids, [] when not impersonating. */
  viewAsRoleIds?: string[];
}

export interface AuthStateResponse {
  status: AuthStatus;
  user?: AuthUser | null;
  // Device-aware session lifetime (server-decided from the user-agent). The
  // client uses this for the idle/cold-start lock checks so it matches exactly.
  lockAfterSeconds?: number;
}

export interface UnlockResult {
  user: AuthUser | null;
  lockAfterSeconds?: number;
}

/** Current auth state — the gate's source of truth. Always resolves (200). */
export async function getAuthState(): Promise<AuthStateResponse> {
  return request<AuthStateResponse>('/auth/state');
}

/** Full-page URL to start the Microsoft Entra sign-in (server redirects to MS). */
export function entraStartUrl(): string {
  return `${BASE_URL}/auth/entra/start`;
}

/** Bind a passkey to this device after Entra sign-in. Requires a user gesture. */
export async function enrollPasskey(): Promise<UnlockResult> {
  const optionsJSON = await request<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/webauthn/register/start',
    { method: 'POST' },
  );
  const response = await startRegistration({ optionsJSON });
  return request<UnlockResult>('/auth/webauthn/register/verify', {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
}

/** Biometric unlock via the device's passkey. Requires a user gesture. */
export async function unlockWithPasskey(): Promise<UnlockResult> {
  const optionsJSON = await request<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/webauthn/authenticate/start',
    { method: 'POST' },
  );
  const response = await startAuthentication({ optionsJSON });
  return request<UnlockResult>('/auth/webauthn/authenticate/verify', {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
}

/** Finish setting up THIS device when its authenticator already holds a passkey
 *  for the account (enrollPasskey() would throw InvalidStateError). Authenticate
 *  with the existing passkey — authorized by the post-Entra enroll ticket — to
 *  trust the device + open a session. Requires a user gesture. */
export async function enrollAuthenticateWithPasskey(): Promise<UnlockResult> {
  const optionsJSON = await request<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/webauthn/enroll/authenticate/start',
    { method: 'POST' },
  );
  const response = await startAuthentication({ optionsJSON });
  return request<UnlockResult>('/auth/webauthn/enroll/authenticate/verify', {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
}

export interface QrLoginStart {
  /** Public id shown in the QR (and the approve deep link). */
  requestId: string;
  /** Private secret this device keeps — never in the QR; proves it's the initiator. */
  secret: string;
  /** Deep link the QR encodes, so an authed phone's native camera can also approve. */
  approveUrl: string;
  expiresInSeconds: number;
}

export type QrLoginPoll =
  | { status: 'pending' }
  | { status: 'expired' }
  | ({ status: 'approved' } & UnlockResult);

/** Start a device-login request on THIS (new, unauthenticated) device. Returns
 *  the requestId to render as a QR and the private secret to poll with. */
export async function startQrLogin(): Promise<QrLoginStart> {
  return request<QrLoginStart>('/auth/qr/start', { method: 'POST' });
}

/** Poll for approval; on 'approved' the session cookies are set on this device. */
export async function pollQrLogin(requestId: string, secret: string): Promise<QrLoginPoll> {
  return request<QrLoginPoll>('/auth/qr/poll', { method: 'POST', body: JSON.stringify({ requestId, secret }) });
}

/** Approve a scanned device-login request (from an already signed-in device). */
export async function approveQrLogin(requestId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/auth/qr/approve', { method: 'POST', body: JSON.stringify({ requestId }) });
}

/** Result of a heartbeat probe. 'expired' is reserved for an authoritative HTTP
 *  401 (session genuinely gone). Any other failure — a network error, or a 5xx
 *  while the API container rolls during a deploy — is 'unreachable' and must NOT
 *  lock: the session is still valid, the server is just briefly absent. */
export type HeartbeatResult = 'ok' | 'expired' | 'unreachable';

/** Slide the session TTL while the app is visible. Returns 'expired' ONLY on a
 *  401 (genuine lapse); transient network/5xx failures return 'unreachable' so
 *  the caller can wait for the server instead of locking the UI. */
export async function authHeartbeat(): Promise<HeartbeatResult> {
  try {
    const res = await fetch(`${BASE_URL}/auth/heartbeat`, { method: 'POST' });
    if (res.ok) return 'ok';
    // 401 = the guard ran against live Redis and the session is gone → genuine.
    if (res.status === 401) return 'expired';
    // 5xx / 502 / 503 / 504 (and any other non-ok): server unhealthy/rolling.
    return 'unreachable';
  } catch {
    // Network error / fetch reject = server unreachable (mid-deploy, offline).
    return 'unreachable';
  }
}

/** End the session. `full` also revokes this device (forces Entra next time). */
export async function authLogout(full = false): Promise<void> {
  await fetch(`${BASE_URL}/auth/logout${full ? '?full=1' : ''}`, { method: 'POST' }).catch(() => {});
  await clearApiCache();
}

// ─── User profile (per-user account + theme) ────────────────────

export interface UserProfile {
  id: string;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  initials: string | null;
  theme: { palette: ThemePalette | null; mode: ThemeMode | null; contrast: ThemeContrast | null; customThemes?: CustomTheme[]; activeCustomId?: string | null };
  /** Personal Zoho reply signature; empty string = fall back to the global one. */
  zohoReplySignature: string;
  /** Per-item-type Auto review — on → that type never shows as New for the user. */
  autoReview: AutoReviewPrefs;
  /** Personal notification opt-ins (syncErrors is admin-gated, default off). */
  notifications: { syncErrors: boolean };
  /** Per-user Backlog board card density (minimal → extensive). */
  backlogCardLayout: BacklogCardLayout;
  /** Per-user Backlog board card type (wide | narrow) — orthogonal to density. */
  backlogCardType: BacklogCardType;
  /** Per-user Page-Specification editor: show type-label eyebrows (vs icons only). */
  pageSpecShowLabels: boolean;
  /** How the Feedback canvas assists placement for this user. (f-01845) */
  feedbackCanvas: FeedbackCanvasPrefs;
  /** How this user sorts a backlog item's Feedback tab, keyed by work item id.
   *  Only non-default choices are present — an absent key means the default.
   *  (f-01920) */
  backlogFeedbackSort: BacklogFeedbackSortPrefs;
  /** Versioned URL of the user's uploaded avatar, or null (→ render initials). */
  avatarUrl: string | null;
  roles: { id: string; name: string }[];
  /** Effective permission keys (may include the '*' wildcard for Administrators).
   *  Reduced while impersonating role(s) via "use the system as". */
  permissions: string[];
  /** Admin-only "use the system as role(s)" control. `canImpersonate` reflects
   *  REAL perms so the control + reset never disappear while impersonating;
   *  `roles` is the assignable-role list for the picker. */
  viewAs: { canImpersonate: boolean; roleIds: string[]; roles: { id: string; name: string }[] };
  /** The user's configurable-dashboard layout, always server-normalized. An
   *  un-onboarded user (no saved layout) gets an empty normalized layout (no
   *  widgets, no onboardedAt) so the SPA shows the setup wizard / empty-state.
   *  Dashboard WRITES go through the dedicated PUT /api/dashboard/layout. */
  dashboard: DashboardLayout;
  /** Which widget-backing subsystems are configured/enabled, so the widget
   *  picker can hide-not-disable ineligible widgets without a second request. */
  configuredFeatures: ConfiguredFeatures;
}

/** The four reviewable item types that carry a per-user New/Reviewed state. */
export interface AutoReviewPrefs {
  discourse: boolean;
  azure: boolean;
  commits: boolean;
  zoho: boolean;
}

export interface ProfilePatch {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  initials?: string;
  theme?: { palette?: ThemePalette; mode?: ThemeMode; contrast?: ThemeContrast; customThemes?: CustomTheme[]; activeCustomId?: string | null };
  /** Empty string clears the personal signature (falls back to global). */
  zohoReplySignature?: string;
  autoReview?: Partial<AutoReviewPrefs>;
  notifications?: { syncErrors?: boolean };
  backlogCardLayout?: BacklogCardLayout;
  backlogCardType?: BacklogCardType;
  pageSpecShowLabels?: boolean;
  /** Whole-object patch — the server coerces it against the stored value, so a
   *  partial send keeps whatever it omits. */
  feedbackCanvas?: FeedbackCanvasPrefs;
  /** ONE item's Feedback-tab sort. Not the whole map: two tabs open on two
   *  different items must not clobber each other. (f-01920) */
  backlogFeedbackSort?: { workItemId: number; sort: BacklogFeedbackSort };
  /** Admin-only: which role(s) to "use the system as" ([] clears the override). */
  viewAsRoleIds?: string[];
}

export async function getProfile(): Promise<UserProfile> {
  return request<UserProfile>('/profile');
}

export async function updateProfile(patch: ProfilePatch): Promise<UserProfile> {
  return request<UserProfile>('/profile', { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Upload (or paste) the signed-in user's avatar image; returns the updated profile. */
export async function uploadProfileAvatar(file: File | Blob): Promise<UserProfile> {
  const fd = new FormData();
  fd.append('file', file, (file as File).name || 'avatar.png');
  const res = await fetch(`${BASE_URL}/profile/avatar`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Avatar upload failed');
  }
  return (await res.json()).data as UserProfile;
}

/** Remove the signed-in user's avatar; returns the updated profile. */
export async function deleteProfileAvatar(): Promise<UserProfile> {
  return request<UserProfile>('/profile/avatar', { method: 'DELETE' });
}

/** Name/email → avatar-URL maps for every user who has uploaded an avatar. Lets the
 *  board / comments / state-flow resolve a person (known only by ADO email or display
 *  name) to a PCP user's avatar. `byEmail` keys win over `byName` (more reliable). */
export interface AvatarDirectory {
  byEmail: Record<string, string>;
  byName: Record<string, string>;
}

export async function getAvatarDirectory(): Promise<AvatarDirectory> {
  return request<AvatarDirectory>('/avatars/directory');
}

// ─── Per-user integration connections ───────────────────────────
// Writes to Zoho / Azure DevOps / Discourse post as the signed-in user; these
// endpoints manage the user's own linked accounts. A 412 connection_required
// from any write means "connect here first" (see ConnectionRequiredSheet).

export interface UserConnection {
  provider: ConnectionProvider;
  status: 'connected' | 'error' | 'not_connected';
  identity: { email?: string; username?: string } | null;
  connectedAt: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
}

export async function getConnections(): Promise<UserConnection[]> {
  return request<UserConnection[]>('/connections');
}

/** Full-page URL that starts the provider's OAuth flow (302 — navigate, don't fetch). */
export function connectStartUrl(p: 'zoho' | 'azure' | 'microsoft'): string {
  return `${BASE_URL}/connections/${p}/start`;
}

export async function disconnectProvider(p: ConnectionProvider): Promise<void> {
  return request<void>(`/connections/${p}`, { method: 'DELETE' });
}

/** Link a Discourse account by username (no OAuth — the forum has none). */
export async function connectDiscourse(
  username: string,
): Promise<{ provider: 'discourse'; username: string; name: string | null }> {
  return request<{ provider: 'discourse'; username: string; name: string | null }>(
    '/connections/discourse',
    { method: 'POST', body: JSON.stringify({ username }) },
  );
}

// ─── Users & roles administration (users.manage) ────────────────

export interface AdminRole {
  id: string;
  name: string;
  description: string | null;
  /** Seeded roles (e.g. Administrator) — not editable/deletable. */
  isSystem: boolean;
  permissions: string[];
  memberCount: number;
}

export interface AdminUser {
  id: string;
  email: string | null;
  displayName: string | null;
  initials: string | null;
  jobTitle: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  roles: { id: string; name: string }[];
  permissions: string[];
  connections: Partial<Record<ConnectionProvider, UserConnection['status']>>;
  identities: import('@pcp/shared').ExternalIdentity[];
}

export async function getAdminUsers(): Promise<AdminUser[]> {
  return request<AdminUser[]>('/admin/users');
}

// ─── External identities (Profile self-service + admin) ──────────
export async function getMyIdentities(): Promise<import('@pcp/shared').ExternalIdentity[]> {
  return request<import('@pcp/shared').ExternalIdentity[]>('/identities');
}
export async function addMyIdentity(body: { provider: string; externalKey: string; label?: string | null }): Promise<import('@pcp/shared').ExternalIdentity> {
  return request<import('@pcp/shared').ExternalIdentity>('/identities', { method: 'POST', body: JSON.stringify(body) });
}
export async function removeMyIdentity(id: string): Promise<void> {
  await request(`/identities/${id}`, { method: 'DELETE' });
}
export async function adminAddIdentity(userId: string, body: { provider: string; externalKey: string; label?: string | null }): Promise<import('@pcp/shared').ExternalIdentity> {
  return request<import('@pcp/shared').ExternalIdentity>(`/admin/users/${userId}/identities`, { method: 'POST', body: JSON.stringify(body) });
}
export async function adminRemoveIdentity(identityId: string): Promise<void> {
  await request(`/admin/identities/${identityId}`, { method: 'DELETE' });
}

// ─── User administration: per-user detail page ──────────────────
export async function getUserAdminUserDetail(userId: string): Promise<import('@pcp/shared').UserAdminUserDetail> {
  return request<import('@pcp/shared').UserAdminUserDetail>(`/user-admin/users/${userId}`);
}
export async function updateUserAdminUser(
  userId: string,
  patch: { displayName?: string | null; firstName?: string | null; lastName?: string | null; jobTitle?: string | null; initials?: string | null },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/user-admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
export async function getUserSessions(userId: string): Promise<import('@pcp/shared').UserSessionInfo[]> {
  return request<import('@pcp/shared').UserSessionInfo[]>(`/user-admin/users/${userId}/sessions`);
}
export async function revokeUserSession(userId: string, sessionId: string): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>(`/user-admin/users/${userId}/sessions/${sessionId}`, { method: 'DELETE' });
}
export async function getUserPasskeys(userId: string): Promise<import('@pcp/shared').UserPasskeyInfo[]> {
  return request<import('@pcp/shared').UserPasskeyInfo[]>(`/user-admin/users/${userId}/passkeys`);
}
export async function removeUserPasskey(userId: string, credId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/user-admin/users/${userId}/passkeys/${credId}`, { method: 'DELETE' });
}
export async function getUserAudit(
  userId: string,
  opts: { limit?: number; before?: string | null; category?: string | null } = {},
): Promise<import('@pcp/shared').AuditListResponse> {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  if (opts.category) q.set('category', opts.category);
  const qs = q.toString();
  return request<import('@pcp/shared').AuditListResponse>(`/user-admin/users/${userId}/audit${qs ? `?${qs}` : ''}`);
}
/** `failed` names the credential kinds the deactivation sweep could not revoke
 *  (absent on an older API). The user is locked out regardless; reactivation
 *  re-runs the sweep before access returns. (b-02042) */
export async function deactivateUser(userId: string): Promise<{ ok: boolean; failed?: string[] }> {
  return request<{ ok: boolean; failed?: string[] }>(`/user-admin/users/${userId}/deactivate`, { method: 'POST' });
}
export async function reactivateUser(userId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/user-admin/users/${userId}/reactivate`, { method: 'POST' });
}
export async function getIdentitySuggestions(userId: string): Promise<import('@pcp/shared').IdentitySuggestion[]> {
  return request<import('@pcp/shared').IdentitySuggestion[]>(`/user-admin/users/${userId}/identity-suggestions`);
}
export async function scanIdentitySuggestions(userId: string): Promise<import('@pcp/shared').IdentitySuggestion[]> {
  return request<import('@pcp/shared').IdentitySuggestion[]>(`/user-admin/users/${userId}/identity-suggestions/scan`, { method: 'POST' });
}
export async function acceptIdentitySuggestion(
  userId: string,
  body: { provider: string; externalKey: string; label?: string | null },
): Promise<import('@pcp/shared').ExternalIdentity> {
  return request<import('@pcp/shared').ExternalIdentity>(`/user-admin/users/${userId}/identity-suggestions/accept`, { method: 'POST', body: JSON.stringify(body) });
}
export async function declineIdentitySuggestion(userId: string, body: { provider: string; externalKey: string }): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/user-admin/users/${userId}/identity-suggestions/decline`, { method: 'POST', body: JSON.stringify(body) });
}
export async function removeUserIdentity(userId: string, identityId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/user-admin/users/${userId}/identities/${identityId}`, { method: 'DELETE' });
}
/** Best-effort page-view beacon — swallows errors (it must never disrupt navigation). */
export async function trackActivity(route: string, title?: string): Promise<void> {
  try {
    await request('/activity/track', { method: 'POST', body: JSON.stringify({ route, title }) });
  } catch {
    /* beacon is fire-and-forget */
  }
}

export async function getAdminRoles(): Promise<AdminRole[]> {
  return request<AdminRole[]>('/admin/roles');
}

export async function createRole(input: {
  name: string;
  description?: string;
  permissions: string[];
}): Promise<AdminRole> {
  return request<AdminRole>('/admin/roles', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateRole(
  id: string,
  patch: { name?: string; description?: string; permissions?: string[] },
): Promise<AdminRole> {
  return request<AdminRole>(`/admin/roles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteRole(id: string): Promise<void> {
  return request<void>(`/admin/roles/${id}`, { method: 'DELETE' });
}

export async function setUserRoles(
  userId: string,
  roleIds: string[],
): Promise<{ userId: string; roles: { id: string; name: string }[]; permissions: string[] }> {
  return request<{ userId: string; roles: { id: string; name: string }[]; permissions: string[] }>(
    `/admin/users/${userId}/roles`,
    { method: 'PUT', body: JSON.stringify({ roleIds }) },
  );
}

// ─── Playbooks (global + per-user overrides) ────────────────────
// Any user can read all playbooks and manage their own override; editing the
// GLOBAL content stays on the per-integration routes (gated playbooks.manage).

export type PlaybookKind = 'zoho-comment' | 'zoho-reply' | 'workitem' | 'discourse' | 'release-notes';

export interface PlaybookEntry {
  kind: PlaybookKind;
  label: string;
  hint: string;
  global: string;
  mine: { content: string; enabled: boolean; updatedAt: string } | null;
}

export async function getPlaybooks(): Promise<PlaybookEntry[]> {
  return request<PlaybookEntry[]>('/playbooks');
}

export async function savePlaybookOverride(
  kind: PlaybookKind,
  body: { content: string; enabled: boolean },
): Promise<void> {
  await request<unknown>(`/playbooks/${kind}/mine`, { method: 'PUT', body: JSON.stringify(body) });
}

export async function deletePlaybookOverride(kind: PlaybookKind): Promise<void> {
  await request<unknown>(`/playbooks/${kind}/mine`, { method: 'DELETE' });
}

// ─── Feature / Bug Requests (PCP's own kind) ─────────────────────

import type {
  FeatureRequest, FeatureRequestListResult, FeatureRequestFacets, FeatureRequestStats,
  FeatureRequestType, FeatureRequestStatus, FeatureRequestArea, EntityAttachment,
  ReleaseNote, ReleaseNoteDetail, ReleaseNoteListResult, ReleaseNoteStatus,
  WhatsNew,
} from '@pcp/shared';

export interface FeatureRequestQuery {
  q?: string;
  type?: FeatureRequestType[];
  status?: FeatureRequestStatus[];
  state?: import('@pcp/shared').FeatureRequestState[];
  area?: FeatureRequestArea[];
  tags?: string[];
  /** 'implemented' = newest implementedAt first (nulls last). Distinct from 'updated',
   *  which release bundling rewrites across every item it bundles. */
  sort?: 'votes' | 'newest' | 'oldest' | 'updated' | 'implemented';
  page?: number;
  pageSize?: number;
}

export async function listFeatureRequests(params: FeatureRequestQuery = {}): Promise<FeatureRequestListResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.type?.length) qs.set('type', params.type.join(','));
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.state?.length) qs.set('state', params.state.join(','));
  if (params.area?.length) qs.set('area', params.area.join(','));
  if (params.tags?.length) qs.set('tags', params.tags.join(','));
  if (params.sort) qs.set('sort', params.sort);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return request<FeatureRequestListResult>(`/requests${q ? `?${q}` : ''}`);
}

export async function getFeatureRequest(id: string): Promise<FeatureRequest> {
  return request<FeatureRequest>(`/requests/${id}`);
}

/** Resolve a request by its human-facing ref id (e.g. f-00123) — used to open the
 *  request modal straight from a ref id in the release notes. */
export async function getFeatureRequestByRef(refId: string): Promise<FeatureRequest> {
  return request<FeatureRequest>(`/requests/ref/${encodeURIComponent(refId)}`);
}

export async function getFeatureRequestFacets(): Promise<FeatureRequestFacets> {
  return request<FeatureRequestFacets>('/requests/facets');
}

export async function getFeatureRequestStats(): Promise<FeatureRequestStats> {
  return request<FeatureRequestStats>('/requests/stats');
}

export async function createFeatureRequest(body: {
  title: string; body?: string; howTo?: string | null; type?: FeatureRequestType; tags?: string[];
  originContext?: import('@pcp/shared').RequestOriginContext | null;
}): Promise<FeatureRequest> {
  return request<FeatureRequest>('/requests', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateFeatureRequest(id: string, patch: {
  title?: string; body?: string; howTo?: string | null; type?: FeatureRequestType; area?: FeatureRequestArea; tags?: string[];
}): Promise<FeatureRequest> {
  return request<FeatureRequest>(`/requests/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function setFeatureRequestStatus(id: string, status: FeatureRequestStatus): Promise<FeatureRequest> {
  return request<FeatureRequest>(`/requests/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) });
}

export async function voteFeatureRequest(id: string, voted?: boolean): Promise<{ voted: boolean; voteCount: number }> {
  return request<{ voted: boolean; voteCount: number }>(`/requests/${id}/vote`, { method: 'POST', body: JSON.stringify({ voted }) });
}

export async function deleteFeatureRequest(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/requests/${id}`, { method: 'DELETE' });
}

/** Upload an attachment (screenshot/file) to a request; returns its EntityAttachment. */
export async function uploadRequestAttachment(requestId: string, file: File | Blob): Promise<EntityAttachment> {
  const fd = new FormData();
  fd.append('file', file, (file as File).name || 'file');
  const res = await fetch(`${BASE_URL}/requests/${requestId}/attachments`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Attachment upload failed');
  }
  return (await res.json()).data as EntityAttachment;
}

export async function deleteRequestAttachment(requestId: string, attId: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/requests/${requestId}/attachments/${attId}`, { method: 'DELETE' });
}

/** Upload an image embedded inline in a request's markdown description. Id-less
 *  (works before the request exists, e.g. while composing); returns its public URL. */
export async function uploadRequestInlineImage(file: File | Blob): Promise<{ url: string; filename: string }> {
  const fd = new FormData();
  fd.append('file', file, (file as File).name || 'image.png');
  const res = await fetch(`${BASE_URL}/requests/images`, { method: 'POST', body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, e.error || 'Image upload failed');
  }
  return (await res.json()).data as { url: string; filename: string };
}

// ─── Release Notes ───────────────────────────────────────────────
export async function listReleaseNotes(params: { status?: ReleaseNoteStatus; page?: number; pageSize?: number } = {}): Promise<ReleaseNoteListResult> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return request<ReleaseNoteListResult>(`/release-notes${q ? `?${q}` : ''}`);
}

export async function getReleaseNote(id: string): Promise<ReleaseNoteDetail> {
  return request<ReleaseNoteDetail>(`/release-notes/${id}`);
}

/** Implemented requests not yet bundled into any release — the composer's pool. */
export async function getUnreleasedShippedItems(): Promise<FeatureRequest[]> {
  return request<FeatureRequest[]>('/release-notes/unreleased-items');
}

/** Draft over ALL unreleased items. Returns null when there's nothing to release. */
export async function generateReleaseNoteDraft(): Promise<ReleaseNoteDetail | null> {
  return request<ReleaseNoteDetail | null>('/release-notes/generate', { method: 'POST', body: JSON.stringify({}) });
}

/** Manual create over a chosen subset of unreleased items. */
export async function createReleaseNote(body: { itemIds: string[]; version?: string; title?: string }): Promise<ReleaseNoteDetail> {
  return request<ReleaseNoteDetail>('/release-notes', { method: 'POST', body: JSON.stringify(body) });
}

export async function publishReleaseNote(id: string): Promise<ReleaseNoteDetail> {
  return request<ReleaseNoteDetail>(`/release-notes/${id}/publish`, { method: 'POST', body: JSON.stringify({}) });
}

export async function regenerateReleaseNoteDraft(id: string): Promise<ReleaseNoteDetail> {
  return request<ReleaseNoteDetail>(`/release-notes/${id}/regenerate`, { method: 'POST', body: JSON.stringify({}) });
}

export async function updateReleaseNote(id: string, patch: { title?: string; version?: string | null; summary?: string | null; notesMarkdown?: string | null }): Promise<ReleaseNoteDetail> {
  return request<ReleaseNoteDetail>(`/release-notes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Delete a release note of ANY status (f-01766) — its bundled shipped items return
 *  to the unreleased pool, free to be bundled into a new release. Irreversible. */
export async function deleteReleaseNote(id: string): Promise<{ deleted: boolean; status: ReleaseNoteStatus; freedItems: number }> {
  return request<{ deleted: boolean; status: ReleaseNoteStatus; freedItems: number }>(`/release-notes/${id}`, { method: 'DELETE' });
}

// ─── What's-new indicator (per-user unseen counts + seen watermark) ──
export async function getWhatsNew(): Promise<WhatsNew> {
  return request<WhatsNew>('/requests/whats-new');
}

export async function markWhatsNewSeen(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/requests/seen', { method: 'POST', body: JSON.stringify({}) });
}

// ─── Notifications (per-user feed, settings, follows) ────────────

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  url: string | null;
  /** Why this user got it: subscribed | mine | followed | parent (a child item of
   *  a Feature of theirs, f-01956). A string, not a union: a newer reason must
   *  render as no tag in this bundle, never break it. */
  reason: string;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationPage {
  items: AppNotification[];
  total: number;
  unread: number;
  page: number;
  pageSize: number;
}

export interface NotificationPrefs {
  kinds: NotificationKindDef[];
  modes: Record<string, NotificationMode>;
  /** The on/off options that change what those kinds cover (f-01956). Optional:
   *  an API older than them sends neither, and the screen then shows no option row. */
  optionDefs?: NotificationOptionDef[];
  /** What this user switched; an absent key is the option's own default. */
  options?: Record<string, boolean>;
}

export async function getNotifications(params: {
  page?: number; pageSize?: number; category?: string; unread?: boolean;
} = {}): Promise<NotificationPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.category) qs.set('category', params.category);
  if (params.unread) qs.set('unread', '1');
  return request<NotificationPage>(`/notifications?${qs.toString()}`);
}

/** `unread` counts EVENTS; `unreadItems` counts the records they are about — the
 *  badge shows the latter. Optional because a response cached before f-01817
 *  carries only `unread`, and the tray falls back to it rather than showing 0. */
export async function getUnreadNotificationCount(): Promise<{ unread: number; unreadItems?: number }> {
  return request<{ unread: number; unreadItems?: number }>('/notifications/unread-count');
}

export async function markNotificationsRead(target: { ids?: string[]; all?: boolean }): Promise<{ marked: number }> {
  return request<{ marked: number }>('/notifications/read', { method: 'POST', body: JSON.stringify(target) });
}

export async function dismissNotification(id: string): Promise<{ dismissed: boolean }> {
  return request<{ dismissed: boolean }>(`/notifications/${id}/dismiss`, { method: 'POST', body: JSON.stringify({}) });
}

export async function getNotificationPrefs(): Promise<NotificationPrefs> {
  return request<NotificationPrefs>('/notifications/preferences');
}

export async function saveNotificationPrefs(modes: Record<string, NotificationMode>): Promise<{ modes: Record<string, NotificationMode> }> {
  return request<{ modes: Record<string, NotificationMode> }>('/notifications/preferences', {
    method: 'PUT', body: JSON.stringify({ modes }),
  });
}

/** Switch notification options (f-01956). Only the keys sent change; the answer
 *  carries the user's resulting options. */
export async function saveNotificationOptions(
  options: Record<string, boolean>,
): Promise<{ modes: Record<string, NotificationMode>; options?: Record<string, boolean> }> {
  return request<{ modes: Record<string, NotificationMode>; options?: Record<string, boolean> }>('/notifications/preferences', {
    method: 'PUT', body: JSON.stringify({ options }),
  });
}

export interface NotificationFollow { entityType: string; entityId: string; createdAt: string }

export async function getNotificationFollows(): Promise<{ items: NotificationFollow[] }> {
  return request<{ items: NotificationFollow[] }>('/notifications/follows');
}

export async function setNotificationFollow(
  entityType: string, entityId: string | number, following: boolean,
): Promise<{ following: boolean }> {
  return request<{ following: boolean }>('/notifications/follows', {
    method: 'POST', body: JSON.stringify({ entityType, entityId, following }),
  });
}

export type { ReleaseNote, ReleaseNoteDetail };

// ─── Status Page — the AUTHENTICATED admin console (f-01494) ─────────
//
// These call the admin routes under /api/status (plus the sealed-secret routes
// under /api/settings/secrets). They are for the in-app admin area ONLY — the
// PUBLIC status page is a separate Vite entry (src/status/**) with no auth, no
// React Query and no EventSource: it polls its own static snapshot so it keeps
// working while this API is down. Never import a public-page module here (and
// never import the Danish dictionary — the admin area is English).
//
// REACT QUERY KEY CONVENTION (the screens MUST use these prefixes). Every prefix
// below is registered in ENTITY_MAP.status (components/RealtimeBridge.tsx); a key
// that is NOT listed there goes stale ~10 minutes after a live change:
//   ['status-incidents', 'page', filters]            incident list
//   ['status-incident', id]                          incident detail (id-busted per event)
//   ['status-incident', 'ref', refId]                incident resolved by i-00042
//   ['status-facets']                                incident facets
//   ['status-components']                            components  (+ ['status-components','groups'])
//   ['status-audiences']                             audiences
//   ['status-subscribers', filters]                  subscribers (+ ['status-subscribers','suppressions'])
//   ['status-contact-points', filters]               contact points
//   ['status-contact-points', channel, destination, 'history']   one destination's ledger
//   ['status-snapshot-state']                        snapshot-writer state
//
// SIMULATION: a send can be emulated by the environment (STATUS_NOTIFY_DRY_RUN),
// by the statusPage.simulationMode setting (which DEFAULTS ON) or per request.
// Always call previewStatusNotify() first and surface `willSimulate` /
// `simulatedReason` before the operator presses send — an operator must never
// believe customers were told when they were not.

import type {
  StatusAudience,
  StatusChannel,
  StatusComponent,
  StatusComponentStatus,
  StatusContactPointListResult,
  StatusDeliveryRecord,
  StatusDeliveryStatus,
  StatusFacets,
  StatusFilterLabel,
  StatusImpact,
  StatusIncidentDetail,
  StatusIncidentKind,
  StatusIncidentListResult,
  StatusIncidentState,
  StatusIncidentStatus,
  StatusIncidentUpdate,
  StatusNotification,
  StatusNotifyPreview,
  StatusSimulationReason,
  StatusSnapshotState,
  StatusIncidentTemplate,
  StatusMessageEvent,
  StatusMessageLocale,
  StatusMessageTemplate,
  StatusSubscriber,
  StatusSubscription,
  StatusSubscriberUpsertResult,
  StatusTemplateStage,
  StatusUpdateKind,
  StatusSubscriberStatus,
  StatusSuppressionReason,
  SecretStatus,
  SecretsStatusResponse,
} from '@pcp/shared';
import { STATUS_DRY_RUN_HEADER } from '@pcp/shared';

/** Suppression ledger row (no shared DTO yet — the admin list is the only reader). */
export interface StatusSuppression {
  id: string;
  channel: StatusChannel;
  destination: string;
  destinationMasked: string;
  reason: StatusSuppressionReason | string;
  note: string | null;
  createdAt: string;
  expiresAt: string | null;
}

/** Result of a Settings → Status Page test send. `simulated` is authoritative:
 *  true means the pipeline ran but no provider was called. */
export interface StatusTestSendResult {
  ok: boolean;
  channel: StatusChannel;
  destination: string;
  status: StatusDeliveryStatus;
  simulated: boolean;
  simulatedReason: StatusSimulationReason | null;
  providerMessageId?: string | null;
  previewText?: string | null;
  error?: string | null;
}

// ─── Audiences ──────────────────────────────────────────────────────


// ─── Incident templates (the "start from" library) ───────────────────

/** Body for both create and patch — the service normalises them identically. */
export interface StatusIncidentTemplateInput {
  name?: string;
  kind?: StatusIncidentKind;
  impact?: StatusImpact;
  stage?: StatusTemplateStage;
  status?: StatusIncidentStatus | null;
  title?: string | null;
  body?: string | null;
  ordering?: number;
  sourceRef?: string | null;
}

export async function getStatusIncidentTemplates(opts: { includeArchived?: boolean } = {}): Promise<StatusIncidentTemplate[]> {
  return request<StatusIncidentTemplate[]>(`/status/incident-templates${opts.includeArchived ? '?includeArchived=1' : ''}`);
}

export async function createStatusIncidentTemplate(body: StatusIncidentTemplateInput): Promise<StatusIncidentTemplate> {
  return request<StatusIncidentTemplate>('/status/incident-templates', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateStatusIncidentTemplate(id: string, patch: StatusIncidentTemplateInput): Promise<StatusIncidentTemplate> {
  return request<StatusIncidentTemplate>(`/status/incident-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Soft-retire, or restore with `archived: false`. */
export async function archiveStatusIncidentTemplate(id: string, archived: boolean): Promise<StatusIncidentTemplate> {
  return request<StatusIncidentTemplate>(`/status/incident-templates/${id}/archive`, { method: 'POST', body: JSON.stringify({ archived }) });
}

export async function deleteStatusIncidentTemplate(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/incident-templates/${id}`, { method: 'DELETE' });
}

export async function reorderStatusIncidentTemplates(ids: string[]): Promise<StatusIncidentTemplate[]> {
  return request<StatusIncidentTemplate[]>('/status/incident-templates/reorder', { method: 'POST', body: JSON.stringify({ ids }) });
}

// ─── Brand message templates (what the outbound copy says) ───────────
//
// A SPARSE OVERRIDE: a slot with no row uses the compiled-in Danish/English
// copy, so "no template" is the normal case rather than an error state. Deleting
// a row IS the revert — there is no separate reset endpoint that could drift
// from it.

/** Upsert body. unique(event, channel, locale) makes the PUT idempotent, so the
 *  editor never has to know whether a row already exists. */
export interface StatusMessageTemplateInput {
  event: StatusMessageEvent;
  channel: StatusChannel;
  locale: StatusMessageLocale;
  subject?: string;
  body?: string;
  enabled?: boolean;
}

export interface StatusMessageTemplatePreview {
  subject: string;
  html: string;
  text: string;
  /** SMS only — measured on the FINAL string, stop link included. */
  smsSegments: number | null;
  /** The source could not be rendered, so this is the built-in copy: exactly
   *  what a real send would fall back to. */
  usedFallback: boolean;
  problems: string[];
}

export async function getStatusMessageTemplates(): Promise<StatusMessageTemplate[]> {
  return request<StatusMessageTemplate[]>('/status/message-templates');
}

export async function saveStatusMessageTemplate(body: StatusMessageTemplateInput): Promise<StatusMessageTemplate> {
  return request<StatusMessageTemplate>('/status/message-templates', { method: 'PUT', body: JSON.stringify(body) });
}

/** Revert this slot to the built-in copy. */
export async function deleteStatusMessageTemplate(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/message-templates/${id}`, { method: 'DELETE' });
}

export async function previewStatusMessageTemplate(body: {
  channel: StatusChannel;
  locale: StatusMessageLocale;
  subject?: string;
  body?: string;
}): Promise<StatusMessageTemplatePreview> {
  return request<StatusMessageTemplatePreview>('/status/message-templates/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getStatusAudiences(): Promise<StatusAudience[]> {
  return request<StatusAudience[]>('/status/audiences');
}

/** The ADMIN view of the audiences: hidden ones included, each with its live
 *  componentCount + confirmed subscriberCount. The plain list above is what a
 *  picker needs; this is what the Audiences tab renders — without the two flags
 *  the server computes neither, and the count columns read "—" (b-02003). */
export async function getStatusAudiencesAdmin(): Promise<StatusAudience[]> {
  return request<StatusAudience[]>('/status/audiences?includeHidden=1&withCounts=1');
}

export async function createStatusAudience(body: {
  slug: string; name: string; description?: string | null; hidden?: boolean; ordering?: number;
}): Promise<StatusAudience> {
  return request<StatusAudience>('/status/audiences', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateStatusAudience(id: string, patch: {
  slug?: string; name?: string; description?: string | null; hidden?: boolean; ordering?: number;
}): Promise<StatusAudience> {
  return request<StatusAudience>(`/status/audiences/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteStatusAudience(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/audiences/${id}`, { method: 'DELETE' });
}

// ─── Components ─────────────────────────────────────────────────────
// One entity that NESTS (parentId, up to 3 levels) — there is no separate
// component-group endpoint any more.

export interface StatusComponentQuery {
  /** Audience slug — only components published to it. */
  audience?: string;
  includeArchived?: boolean;
}

export async function getStatusComponents(params: StatusComponentQuery = {}): Promise<StatusComponent[]> {
  const qs = new URLSearchParams();
  if (params.audience) qs.set('audience', params.audience);
  if (params.includeArchived) qs.set('includeArchived', '1');
  const q = qs.toString();
  return request<StatusComponent[]>(`/status/components${q ? `?${q}` : ''}`);
}

export async function createStatusComponent(body: {
  name: string; parentId?: string | null; description?: string | null; iconUrl?: string | null;
  externalUrl?: string | null; showHistory?: boolean; ordering?: number; audiences?: string[];
}): Promise<StatusComponent> {
  return request<StatusComponent>('/status/components', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateStatusComponent(id: string, patch: {
  name?: string; parentId?: string | null; description?: string | null; iconUrl?: string | null;
  externalUrl?: string | null; showHistory?: boolean; ordering?: number; audiences?: string[];
}): Promise<StatusComponent> {
  return request<StatusComponent>(`/status/components/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteStatusComponent(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/components/${id}`, { method: 'DELETE' });
}

export interface StatusComponentOrderEntry {
  id: string;
  /** null promotes the component to the top level (a root). */
  parentId?: string | null;
  ordering: number;
}

/** Persist a drag-reorder (and any re-parenting) in one write. */
export async function reorderStatusComponents(order: StatusComponentOrderEntry[]): Promise<StatusComponent[]> {
  return request<StatusComponent[]>('/status/components/reorder', {
    method: 'POST',
    body: JSON.stringify({ order }),
  });
}

/** Archive (or restore) a component — archived components leave the public page
 *  but keep their history. */
export async function archiveStatusComponent(id: string, archived = true): Promise<StatusComponent> {
  return request<StatusComponent>(`/status/components/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({ archived }),
  });
}

/** Pin a manual status override on a component (beats the incident-derived one).
 *  Pass `status: null` to clear the pin and fall back to the open incidents. */
export async function setStatusComponentStatus(
  id: string,
  body: { status: StatusComponentStatus | null; note?: string | null },
): Promise<StatusComponent> {
  return request<StatusComponent>(`/status/components/${id}/status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─── Incidents ──────────────────────────────────────────────────────

export interface StatusIncidentQuery {
  q?: string;
  kind?: StatusIncidentKind[];
  status?: StatusIncidentStatus[];
  state?: StatusIncidentState;
  /** Derived chips (see incidentFilterLabels in @pcp/shared). */
  label?: StatusFilterLabel[];
  /** Audience SLUGS. */
  audience?: string[];
  componentId?: string[];
  sort?: 'newest' | 'oldest' | 'updated';
  page?: number;
  pageSize?: number;
}

export async function getStatusIncidents(params: StatusIncidentQuery = {}): Promise<StatusIncidentListResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.kind?.length) qs.set('kind', params.kind.join(','));
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.state) qs.set('state', params.state);
  if (params.label?.length) qs.set('label', params.label.join(','));
  if (params.audience?.length) qs.set('audience', params.audience.join(','));
  if (params.componentId?.length) qs.set('componentId', params.componentId.join(','));
  if (params.sort) qs.set('sort', params.sort);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return request<StatusIncidentListResult>(`/status/incidents${q ? `?${q}` : ''}`);
}

/** Filter counts for the list toolbar (kinds / statuses / chips / audiences / components). */
export async function getStatusFacets(): Promise<StatusFacets> {
  return request<StatusFacets>('/status/incidents/facets');
}

/** Incident + its updates (newest first). */
export async function getStatusIncident(id: string): Promise<StatusIncidentDetail> {
  return request<StatusIncidentDetail>(`/status/incidents/${id}`);
}

/** Resolve an incident by its human ref id (i-00042) or a preserved StatusCast id. */
export async function getStatusIncidentByRef(refId: string): Promise<StatusIncidentDetail> {
  return request<StatusIncidentDetail>(`/status/incidents/ref/${encodeURIComponent(refId)}`);
}

export interface StatusIncidentComponentInput {
  /** ⚠ THE WIRE NAME IS `component`, NOT `id`.
   *  The API reads `entry.component` (resolveAttachments) and skips anything
   *  falsy WITHOUT even recording it as unresolved — so sending `{ id }` made
   *  every component the operator ticked vanish silently: the incident was
   *  created with nothing attached, the public page stayed green and the email
   *  said "Påvirkning: Normal" (b-01596). Callers keep using `id`; the two
   *  functions below do the rename on the way out, in ONE place. */
  id: string;
  /** 'none' lists the component without touching its status (an Information post). */
  impact: StatusImpact;
}

/** Rename `components[].id` to the wire's `component`. Everything else passes
 *  through untouched. */
function toWire<T extends { components?: StatusIncidentComponentInput[] }>(body: T): Omit<T, 'components'> & { components?: Array<{ component: string; impact: StatusImpact }> } {
  const { components, ...rest } = body;
  return {
    ...rest,
    ...(components ? { components: components.map((c) => ({ component: c.id, impact: c.impact })) } : {}),
  };
}

export async function createStatusIncident(body: {
  title: string;
  kind?: StatusIncidentKind;
  status?: StatusIncidentStatus;
  body?: string | null;
  /** WHEN IT HAPPENED. The public 90-day grid is painted from this and
   *  `resolvedAt` (syncIncidentIntervals writes started_at/ended_at from them),
   *  so backdating it is how an incident that already happened lands on the
   *  right days. Defaults to now. */
  occurredAt?: string;
  /** When it was over. Only meaningful on a CLOSED status; omitted on one, the
   *  server resolves it at `occurredAt` rather than "now", so a post-mortem
   *  filed today about last week paints last week and not a week-long stripe. */
  resolvedAt?: string | null;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
  pinned?: boolean;
  /** Audience SLUGS. */
  audiences?: string[];
  components?: StatusIncidentComponentInput[];
  /**
   * The timeline updates to seed. REQUIRED to notify from: a notification
   * always belongs to an update, and createIncident writes none unless this is
   * present — `body` only fills the incident's own summary field. Omitting it
   * is what made every "Create & notify" a silent no-op (b-01594).
   */
  updates?: Array<{ body: string; status?: StatusIncidentStatus; kind?: StatusUpdateKind; occurredAt?: string }>;
  /** Publish immediately instead of leaving it unpublished. */
  publish?: boolean;
}): Promise<StatusIncidentDetail> {
  // ⚠ THE ROUTE RETURNS A WRAPPER, NOT THE INCIDENT.
  // POST /status/incidents answers with CreateIncidentResult —
  // `{ incident, created, unresolvedComponents, unresolvedAudiences }` — while
  // this function was typed as (and returned) StatusIncidentDetail. So every
  // caller read `.refId`, `.kind` and `.updates` off the wrapper and got
  // undefined, and `created.updates[0]` THREW: the incident was written, then
  // the client blew up reading the response, which is why a create reported a
  // failure while the incident appeared in the list anyway, and why nobody was
  // ever notified (b-01594).
  const result = await request<{
    incident: StatusIncidentDetail;
    created: boolean;
    unresolvedComponents: string[];
    unresolvedAudiences: string[];
  }>('/status/incidents', { method: 'POST', body: JSON.stringify(toWire(body)) });
  // Surfaced, not swallowed: a component that failed to resolve means the
  // incident is live while the systems it is about are not marked, and the
  // caller has to be able to say so.
  if (result.unresolvedComponents?.length) {
    throw new ApiError(422, `These components could not be attached: ${result.unresolvedComponents.join(', ')}`, { code: 'components_unresolved' });
  }
  return result.incident;
}

export async function updateStatusIncident(id: string, patch: {
  title?: string;
  kind?: StatusIncidentKind;
  body?: string | null;
  occurredAt?: string;
  resolvedAt?: string | null;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
  pinned?: boolean;
  audiences?: string[];
  components?: StatusIncidentComponentInput[];
}): Promise<StatusIncidentDetail> {
  return request<StatusIncidentDetail>(`/status/incidents/${id}`, { method: 'PATCH', body: JSON.stringify(toWire(patch)) });
}

export async function deleteStatusIncident(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/incidents/${id}`, { method: 'DELETE' });
}

/** Move the incident along its lifecycle. `body` (when given) is posted as the
 *  accompanying update, so the public timeline explains the transition. */
export async function setStatusIncidentStatus(
  id: string,
  input: { status: StatusIncidentStatus; body?: string; occurredAt?: string },
): Promise<StatusIncidentDetail> {
  return request<StatusIncidentDetail>(`/status/incidents/${id}/status`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Publish (or un-publish) an incident to the public page. */
export async function publishStatusIncident(id: string, published = true): Promise<StatusIncidentDetail> {
  return request<StatusIncidentDetail>(`/status/incidents/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify({ published }),
  });
}

// ─── Incident updates ───────────────────────────────────────────────

export async function postStatusIncidentUpdate(
  incidentId: string,
  input: { body: string; status?: StatusIncidentStatus; occurredAt?: string },
): Promise<StatusIncidentUpdate> {
  return request<StatusIncidentUpdate>(`/status/incidents/${incidentId}/updates`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateStatusIncidentUpdate(
  incidentId: string,
  updateId: string,
  patch: { body?: string; status?: StatusIncidentStatus; occurredAt?: string },
): Promise<StatusIncidentUpdate> {
  return request<StatusIncidentUpdate>(`/status/incidents/${incidentId}/updates/${updateId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteStatusIncidentUpdate(
  incidentId: string,
  updateId: string,
): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/incidents/${incidentId}/updates/${updateId}`, { method: 'DELETE' });
}

// ─── Notify (the send path — read the SIMULATION note at the top) ────

export interface StatusNotifyOptions {
  email?: boolean;
  sms?: boolean;
  /** REQUIRED whenever `sms` is true. SMS costs money and is never pre-ticked;
   *  the server rejects sms:true without it, and so do we (below). */
  smsConfirm?: boolean;
  /** Force this ONE send to emulate. One-way: it can only ever turn simulation
   *  ON — omitting it never switches real sending on. */
  dryRun?: boolean;
}

/**
 * The same preview for an incident that does not exist yet — for the create
 * wizard, which has audiences and components but no update id.
 *
 * Use this and NOT a subscriber-list count. The wizard used to count its own
 * reach with getStatusSubscribers({ audience }), which disagreed with the send
 * and always under-reported: the slugs went over the wire comma-joined into one
 * `audience=` value that matches no slug, and that query counts only
 * subscribers with an explicit audience row while a subscriber who picked NO
 * audience receives everything. It read "0 confirmed email subscribers" while
 * Send would have emailed the whole confirmed list.
 */
export async function previewStatusNotifyFor(
  opts: { audiences?: string[]; componentIds?: string[] } & StatusNotifyOptions = {},
): Promise<StatusNotifyPreview> {
  return request<StatusNotifyPreview>('/status/notify/preview', {
    method: 'POST',
    body: JSON.stringify({
      audiences: opts.audiences ?? [],
      componentIds: opts.componentIds ?? [],
      email: opts.email ?? false,
      sms: opts.sms ?? false,
      ...(opts.dryRun ? { dryRun: true } : {}),
    }),
    ...(opts.dryRun ? { headers: { [STATUS_DRY_RUN_HEADER]: '1' } } : {}),
  });
}

/** What pressing Send would actually do: per-channel reach, the suppression
 *  breakdown, SMS segments — and `willSimulate` / `simulatedReason`. ALWAYS show
 *  this before the send button. */
export async function previewStatusNotify(
  updateId: string,
  opts: StatusNotifyOptions = {},
): Promise<StatusNotifyPreview> {
  return request<StatusNotifyPreview>(`/status/updates/${updateId}/notify/preview`, {
    method: 'POST',
    body: JSON.stringify({
      email: opts.email ?? false,
      sms: opts.sms ?? false,
      ...(opts.dryRun ? { dryRun: true } : {}),
    }),
    ...(opts.dryRun ? { headers: { [STATUS_DRY_RUN_HEADER]: '1' } } : {}),
  });
}

/**
 * Send an incident update to the subscribers of its audiences. Returns one
 * StatusNotification per requested channel — check `simulated` on each: a
 * simulated run wrote the delivery rows but called no provider.
 *
 * Belt and braces: sms:true without smsConfirm:true is rejected HERE as well as
 * on the server, so an invalid request is never even put on the wire.
 */
export async function notifyStatusUpdate(
  updateId: string,
  opts: StatusNotifyOptions,
): Promise<StatusNotification[]> {
  if (opts.sms && opts.smsConfirm !== true) {
    throw new ApiError(400, 'SMS notification needs explicit confirmation before it can be sent.', {
      code: 'sms_confirm_required',
    });
  }
  const body: Record<string, unknown> = { email: opts.email ?? false, sms: opts.sms ?? false };
  if (opts.sms) body.smsConfirm = true;
  if (opts.dryRun) body.dryRun = true;
  // ⚠ THE ROUTE ANSWERS WITH A WRAPPER: `{ dryRun, results }`, where each result
  // is `{ channel, notification, ...startNotify() }`. Typing this as
  // StatusNotification[] made every caller run `.some(...)` on an object —
  // "sent.some is not a function" — AFTER the send had already gone out, so the
  // emails arrived and the operator was shown a failure (b-01596). Unwrapped
  // here so both call sites keep the array they expect.
  const res = await request<{
    dryRun: boolean;
    results: Array<{ channel: StatusChannel; notification: StatusNotification }>;
  }>(`/status/updates/${updateId}/notify`, {
    method: 'POST',
    body: JSON.stringify(body),
    ...(opts.dryRun ? { headers: { [STATUS_DRY_RUN_HEADER]: '1' } } : {}),
  });
  return (res?.results ?? []).map((r) => r.notification).filter(Boolean);
}

/** One send's audit record — counts, driver, simulated flag, rendered content. */
export async function getStatusNotification(id: string): Promise<StatusNotification> {
  return request<StatusNotification>(`/status/notifications/${id}`);
}

/** Re-run the failed recipients of a notification. */
export async function retryStatusNotification(id: string): Promise<StatusNotification> {
  return request<StatusNotification>(`/status/notifications/${id}/retry`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── Subscribers ────────────────────────────────────────────────────

export interface StatusSubscriberQuery {
  q?: string;
  channel?: StatusChannel;
  status?: StatusSubscriberStatus[];
  /** Audience SLUGS — subscribers who explicitly chose one of them. */
  audience?: string[];
  /** Also (or, with no slugs, only) subscribers who chose NO audience. */
  noAudience?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Unwrap a list endpoint that may answer either a bare array or a paginated
 * `{ items, total, page, pageSize }` envelope.
 *
 * WHY THIS EXISTS: the status admin list routes return the PAGINATED shape,
 * because their services clamp pageSize to 50. A client typed `Promise<T[]>`
 * against them compiles perfectly — `request<T>` just asserts the type — and
 * then blows up at RUNTIME on the first `.length`. That is exactly how
 * /status crashed on int with "Cannot read properties of undefined (reading
 * 'toLocaleString')": the count was `undefined`, not 0.
 *
 * Accepting both shapes means a route that later gains or drops pagination
 * cannot silently break the console again.
 */
function unwrapList<T>(res: T[] | { items?: T[] } | null | undefined): T[] {
  if (Array.isArray(res)) return res;
  return res?.items ?? [];
}

export async function getStatusSubscribers(params: StatusSubscriberQuery = {}): Promise<StatusSubscriber[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.channel) qs.set('channel', params.channel);
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.audience?.length) qs.set('audience', params.audience.join(','));
  if (params.noAudience) qs.set('noAudience', '1');
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return unwrapList(await request<StatusSubscriber[] | { items: StatusSubscriber[] }>(`/status/subscribers${q ? `?${q}` : ''}`));
}

/** The same page, GROUPED into subscriptions — one entry per person, with the
 *  email and the phone side by side and each keeping its own state (f-01650).
 *  The flat `items` are still returned for anything that wants a channel row. */
export async function getStatusSubscriptions(
  params: StatusSubscriberQuery = {},
): Promise<{ items: StatusSubscriber[]; subscriptions: StatusSubscription[] }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.channel) qs.set('channel', params.channel);
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.audience?.length) qs.set('audience', params.audience.join(','));
  if (params.noAudience) qs.set('noAudience', '1');
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  const res = await request<{ items?: StatusSubscriber[]; subscriptions?: StatusSubscription[] }>(
    `/status/subscribers${q ? `?${q}` : ''}`,
  );
  return { items: res.items ?? [], subscriptions: res.subscriptions ?? [] };
}

/** Name a subscription. Pass the channel row ids too — naming a pair that has
 *  never been grouped is what creates the subscription in the first place. */
export async function saveStatusSubscription(body: {
  subscriptionId?: string | null;
  subscriberIds: string[];
  displayName: string | null;
}): Promise<{ id: string }> {
  return request<{ id: string }>('/status/subscriptions', { method: 'PUT', body: JSON.stringify(body) });
}

/** Returns the subscriber PLUS whether the confirmation actually went out. The
 *  opt-in mail passes the same gate as everything else, so it can be suppressed,
 *  capped or emulated — callers must report `confirmationSent`, never assume it. */
export async function createStatusSubscriber(body: {
  channel: StatusChannel; destination: string; audiences?: string[]; locale?: string;
  /** Skip the double-opt-in confirmation (an operator adding a known recipient). */
  confirmed?: boolean;
}): Promise<StatusSubscriberUpsertResult> {
  return request<StatusSubscriberUpsertResult>('/status/subscribers', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateStatusSubscriber(id: string, patch: {
  audiences?: string[];
  /** Component ids. Omitted leaves them alone; `[]` clears the filter, which
   *  means EVERY system — the same rule the fan-out applies. */
  componentIds?: string[];
  locale?: string; status?: StatusSubscriberStatus;
}): Promise<StatusSubscriber> {
  return request<StatusSubscriber>(`/status/subscribers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteStatusSubscriber(id: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/status/subscribers/${id}`, { method: 'DELETE' });
}

// ─── Contact points (the permanent per-destination ledger) ───────────

export interface StatusContactPointQuery {
  q?: string;
  channel?: StatusChannel;
  page?: number;
  pageSize?: number;
}

export async function getStatusContactPoints(
  params: StatusContactPointQuery = {},
): Promise<StatusContactPointListResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.channel) qs.set('channel', params.channel);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return request<StatusContactPointListResult>(`/status/contact-points${q ? `?${q}` : ''}`);
}

/** Every delivery attempt ever made to one destination (the history drawer). */
export async function getStatusContactPointHistory(
  channel: StatusChannel,
  destination: string,
  params: { limit?: number } = {},
): Promise<StatusDeliveryRecord[]> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return unwrapList(await request<StatusDeliveryRecord[] | { items: StatusDeliveryRecord[] }>(
    `/status/contact-points/${channel}/${encodeURIComponent(destination)}/history${q ? `?${q}` : ''}`,
  ));
}

// ─── Suppressions ───────────────────────────────────────────────────

export async function getStatusSuppressions(
  params: { channel?: StatusChannel; q?: string } = {},
): Promise<StatusSuppression[]> {
  const qs = new URLSearchParams();
  if (params.channel) qs.set('channel', params.channel);
  if (params.q) qs.set('q', params.q);
  const q = qs.toString();
  return unwrapList(await request<StatusSuppression[] | { items: StatusSuppression[] }>(`/status/suppressions${q ? `?${q}` : ''}`));
}

export async function createStatusSuppression(body: {
  channel: StatusChannel; destination: string; reason?: string; note?: string | null; expiresAt?: string | null;
}): Promise<StatusSuppression> {
  return request<StatusSuppression>('/status/suppressions', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Lift a suppression so the destination can receive again.
 *
 * Keyed by (channel, destination), NOT by the row id: that pair is the table's
 * natural key (status_suppressions is unique on it) and it is what the service
 * removeSuppression(channel, destination) takes — it clears BOTH the value as
 * typed and its canonical form, because the send gate blocks on either. The
 * selector rides the query string; the DELETE route also accepts it in the body,
 * but a body on a DELETE is awkward for fetch() and some proxies drop it.
 */
export async function deleteStatusSuppression(
  channel: StatusChannel,
  destination: string,
): Promise<{ deleted: boolean }> {
  const qs = new URLSearchParams({ channel, destination });
  return request<{ deleted: boolean }>(`/status/suppressions?${qs.toString()}`, { method: 'DELETE' });
}

// ─── Public snapshot writer ─────────────────────────────────────────

/** Freshness of the generated static snapshot the public page serves. */
export async function getStatusSnapshotState(): Promise<StatusSnapshotState> {
  return request<StatusSnapshotState>('/status/snapshot/state');
}

/** Force a full re-generate of the public snapshot files. */
export async function rebuildStatusSnapshot(): Promise<StatusSnapshotState> {
  return request<StatusSnapshotState>('/status/snapshot/rebuild', { method: 'POST', body: JSON.stringify({}) });
}

// ─── Provider test sends (Settings → Status Page) ────────────────────

export async function testStatusEmail(to: string): Promise<StatusTestSendResult> {
  return request<StatusTestSendResult>('/status/settings/test-email', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

export async function testStatusSms(to: string): Promise<StatusTestSendResult> {
  return request<StatusTestSendResult>('/status/settings/test-sms', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

// ─── Sealed secrets (write-only credentials; never read back) ─────────

/** Presence/provenance of every sealed secret — values are never returned. */
export async function getStatusSecrets(): Promise<SecretsStatusResponse> {
  return request<SecretsStatusResponse>('/settings/secrets');
}

/** Signups per channel that are waiting for that channel to be switched on.
 *  They were accepted while it was off, so no double opt-in could be sent;
 *  enabling the channel releases them automatically. */
export async function getStatusDeferredSignups(): Promise<{ email: number; sms: number }> {
  return request<{ email: number; sms: number }>('/status/subscribers/deferred');
}

/** Store (or replace) one sealed secret. The value leaves the browser once and
 *  is never readable again — the UI only ever shows a SecretStatus. */
export async function putStatusSecret(name: string, value: string): Promise<SecretStatus> {
  return request<SecretStatus>(`/settings/secrets/${encodeURIComponent(name)}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

/** Remove a stored secret (the integration falls back to unconfigured). */
export async function clearStatusSecret(name: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/settings/secrets/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** Ask Azure DevOps whether the shared service token still works. The verdict is
 *  Azure DevOps' own, so an expired token says so in its own words. (f-01895) */
export async function checkAzureServiceToken(): Promise<{ ok: boolean; status: number; reason: string | null }> {
  return request<{ ok: boolean; status: number; reason: string | null }>('/azure/service-token/check');
}

// ─── Global search (f-01826 / f-01827) ──────────────────────────
// One call serves both modes: the hits are identical either way, and AI mode
// simply adds an answer above them. Reads the local mirror only.

export interface GlobalSearchParams {
  q: string;
  mode?: SearchMode;
  filters?: Partial<SearchFilters>;
  limit?: number;
}

/** Serialize the filter set into the query string, so a result set has a URL
 *  someone can paste. Only narrowed values are written — an untouched filter
 *  stays out so the link reads as what the user actually chose. */
export function searchParamsToQuery(p: GlobalSearchParams): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set('q', p.q);
  if (p.mode === 'ai') qs.set('mode', 'ai');
  const f = p.filters ?? {};
  // `areas` is always written when supplied: absent means "use my saved
  // defaults", which is a different thing from an explicit selection.
  if (f.areas) qs.set('areas', f.areas.join(','));
  if (f.states?.length) qs.set('states', f.states.join(','));
  if (f.types?.length) qs.set('types', f.types.join(','));
  if (f.assignees?.length) qs.set('assignees', f.assignees.join(','));
  if (f.authors?.length) qs.set('authors', f.authors.join(','));
  if (f.tags?.length) qs.set('tags', f.tags.join(','));
  if (f.updatedWithin && f.updatedWithin !== 'any') qs.set('updatedWithin', f.updatedWithin);
  if (f.createdWithin && f.createdWithin !== 'any') qs.set('createdWithin', f.createdWithin);
  if (f.reviewed && f.reviewed !== 'any') qs.set('reviewed', f.reviewed);
  if (f.sort && f.sort !== 'relevance') qs.set('sort', f.sort);
  if (p.limit) qs.set('limit', String(p.limit));
  return qs;
}

export async function globalSearch(p: GlobalSearchParams): Promise<SearchResponse> {
  return request<SearchResponse>(`/search?${searchParamsToQuery(p).toString()}`);
}

/** The signed-in user's default areas + last used mode. */
export async function getSearchPreferences(): Promise<SearchPreferences> {
  return request<SearchPreferences>('/search/preferences');
}

export async function saveSearchPreferences(
  patch: { areas?: SearchAreaKey[]; mode?: SearchMode },
): Promise<SearchPreferences> {
  return request<SearchPreferences>('/search/preferences', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ─── Feedback boards ─────────────────────────────────────────────
// PCP-local, so every call is a plain authenticated request: no connection
// checks, no provider fallbacks, nothing that can raise `connection_required`.

export interface FeedbackListParams {
  q?: string;
  states?: string[];
  severities?: string[];
  people?: string[];
  hideTerminal?: boolean;
  sort?: FeedbackSort;
  page?: number;
  pageSize?: number;
}

export async function getFeedbackList(p: FeedbackListParams = {}): Promise<FeedbackListResult> {
  const qs = new URLSearchParams();
  if (p.q?.trim()) qs.set('q', p.q.trim());
  if (p.states?.length) qs.set('states', p.states.join(','));
  if (p.severities?.length) qs.set('severities', p.severities.join(','));
  if (p.people?.length) qs.set('people', p.people.join(','));
  if (p.hideTerminal) qs.set('hideTerminal', 'true');
  if (p.sort) qs.set('sort', p.sort);
  if (p.page) qs.set('page', String(p.page));
  if (p.pageSize) qs.set('pageSize', String(p.pageSize));
  const q = qs.toString();
  return request<FeedbackListResult>(`/feedback${q ? `?${q}` : ''}`);
}

export async function getFeedbackFacets(): Promise<FeedbackFacets> {
  return request<FeedbackFacets>('/feedback/facets');
}

/** Every board linked to one backlog item — its Feedback tab. Unfiltered and
 *  unpaginated by contract: all of them, whatever state they are in. `sort`
 *  includes 'opened', which orders by when THIS user last opened each board.
 *  (f-01920) */
export async function getWorkItemFeedbackBoards(
  workItemId: number,
  sort: BacklogFeedbackSort,
): Promise<LinkedFeedbackSummary[]> {
  return request<LinkedFeedbackSummary[]>(`/feedback/for-work-item/${workItemId}?sort=${sort}`);
}

export async function getFeedbackBoard(id: string): Promise<Feedback> {
  return request<Feedback>(`/feedback/${id}`);
}

// ─── A request's own board ───────────────────────────────────────
// The board of ONE feature/bug request. Reached only through the request — it is
// deliberately absent from every list, facet, search and picker above. (f-01927)

/** This request's board, or null when nobody has marked one yet. Creates nothing. */
export async function getRequestFeedbackBoard(requestId: string): Promise<Feedback | null> {
  return request<Feedback | null>(`/requests/${requestId}/feedback`);
}

/**
 * Get this request's board, creating it if it has none.
 *
 * Idempotent on the server (`ON CONFLICT DO NOTHING`), which is what makes it
 * safe to call from "the user just pasted something" rather than from a button —
 * two people marking an unmarked board at the same instant land on one board.
 */
export async function ensureRequestFeedbackBoard(requestId: string): Promise<Feedback> {
  return request<Feedback>(`/requests/${requestId}/feedback`, { method: 'POST' });
}

/** Type-ahead over boards (title or `fb-<n>`), for the link picker. */
export async function searchFeedbackBoards(q: string): Promise<FeedbackSummary[]> {
  return request<FeedbackSummary[]>(`/feedback/search?q=${encodeURIComponent(q)}`);
}

export async function createFeedbackBoard(
  input: { title: string; description?: string; severity?: FeedbackSeverity },
): Promise<FeedbackSummary> {
  return request<FeedbackSummary>('/feedback', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateFeedbackBoard(
  id: string,
  patch: { title?: string; description?: string; severity?: FeedbackSeverity; state?: string },
): Promise<FeedbackSummary> {
  return request<FeedbackSummary>(`/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteFeedbackBoard(id: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}`, { method: 'DELETE' });
}

/**
 * Upload one screenshot onto a board's canvas at world (x, y).
 *
 * `naturalWidth`/`naturalHeight` are sent as a courtesy — the server measures the
 * bytes itself and only falls back to these — because the client has already
 * decoded the image to place it and the measurement is free here.
 */
export async function uploadFeedbackImage(
  id: string,
  file: File | Blob,
  opts: { x: number; y: number; naturalWidth?: number; naturalHeight?: number; fileName?: string },
): Promise<FeedbackImage> {
  const fd = new FormData();
  fd.append('file', file, opts.fileName || (file as File).name || 'screenshot.png');
  fd.append('x', String(Math.round(opts.x)));
  fd.append('y', String(Math.round(opts.y)));
  if (opts.naturalWidth) fd.append('naturalWidth', String(opts.naturalWidth));
  if (opts.naturalHeight) fd.append('naturalHeight', String(opts.naturalHeight));
  const res = await fetch(`${BASE_URL}/feedback/${id}/images`, { method: 'POST', body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new ApiError(res.status, err.error || 'Screenshot upload failed');
  }
  return (await res.json()).data as FeedbackImage;
}

export async function deleteFeedbackImage(id: string, imageId: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/images/${imageId}`, { method: 'DELETE' });
}

/** Batched placement write — the canvas's debounced drag flush. */
export async function saveFeedbackElements(
  id: string, elements: FeedbackElementPatch[],
): Promise<{ changed: number }> {
  return request(`/feedback/${id}/elements`, { method: 'PATCH', body: JSON.stringify({ elements }) });
}

export async function addFeedbackComment(
  id: string,
  input: {
    body: string;
    parentId?: string;
    imageId?: string | null;
    anchorU?: number | null;
    anchorV?: number | null;
    x?: number;
    y?: number;
    state?: FeedbackCommentState;
  },
): Promise<FeedbackComment | FeedbackReply> {
  return request(`/feedback/${id}/comments`, { method: 'POST', body: JSON.stringify(input) });
}

export async function updateFeedbackComment(
  id: string, commentId: string, patch: { body?: string; state?: FeedbackCommentState },
): Promise<FeedbackComment | FeedbackReply> {
  return request(`/feedback/${id}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteFeedbackComment(id: string, commentId: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/comments/${commentId}`, { method: 'DELETE' });
}

/** Link/unlink a backlog work item. One endpoint pair serves BOTH directions —
 *  the backlog detail page calls the same routes, so a link is removable from
 *  whichever side the user is standing on. */
export async function linkFeedbackToWorkItem(id: string, workItemId: number): Promise<{ linked: boolean }> {
  return request(`/feedback/${id}/links/${workItemId}`, { method: 'POST' });
}

export async function unlinkFeedbackFromWorkItem(id: string, workItemId: number): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/links/${workItemId}`, { method: 'DELETE' });
}

/** Place a sticky note on a board's canvas. (f-01859) */
export async function addFeedbackNote(
  id: string, input: { body?: string; color?: string; x?: number; y?: number; w?: number; h?: number },
): Promise<FeedbackNote> {
  return request<FeedbackNote>(`/feedback/${id}/notes`, { method: 'POST', body: JSON.stringify(input) });
}


/** Put a link card on the board. Returns immediately with the card in `pending`;
 *  the unfurl fills it in afterwards and arrives by the board's realtime
 *  write-through. (f-01893) */
export async function addFeedbackLink(
  id: string,
  input: { url: string; x?: number; y?: number; w?: number; titleOverride?: string },
): Promise<FeedbackLink> {
  return request<FeedbackLink>(`/feedback/${id}/link-cards`, { method: 'POST', body: JSON.stringify(input) });
}

/** The user's own title for a link, which sits beside whatever the page supplied
 *  rather than replacing it — so no state refuses it. (f-01903) */
export async function updateFeedbackLink(
  id: string, linkId: string, input: { titleOverride?: string },
): Promise<FeedbackLink> {
  return request<FeedbackLink>(`/feedback/${id}/link-cards/${linkId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deleteFeedbackLink(id: string, linkId: string): Promise<void> {
  await request(`/feedback/${id}/link-cards/${linkId}`, { method: 'DELETE' });
}

/** Duplicate a screenshot already on this board, at a new spot — the canvas's
 *  ⌘C/⌘V on an image element. Sends no bytes: the server reuses the source row's
 *  content-addressed file. (f-01875) */
export async function copyFeedbackImage(
  id: string, imageId: string, at: { x: number; y: number },
): Promise<FeedbackImage> {
  return request<FeedbackImage>(`/feedback/${id}/images/${imageId}/copy`, {
    method: 'POST', body: JSON.stringify(at),
  });
}

export async function deleteFeedbackNote(id: string, noteId: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/notes/${noteId}`, { method: 'DELETE' });
}

/** Place a rich-text block on a board's canvas. `body` is HTML, sanitized by the
 *  server on the way in — the client sanitizes too, so a block on screen and a
 *  block in the database are the same document. (f-01954) */
export async function addFeedbackTextBlock(
  id: string,
  input: {
    body?: string; fill?: string; border?: string; leading?: string;
    x?: number; y?: number; w?: number; h?: number;
  },
): Promise<FeedbackTextBlock> {
  return request<FeedbackTextBlock>(`/feedback/${id}/text-blocks`, { method: 'POST', body: JSON.stringify(input) });
}

/** A block's content or colours OUTSIDE the debounced geometry flush. The canvas
 *  itself does not use this — everything it changes rides `/elements` so an edit
 *  and a drag cost one write — it is here for a caller that has just the patch. */
export async function updateFeedbackTextBlock(
  id: string, blockId: string,
  input: { body?: string; fill?: string; border?: string; leading?: string },
): Promise<FeedbackTextBlock> {
  return request<FeedbackTextBlock>(`/feedback/${id}/text-blocks/${blockId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deleteFeedbackTextBlock(id: string, blockId: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/text-blocks/${blockId}`, { method: 'DELETE' });
}

/** Draw a connector between two anchors. Both ends are sent WHOLE — an end is a
 *  discriminated union and half of one is a line pointing somewhere nobody asked
 *  for. (f-01961) */
export async function addFeedbackConnector(
  id: string,
  input: {
    a?: ConnectorEnd; b?: ConnectorEnd;
    color?: string; arrowA?: boolean; arrowB?: boolean;
  },
): Promise<FeedbackConnector> {
  return request<FeedbackConnector>(`/feedback/${id}/connectors`, { method: 'POST', body: JSON.stringify(input) });
}

/** A connector's ends or look OUTSIDE the debounced flush. The canvas itself does
 *  not use this — everything it changes rides `/elements`, so dragging an end and
 *  recolouring the line cost one write between them. */
export async function updateFeedbackConnector(
  id: string, connectorId: string,
  input: { a?: ConnectorEnd; b?: ConnectorEnd; color?: string; arrowA?: boolean; arrowB?: boolean },
): Promise<FeedbackConnector> {
  return request<FeedbackConnector>(`/feedback/${id}/connectors/${connectorId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deleteFeedbackConnector(id: string, connectorId: string): Promise<{ deleted: boolean }> {
  return request(`/feedback/${id}/connectors/${connectorId}`, { method: 'DELETE' });
}

export interface FeedbackAiBrief {
  /** The board as text: every screenshot read, every comment thread placed against
   *  the screenshot and pixel it points at. Fed to the work-item draft as the
   *  brief, so the AI writes from the actual evidence. (f-01857) */
  text: string;
  title: string;
  imagesRead: number;
  imagesSkipped: number;
  threads: number;
}

/** Read the board as an AI brief. Spends vision tokens on first call per image and
 *  is cached by image content thereafter, so re-drafting is cheap. */
export async function getFeedbackAiBrief(id: string): Promise<FeedbackAiBrief> {
  return request<FeedbackAiBrief>(`/feedback/${id}/ai-brief`);
}
